import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { store } from "./utils/store.js";
import { ai, aiChat } from "./utils/api.js";
import { supabase, signIn, signUp, signInWithEmail, signInWithGoogle, resetPassword, updatePassword, signOut, getProfile, upsertProfile, acceptPendingInvite, getTeamMembers, getPendingInvites, inviteTeamMember, revokeInvite, removeMember, updateMemberRole, getWorkspaceId, getWorkspaces, createWorkspace, checkWebsiteDomain, switchWorkspace, clearWorkspaceCache, submitCustomPlanRequest, getIsSuperAdmin, getIsAgencyAdmin, adminGetAllWorkspaces, getMyWorkspaceRole, getWhiteLabelConfig, getWhiteLabelConfigByDomain, getWhiteLabelConfigBySubdomain } from "./utils/supabase.js";
import { ROLES, DEFAULT_PERMS, canAccess, canInvite, canManageBilling, canDeleteAccount, canRemoveMember, canChangeRole } from "./utils/roles.js";
import { C, SB, S, NAV, GM, RV, CM_COL, RS_COL, WB_EDITOR_SCRIPT, SOCIAL_CHAR_LIMITS, SOCIAL_IMAGE_LIMITS, SOCIAL_PLATFORMS, INTEGRATION_PLATFORMS, buildBrandGuardrails, ACTIONS, getCampaignPrompt, parseCSV, NICHES, DEFAULT_NAV, DEFAULT_OUTCOMES, logActivity } from "./shared/constants.js";
import { useIsMobile, useScrollPreserve, useAIContext } from "./shared/hooks.js";
import { Field, Chip, AIBox, Badge, FilterBar, MiniEditor, GBPEditor, MarkdownView, LibraryCard, MediaPickerModal, renderMarkdown, GenerateImagePanel, CW_TYPE_COL, IMAGE_PROVIDERS, IMAGE_PROVIDER_LABEL, ImagePackageLaunchModal, InlineField, TranscriptSection, CallPanel, BrandKitForm, BrandDefaultsEditor, TopicDistributionEditor, HelpIcon, CategoryBadge } from "./shared/components.jsx";
import AIAssistant from "./modules/ai.jsx"; // direct (non-lazy) — always-present core UI, not a routed page
const ResearchStrategy = React.lazy(() => import("./modules/research.jsx"));
const ReviewsModule = React.lazy(() => import("./modules/reviews.jsx"));
const MediaLibrary = React.lazy(() => import("./modules/media.jsx"));
const BloggerFinder = React.lazy(() => import("./modules/bloggers.jsx"));
const AdsManager = React.lazy(() => import("./modules/ads.jsx"));
const EmailCampaigns = React.lazy(() => import("./modules/email-campaigns.jsx"));
const GoogleBusinessProfile = React.lazy(() => import("./modules/gbp.jsx"));
const SocialMedia = React.lazy(() => import("./modules/social.jsx"));
const ContentWriter = React.lazy(() => import("./modules/content.jsx"));
const CampaignManager = React.lazy(() => import("./modules/campaigns.jsx"));
const CRM = React.lazy(() => import("./modules/crm.jsx"));
const BusinessBrain = React.lazy(() => import("./modules/business-brain.jsx"));
const ContentEngineShell = React.lazy(() => import("./modules/content-engine.jsx"));
const ContentAgentMyBusiness = React.lazy(() => import("./modules/content-engine.jsx").then(m => ({ default: m.ContentAgentMyBusiness })));
const LeadAgent = React.lazy(() => import("./modules/lead-agent.jsx"));
const CallTrackingModule = React.lazy(() => import("./modules/call-tracking.jsx"));
const SEOModule = React.lazy(() => import("./modules/seo.jsx"));
const AEOVisibility = React.lazy(() => import("./modules/seo.jsx").then(m => ({ default: m.AEOVisibility })));
const ConversionModule = React.lazy(() => import("./modules/conversion.jsx"));
const AuthorityAgent = React.lazy(() => import("./modules/authority.jsx"));
const CitationLibraryAdmin = React.lazy(() => import("./modules/authority.jsx").then(m => ({ default: m.CitationLibraryAdmin })));
const ImageStudio = React.lazy(() => import("./modules/image-studio.jsx"));
const AdminDashboard = React.lazy(() => import("./modules/admin.jsx"));
const SuperAdminUsers = React.lazy(() => import("./modules/admin.jsx").then(m => ({ default: m.SuperAdminUsers })));
const AgencyUsers = React.lazy(() => import("./modules/admin.jsx").then(m => ({ default: m.AgencyUsers })));
const InboxModule = React.lazy(() => import("./modules/inbox.jsx"));
const SuperAdminActivity = React.lazy(() => import("./modules/admin.jsx").then(m => ({ default: m.SuperAdminActivity })));
const SystemHealth = React.lazy(() => import("./modules/admin.jsx").then(m => ({ default: m.SystemHealth })));
const PlatformReports = React.lazy(() => import("./modules/admin.jsx").then(m => ({ default: m.PlatformReports })));
const CostUsage = React.lazy(() => import("./modules/admin.jsx").then(m => ({ default: m.CostUsage })));
const AtRiskWorkspaces = React.lazy(() => import("./modules/admin.jsx").then(m => ({ default: m.AtRiskWorkspaces })));
const WhiteLabelDomains = React.lazy(() => import("./modules/admin.jsx").then(m => ({ default: m.WhiteLabelDomains })));
const AgencyWorkspaces = React.lazy(() => import("./modules/admin.jsx").then(m => ({ default: m.AgencyWorkspaces })));
const Settings         = React.lazy(() => import("./modules/settings.jsx").then(m => ({ default: m.Settings })));
const RolesPermissions = React.lazy(() => import("./modules/settings.jsx").then(m => ({ default: m.RolesPermissions })));
const PricingPage      = React.lazy(() => import("./modules/settings.jsx").then(m => ({ default: m.PricingPage })));
const BrandGuidelines  = React.lazy(() => import("./modules/settings.jsx").then(m => ({ default: m.BrandGuidelines })));
const ChangeLog        = React.lazy(() => import("./modules/settings.jsx").then(m => ({ default: m.ChangeLog })));

/* ─── API & STORE imported from src/utils/ ──────────────────────────── */

/* ─── DESIGN TOKENS imported from src/shared/constants.js ───────────── */

/* ─── ACTIVITY LOG ──────────────────────────────────────────────────── */
const ACT_TYPES = {
  content:     { label: "Content Studio", color: "#6366f1", icon: "✦" },
  review:      { label: "Reviews",     color: "#f59e0b", icon: "★" },
  research:    { label: "Research",    color: "#10b981", icon: "⊹" },
  campaign:    { label: "Campaigns",   color: "#c4824a", icon: "⚡" },
  reservation: { label: "Bookings",    color: "#8b5cf6", icon: "◉" },
  integration: { label: "Integration", color: "#4285f4", icon: "⊕" },
  crm:         { label: "CRM",         color: "#5a9e6d", icon: "◈" },
};

/* ─── AUTOPILOT CONFIG ───────────────────────────────────────────────────── */
const PLAYBOOKS = {
  starter: {
    name: "Starter", color: "#5a9e6d",
    rules: [
      { id: "keywords",      icon: "⊹", label: "Discover Top 10 Profit Keywords",  type: "keyword_research",    config: { count: 10 } },
      { id: "content_ideas", icon: "✦", label: "Generate 10 Content Ideas",         type: "content_ideas",       config: { count: 10 } },
    ],
  },
  growth: {
    name: "Growth", color: "#6366f1",
    rules: [
      { id: "keywords",      icon: "⊹", label: "Discover Top 10 Profit Keywords",  type: "keyword_research",    config: { count: 10 } },
      { id: "seo_audit",     icon: "◑", label: "Run Full SEO Site Audit",           type: "seo_audit",           config: {} },
      { id: "competitors",   icon: "◈", label: "Map Top 5 Local Competitors",       type: "competitor_analysis", config: { count: 5 } },
      { id: "content_ideas", icon: "✦", label: "Generate 10 Content Ideas",         type: "content_ideas",       config: { count: 10 } },
    ],
  },
  pro: {
    name: "Pro", color: "#c4824a",
    rules: [
      { id: "keywords",      icon: "⊹", label: "Discover Top 10 Profit Keywords",  type: "keyword_research",    config: { count: 10 } },
      { id: "seo_audit",     icon: "◑", label: "Run Full SEO Site Audit",           type: "seo_audit",           config: {} },
      { id: "competitors",   icon: "◈", label: "Map Top 5 Local Competitors",       type: "competitor_analysis", config: { count: 5 } },
      { id: "content_ideas", icon: "✦", label: "Generate 10 Content Ideas",         type: "content_ideas",       config: { count: 10 } },
      { id: "email_draft",   icon: "✉", label: "Draft Welcome Email Campaign",      type: "draft_campaign",      config: { type: "email", goal: "welcome new guests and drive first visit" } },
      { id: "social_posts",  icon: "◎", label: "Create Social Media Starter Posts", type: "social_content",      config: { platforms: ["instagram", "facebook"] } },
    ],
  },
};

const SCHEDULE_TYPES = [
  { id: "email_newsletter", label: "Email Newsletter",       icon: "✉", color: "#f59e0b" },
  { id: "sms_campaign",     label: "SMS Campaign",           icon: "✆", color: "#8b5cf6" },
  { id: "social_post",      label: "Social Media Post",      icon: "◎", color: "#6366f1" },
  { id: "blog_post",        label: "Blog Post",              icon: "✦", color: "#c4824a" },
];

const FREQUENCIES = [
  { id: "weekly",     label: "Weekly" },
  { id: "biweekly",  label: "Bi-weekly" },
  { id: "monthly",   label: "Monthly" },
  { id: "quarterly", label: "Quarterly" },
];

const DAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

// logActivity → src/shared/constants.js

/* ─── HOOKS ─────────────────────────────────────────────────────────── */

// Module-level bridges live on `window.*` so the extracted AIAssistant (modules/ai.jsx)
// can read them across the file boundary. Each module registers its window._xxx on mount.

/* ─── PLAN → MODULE ACCESS MAP ──────────────────────────────────────── */
// null = all modules; array = allowed module ids
const PLAN_MODULES = {
  starter:  ["dashboard","website","media","content","reviews","crm","reservations","social","settings","pricing","roles","brand"],
  pro:      null, // null = all modules including aeo
  agency:   null,
  dfy_pro:  null,
  dfy_agency: null,
  custom:   null, // resolved from workspace.enabled_modules
};

// Pre-built bundles for quick provisioning from Admin Dashboard
const PLAN_BUNDLES = [
  { id:"restaurant_starter", label:"Restaurant Starter",   modules:["dashboard","website","media","content","reviews","crm","reservations","social","gbp","settings","pricing","roles","brand"] },
  { id:"restaurant_pro",     label:"Restaurant Pro",       modules:null },
  { id:"home_services",      label:"Home Services Pack",   modules:["dashboard","website","media","content","reviews","crm","reservations","gbp","ads","settings","pricing","roles","brand"] },
  { id:"healthcare",         label:"Healthcare Clinic",    modules:["dashboard","website","media","content","reviews","crm","reservations","gbp","settings","pricing","roles","brand"] },
  { id:"agency",             label:"Agency (All Modules)", modules:null },
];

/* ─── NAV ───────────────────────────────────────────────────────────── */
/* NAV imported from src/shared/constants.js */

/* ─── SHARED COMPONENTS ─────────────────────────────────────────────── */
// Field, Chip imported from ./shared/components.jsx

function ComingSoon({ title, icon, desc }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 16, color: C.t2, textAlign: "center", padding: 40 }}>
      <div style={{ fontSize: 48, opacity: 0.3 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{title}</div>
      <div style={{ fontSize: 14, color: C.t2, maxWidth: 360, lineHeight: 1.6 }}>{desc}</div>
      <div style={{ marginTop: 8, padding: "6px 16px", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 20, fontSize: 12, color: C.t3 }}>Coming soon</div>
    </div>
  );
}

// AIBox, Badge, FilterBar imported from ./shared/components.jsx

/* ─── DASHBOARD ─────────────────────────────────────────────────────── */
function Dashboard({ setMod, profile }) {
  const isMobile  = useIsMobile();
  const [data, setData]     = useState({ reservations:[], reviews:[], campaigns:[], contacts:[] });
  const [todayAI, setTodayAI] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [playbookStatus, setPlaybookStatus] = useState(null);
  const [kwCount, setKwCount]   = useState(0);
  const [ideaCount, setIdeaCount] = useState(0);

  useEffect(() => {
    Promise.all([
      store.get("reservations"),
      store.get("reviews"),
      store.get("campaigns"),
      store.get("crm-contacts"),
      store.get("autopilot-playbook-status"),
      store.get("keyword-research-history"),
      store.get("content-ideas"),
    ]).then(([res, rev, cam, contacts, pb, kwHist, ideas]) => {
      setData({ reservations: res||[], reviews: rev||[], campaigns: cam||[], contacts: contacts||[] });
      if (pb) setPlaybookStatus(pb);
      if (kwHist?.length) setKwCount(kwHist[0]?.keywords?.length || 0);
      if (ideas?.length) setIdeaCount(ideas.length);
      setLoaded(true);
    });
  }, []);

  // "What to do today" — AI-generated once data is loaded
  useEffect(() => {
    if (!loaded) return;
    const unresponded = data.reviews.filter(r => !r.responded).length;
    const upcoming    = data.reservations.filter(r => r.status === "confirmed").length;
    const activeCamps = data.campaigns.filter(c => c.status === "active").length;
    const lowReviews  = data.reviews.length < 5;
    setAiLoading(true);
    ai(
      `You are a restaurant marketing advisor. Give a concise daily focus for ${profile?.name || "this restaurant"}.
Context:
- ${unresponded} unresponded Google review${unresponded!==1?"s":""}
- ${upcoming} upcoming reservation${upcoming!==1?"s":""}
- ${activeCamps} active marketing campaign${activeCamps!==1?"s":""}
- ${data.contacts.length} CRM contacts
- Review count: ${data.reviews.length} total${lowReviews?" (low — needs attention)":""}
Write 2–3 short punchy bullet points. Start each with an emoji. No headers, no bold text, no markdown formatting — plain text bullets only. Max 60 words total.`,
      "You are a sharp restaurant business advisor. Give direct, actionable daily priorities."
    ).then(r => { setTodayAI(r.trim().replace(/^\*\*.*?\*\*\n?/, "")); setAiLoading(false); }).catch(() => setAiLoading(false));
  }, [loaded]);

  const today    = new Date().toISOString().split("T")[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];

  const upcomingRes   = data.reservations
    .filter(r => r.status === "confirmed" && (r.date === today || r.date === tomorrow))
    .sort((a,b) => a.date > b.date ? 1 : a.time > b.time ? 1 : -1)
    .slice(0, 5);

  const unrespondedRevs = data.reviews.filter(r => !r.responded).slice(0, 4);
  const activeCampaigns = data.campaigns.filter(c => c.status === "active").slice(0, 4);
  const urgentReviews   = data.reviews.filter(r => !r.responded && r.rating <= 2);

  const avgRating = data.reviews.length
    ? (data.reviews.reduce((a,r) => a+r.rating, 0) / data.reviews.length).toFixed(1)
    : null;

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const stats = [
    { label:"Active Campaigns",   val: data.campaigns.filter(c=>c.status==="active").length,   col:CM_COL,    icon:"⚡", mod:"campaigns" },
    { label:"Unresponded Reviews", val: unrespondedRevs.length, col: unrespondedRevs.length>0?C.danger:C.success, icon:"★", mod:"reviews", alert: unrespondedRevs.length>0 },
    { label:"Today's Reservations",val: data.reservations.filter(r=>r.status==="confirmed"&&r.date===today).length, col:C.accent, icon:"◷", mod:"reservations" },
    { label:"CRM Contacts",        val: data.contacts.length,   col:C.sage,   icon:"◈", mod:"crm" },
  ];

  const starColor = n => n >= 4 ? C.success : n === 3 ? "#f9ab00" : C.danger;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom:28 }}>
        <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize: isMobile ? 26 : 36, fontWeight:300, color:C.text, marginBottom:6, lineHeight:1.1 }}>
          {greeting()}{profile?.name ? `, ${profile.name}` : ""}.
        </div>
        <div style={{ fontSize:13, color:C.t2 }}>
          {new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})} · Marketing Command Center
        </div>
      </div>

      {/* Stat strip */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(140px, 1fr))", gap:12, marginBottom:24 }}>
        {stats.map(s => (
          <button key={s.label} onClick={() => setMod(s.mod)}
            style={{ ...S.card, padding:"18px 20px", cursor:"pointer", textAlign:"left", transition:"all 0.15s", border:`1px solid ${s.alert?`${C.danger}50`:C.border}`, background: s.alert?`${C.danger}08`:C.s1 }}
            onMouseOver={e=>{e.currentTarget.style.borderColor=s.col+"80";e.currentTarget.style.background=s.col+"0a";}}
            onMouseOut={e=>{e.currentTarget.style.borderColor=s.alert?`${C.danger}50`:C.border;e.currentTarget.style.background=s.alert?`${C.danger}08`:C.s1;}}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
              <span style={{ fontSize:18, color:s.col }}>{s.icon}</span>
              {s.alert && <span style={{ fontSize:9, background:C.danger, color:"#fff", borderRadius:10, padding:"2px 7px", fontWeight:700 }}>ACTION</span>}
            </div>
            <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:36, fontWeight:300, color:C.text, lineHeight:1 }}>{s.val}</div>
            <div style={{ fontSize:12, color:C.t3, marginTop:6 }}>{s.label}</div>
          </button>
        ))}
      </div>

      {/* Autopilot section */}
      {!playbookStatus ? (
        <div style={{ ...S.card, padding:"18px 22px", marginBottom:20, background:`${C.accent}07`, borderColor:`${C.accent}30`, display:"flex", alignItems:"center", gap:20, flexWrap:"wrap" }}>
          <div style={{ flex:1, minWidth:240 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.accent, marginBottom:5 }}>⚡ Get Started with Autopilot</div>
            <div style={{ fontSize:12, color:C.t2, lineHeight:1.6 }}>
              Run your onboarding playbook to instantly populate keywords, content ideas, competitor research, and campaign drafts — all in under 2 minutes.
            </div>
            <div style={{ display:"flex", gap:10, marginTop:10, flexWrap:"wrap" }}>
              {[["⊹","Keywords"],["✦","Content Ideas"],["◑","SEO Audit"],["◈","Competitors"]].map(([icon,label]) => (
                <span key={label} style={{ fontSize:11, padding:"3px 10px", borderRadius:10, background:C.s2, color:C.t2, border:`1px solid ${C.border}` }}>{icon} {label}</span>
              ))}
            </div>
          </div>
          <button onClick={() => setMod("autopilot")} style={{ ...S.btn, padding:"12px 24px", flexShrink:0 }}>
            ▶ Run Playbook →
          </button>
        </div>
      ) : (
        <div style={{ ...S.card, marginBottom:20, overflow:"hidden" }}>
          <div style={{ padding:"14px 20px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
            <span style={{ fontSize:13, fontWeight:700, color:C.text }}>⚡ Autopilot Results</span>
            <span style={{ fontSize:10, padding:"2px 9px", borderRadius:10, fontWeight:700,
              background: playbookStatus.status==="completed" ? `${C.success}18` : `${C.accent}18`,
              color: playbookStatus.status==="completed" ? C.success : C.accent }}>
              {playbookStatus.status==="completed" ? "COMPLETED" : "RUNNING"}
            </span>
            <span style={{ fontSize:11, color:C.t3 }}>
              {PLAYBOOKS[playbookStatus.plan]?.name || playbookStatus.plan} plan · {new Date(playbookStatus.triggeredAt).toLocaleDateString("en-US",{month:"short",day:"numeric"})} at {new Date(playbookStatus.triggeredAt).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})}
            </span>
            <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
              <button onClick={() => setMod("autopilot")} style={{ ...S.btnGhost, fontSize:11, padding:"4px 12px" }}>Manage →</button>
            </div>
          </div>
          <div style={{ padding:"14px 20px", display:"flex", gap:12, flexWrap:"wrap" }}>
            {[
              { icon:"⊹", label:"Keywords", count: kwCount, mod:"research", tab:"history", color:"#10b981" },
              { icon:"✦", label:"Content Ideas", count: ideaCount, mod:"content", tab:"write", color:"#6366f1" },
              { icon:"◑", label:"SEO Audit", count: null, mod:"research", tab:"audit", color:"#f59e0b", sub:"Check status" },
              { icon:"◈", label:"Competitors", count: null, mod:"research", tab:"competitors", color:"#8b5cf6", sub:"View map" },
            ].map(item => (
              <button key={item.label} onClick={() => {
                  if (item.tab) sessionStorage.setItem("research-pending-tab", item.tab);
                  setMod(item.mod);
                }}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 16px", borderRadius:10, border:`1px solid ${item.color}30`, background:`${item.color}08`, cursor:"pointer", fontFamily:"inherit", transition:"all 0.12s" }}
                onMouseOver={e=>{e.currentTarget.style.background=`${item.color}18`;e.currentTarget.style.borderColor=`${item.color}60`;}}
                onMouseOut={e=>{e.currentTarget.style.background=`${item.color}08`;e.currentTarget.style.borderColor=`${item.color}30`;}}>
                <span style={{ fontSize:18, color:item.color }}>{item.icon}</span>
                <div style={{ textAlign:"left" }}>
                  <div style={{ fontSize:12, fontWeight:600, color:C.text }}>{item.label}</div>
                  <div style={{ fontSize:11, color:item.color }}>
                    {item.count != null ? `${item.count} found` : item.sub} →
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Main 2-column layout */}
      <div style={{ display:"flex", flexWrap:"wrap", gap:20, marginBottom:20, alignItems:"flex-start" }}>

        {/* Left column */}
        <div style={{ flex:"1 1 480px", minWidth:0, display:"flex", flexDirection:"column", gap:16 }}>

          {/* What to do today */}
          <div style={{ ...S.card, padding:22, borderColor:`${C.accent}50`, background:`${C.accent}07` }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.accent, textTransform:"uppercase", letterSpacing:"0.1em" }}>✦ What to Do Today</div>
              <div style={{ fontSize:11, color:C.t3 }}>{new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"})}</div>
            </div>
            {aiLoading ? (
              <div style={{ display:"flex", gap:10, alignItems:"center", color:C.t2, fontSize:13 }}>
                <span style={{ animation:"spin 1s linear infinite", display:"inline-block", color:C.accent }}>✦</span>
                Analysing your business…
              </div>
            ) : todayAI ? (
              <div style={{ fontSize:13, color:C.text, lineHeight:1.85 }}>{renderMarkdown(todayAI)}</div>
            ) : (
              <div style={{ fontSize:13, color:C.t3 }}>Add some data to get personalised daily priorities.</div>
            )}
          </div>

          {/* Active campaigns */}
          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text }}>⚡ Active Campaigns</div>
              <button style={{ ...S.btnGhost, padding:"4px 12px", fontSize:11, borderColor:CM_COL, color:CM_COL }} onClick={()=>setMod("campaigns")}>
                {activeCampaigns.length>0?"View All →":"+ New Campaign"}
              </button>
            </div>
            {activeCampaigns.length === 0 ? (
              <div style={{ padding:"32px 20px", textAlign:"center" }}>
                <div style={{ fontSize:28, marginBottom:10 }}>⚡</div>
                <div style={{ fontSize:13, color:C.t2, marginBottom:14 }}>No active campaigns. Launch one in 60 seconds.</div>
                <button style={{ ...S.btn, background:CM_COL, padding:"8px 20px", fontSize:12 }} onClick={()=>setMod("campaigns")}>Browse Templates →</button>
              </div>
            ) : (
              <div>
                {activeCampaigns.map((c,i) => {
                  const enabled = Object.values(c.actions||{}).filter(Boolean).length;
                  const runs    = c.runs?.length || 0;
                  const isWeekly  = c.schedule?.type === "weekly";
                  const isMonthly = c.schedule?.type === "monthly";
                  return (
                    <div key={c.id} style={{ padding:"14px 20px", borderBottom: i < activeCampaigns.length-1 ? `1px solid ${C.border}` : "none", display:"flex", alignItems:"center", gap:14 }}>
                      <div style={{ width:8, height:8, borderRadius:"50%", background:CM_COL, flexShrink:0, boxShadow:`0 0 6px ${CM_COL}` }} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:600, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.name}</div>
                        <div style={{ fontSize:11, color:C.t3, marginTop:2 }}>
                          {enabled} channels · {runs} run{runs!==1?"s":""}
                          {isWeekly&&" · Weekly autopilot"}{isMonthly&&" · Monthly autopilot"}
                          {c.lastRun&&` · Last run ${new Date(c.lastRun).toLocaleDateString()}`}
                        </div>
                      </div>
                      <button style={{ ...S.btn, padding:"5px 14px", fontSize:11, background:CM_COL, flexShrink:0 }}
                        onClick={()=>setMod("campaigns")}>Run →</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Unresponded reviews */}
          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                <div style={{ fontSize:13, fontWeight:700, color:C.text }}>★ Reviews Needing Response</div>
                {urgentReviews.length > 0 && (
                  <span style={{ fontSize:9, background:C.danger, color:"#fff", borderRadius:10, padding:"2px 8px", fontWeight:700 }}>
                    {urgentReviews.length} URGENT
                  </span>
                )}
              </div>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                {avgRating && <span style={{ fontSize:12, color:"#f9ab00", fontWeight:600 }}>★ {avgRating} avg</span>}
                <button style={{ ...S.btnGhost, padding:"4px 12px", fontSize:11 }} onClick={()=>setMod("reviews")}>View All →</button>
              </div>
            </div>
            {unrespondedRevs.length === 0 ? (
              <div style={{ padding:"24px 20px", display:"flex", gap:12, alignItems:"center" }}>
                <span style={{ fontSize:24 }}>✅</span>
                <div style={{ fontSize:13, color:C.success, fontWeight:600 }}>All reviews responded to — great job!</div>
              </div>
            ) : (
              unrespondedRevs.map((r,i) => (
                <div key={r.id} style={{ padding:"14px 20px", borderBottom: i < unrespondedRevs.length-1 ? `1px solid ${C.border}` : "none", display:"flex", gap:12, alignItems:"flex-start" }}>
                  <div style={{ width:32, height:32, borderRadius:"50%", background:C.s3, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:C.t2, flexShrink:0 }}>{(r.reviewer||"?")[0]}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:3 }}>
                      <span style={{ fontSize:12, fontWeight:600, color:C.text }}>{r.reviewer}</span>
                      <span style={{ color:starColor(r.rating), fontSize:12 }}>{"★".repeat(r.rating)}{"☆".repeat(5-r.rating)}</span>
                      <span style={{ fontSize:11, color:C.t3 }}>{r.date}</span>
                    </div>
                    <div style={{ fontSize:12, color:C.t2, lineHeight:1.5, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.text}</div>
                  </div>
                  <button style={{ ...S.btn, padding:"5px 14px", fontSize:11, background: r.rating<=2?C.danger:C.accent, flexShrink:0 }}
                    onClick={()=>setMod("reviews")}>Respond →</button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right column */}
        <div style={{ flex:"1 1 280px", maxWidth:340, minWidth:0, display:"flex", flexDirection:"column", gap:16 }}>

          {/* Upcoming reservations */}
          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={{ padding:"14px 18px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text }}>◷ Upcoming</div>
              <button style={{ ...S.btnGhost, padding:"3px 10px", fontSize:11 }} onClick={()=>setMod("reservations")}>View All</button>
            </div>
            {upcomingRes.length === 0 ? (
              <div style={{ padding:"24px 18px", textAlign:"center" }}>
                <div style={{ fontSize:13, color:C.t3, marginBottom:10 }}>No upcoming reservations.</div>
                <button style={{ ...S.btnGhost, padding:"6px 14px", fontSize:11 }} onClick={()=>setMod("reservations")}>+ Add Booking</button>
              </div>
            ) : (
              upcomingRes.map((r,i) => (
                <div key={r.id} style={{ padding:"12px 18px", borderBottom: i < upcomingRes.length-1 ? `1px solid ${C.border}` : "none" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{r.name}</div>
                    <div style={{ fontSize:11, padding:"2px 8px", borderRadius:10, background:`${C.accent}20`, color:C.accent, fontWeight:600, flexShrink:0 }}>
                      {r.party} guests
                    </div>
                  </div>
                  <div style={{ fontSize:11, color:C.t2, marginTop:3 }}>
                    {r.date === today ? "Today" : "Tomorrow"} at {r.time}
                  </div>
                  {r.notes && <div style={{ fontSize:11, color:C.t3, marginTop:2, fontStyle:"italic" }}>{r.notes}</div>}
                </div>
              ))
            )}
          </div>

          {/* Quick actions */}
          <div style={{ ...S.card, padding:18 }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.t3, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:14 }}>Quick Actions</div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {[
                { icon:"⚡", label:"Autopilot Playbook",   col:C.accent,   mod:"autopilot" },
                { icon:"◷", label:"Scheduler",             col:"#10b981",  mod:"scheduler" },
                { icon:"✦", label:"Write Content",        col:"#a78bfa",  mod:"content"   },
                { icon:"★", label:"Respond to Reviews",   col:"#f9ab00",  mod:"reviews"   },
                { icon:"✉", label:"Send Email / SMS",     col:"#8b5cf6",  mod:"marketing" },
                { icon:"G", label:"Update Google Business",col:"#4285f4", mod:"gbp"       },
              ].map(a => (
                <button key={a.mod} onClick={()=>setMod(a.mod)}
                  style={{ display:"flex", gap:10, alignItems:"center", padding:"10px 12px", borderRadius:8, border:`1px solid ${C.border}`, background:"transparent", cursor:"pointer", textAlign:"left", fontFamily:"inherit", transition:"all 0.12s" }}
                  onMouseOver={e=>{e.currentTarget.style.background=`${a.col}10`;e.currentTarget.style.borderColor=`${a.col}50`;}}
                  onMouseOut={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.borderColor=C.border;}}>
                  <span style={{ fontSize:14, color:a.col, fontWeight:700, width:18, textAlign:"center" }}>{a.icon}</span>
                  <span style={{ fontSize:12, color:C.text, fontWeight:500 }}>{a.label}</span>
                  <span style={{ marginLeft:"auto", fontSize:11, color:C.t4 }}>→</span>
                </button>
              ))}
            </div>
          </div>

          {/* Profile snippet */}
          {profile?.name && (
            <div style={{ ...S.card, padding:16, borderColor:`${C.accent}30` }}>
              <div style={{ fontSize:11, color:C.t3, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:12 }}>Your Business</div>
              <div style={{ fontSize:15, fontWeight:600, color:C.text, marginBottom:4 }}>{profile.name}</div>
              {profile.category && <div style={{ fontSize:12, color:C.t2, marginBottom:2 }}>{profile.category}{profile.vibe?` · ${profile.vibe}`:""}</div>}
              {profile.city && <div style={{ fontSize:12, color:C.t3 }}>📍 {profile.city}</div>}
              <button style={{ ...S.btnGhost, marginTop:12, width:"100%", padding:"7px", fontSize:11, justifyContent:"center", display:"flex" }}
                onClick={()=>{ /* trigger edit profile */ window.dispatchEvent(new CustomEvent("edit-profile")); }}>
                Edit Profile
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/* ─── WEBSITE BUILDER ───────────────────────────────────────────────── */
/* ── Website Builder constants ── */
const WB_BIZ_TYPES = [
  { id:"restaurant", label:"Restaurant / Café",  icon:"🍽" },
  { id:"plumber",    label:"Plumber / HVAC",      icon:"🔧" },
  { id:"dentist",    label:"Dentist / Clinic",    icon:"🦷" },
  { id:"salon",      label:"Salon / Beauty",      icon:"✂️" },
  { id:"landscaper", label:"Landscaper",          icon:"🌿" },
  { id:"retail",     label:"Retail / Shop",       icon:"🛍" },
  { id:"lawyer",     label:"Law Firm",            icon:"⚖️" },
  { id:"other",      label:"Other Business",      icon:"◈" },
];
const WB_PAGE_TEMPLATES = {
  restaurant: [
    { id:"home",         name:"Home",         sections:["hero","about-preview","menu-preview","testimonials","cta"] },
    { id:"menu",         name:"Menu",         sections:["page-hero","menu-full","cta"] },
    { id:"about",        name:"About Us",     sections:["page-hero","story","team-preview","values"] },
    { id:"reservations", name:"Reservations", sections:["page-hero","reservation-info","contact-form"] },
    { id:"contact",      name:"Contact",      sections:["page-hero","contact-info","hours","map-placeholder"] },
  ],
  plumber: [
    { id:"home",     name:"Home",     sections:["hero","services-preview","why-us","testimonials","cta"] },
    { id:"services", name:"Services", sections:["page-hero","services-list","pricing","faq"] },
    { id:"about",    name:"About Us", sections:["page-hero","story","certifications","team-preview"] },
    { id:"contact",  name:"Contact",  sections:["page-hero","emergency-cta","contact-info","service-area"] },
  ],
  dentist: [
    { id:"home",     name:"Home",     sections:["hero","services-preview","why-us","testimonials","cta"] },
    { id:"services", name:"Services", sections:["page-hero","services-list","insurance","faq"] },
    { id:"team",     name:"Our Team", sections:["page-hero","team-grid"] },
    { id:"contact",  name:"Contact",  sections:["page-hero","contact-info","hours","map-placeholder"] },
  ],
  salon: [
    { id:"home",     name:"Home",     sections:["hero","services-preview","testimonials","cta"] },
    { id:"services", name:"Services", sections:["page-hero","services-list","pricing"] },
    { id:"gallery",  name:"Gallery",  sections:["page-hero","gallery-grid","cta"] },
    { id:"contact",  name:"Contact",  sections:["page-hero","contact-info","hours","booking-cta"] },
  ],
  landscaper: [
    { id:"home",      name:"Home",      sections:["hero","services-preview","why-us","testimonials","cta"] },
    { id:"services",  name:"Services",  sections:["page-hero","services-list","pricing","faq"] },
    { id:"portfolio", name:"Portfolio", sections:["page-hero","portfolio-grid","cta"] },
    { id:"contact",   name:"Contact",   sections:["page-hero","contact-info","service-area","estimate-cta"] },
  ],
  retail: [
    { id:"home",     name:"Home",     sections:["hero","products-preview","about-preview","cta"] },
    { id:"products", name:"Products", sections:["page-hero","products-grid"] },
    { id:"about",    name:"About Us", sections:["page-hero","story","values"] },
    { id:"contact",  name:"Contact",  sections:["page-hero","contact-info","hours","map-placeholder"] },
  ],
  lawyer: [
    { id:"home",     name:"Home",           sections:["hero","services-preview","why-us","testimonials","cta"] },
    { id:"practice", name:"Practice Areas", sections:["page-hero","services-list","faq"] },
    { id:"about",    name:"About",          sections:["page-hero","story","team-grid"] },
    { id:"contact",  name:"Contact",        sections:["page-hero","contact-info","consultation-cta"] },
  ],
  other: [
    { id:"home",     name:"Home",     sections:["hero","services-preview","about-preview","testimonials","cta"] },
    { id:"services", name:"Services", sections:["page-hero","services-list","why-us","faq"] },
    { id:"about",    name:"About Us", sections:["page-hero","story","values","team-preview"] },
    { id:"contact",  name:"Contact",  sections:["page-hero","contact-info","hours","map-placeholder"] },
  ],
};
const WB_THEMES = [
  { id:"modern",  label:"Modern",  colors:["#ffffff","#2563eb","#f1f5f9"], desc:"Clean, professional" },
  { id:"luxury",  label:"Luxury",  colors:["#0f0f0f","#c9a96e","#1a1a1a"], desc:"Dark, elegant" },
  { id:"fresh",   label:"Fresh",   colors:["#f0fdf4","#16a34a","#dcfce7"], desc:"Nature, wellness" },
  { id:"bold",    label:"Bold",    colors:["#18181b","#a855f7","#27272a"], desc:"Edgy, modern" },
  { id:"warm",    label:"Warm",    colors:["#fef3c7","#d97706","#fffbeb"], desc:"Friendly, inviting" },
  { id:"classic", label:"Classic", colors:["#fafaf9","#44403c","#f5f5f4"], desc:"Timeless, trusted" },
];
const WB_SEC_LABELS = {
  hero:"Hero Banner","about-preview":"About Preview","menu-preview":"Menu Highlights",
  "services-preview":"Services Highlights","products-preview":"Products Preview",
  testimonials:"Testimonials",cta:"Call to Action","page-hero":"Page Header",
  "menu-full":"Full Menu",story:"Our Story","team-preview":"Team Preview","team-grid":"Our Team",
  values:"Our Values","services-list":"Services List",pricing:"Pricing",faq:"FAQ",
  "why-us":"Why Choose Us","contact-info":"Contact Info","contact-form":"Contact Form",
  hours:"Business Hours","map-placeholder":"Location / Map","reservation-info":"Reservations",
  certifications:"Certifications","emergency-cta":"Emergency Line","service-area":"Service Area",
  "gallery-grid":"Gallery","portfolio-grid":"Portfolio","products-grid":"Products",
  insurance:"Insurance","booking-cta":"Book Appointment","consultation-cta":"Free Consultation",
  "estimate-cta":"Free Estimate",
};


const prepareHTML = (html) => {
  if (!html) return html;
  const stripped = html.replace(/<script\s+id="__kurb-editor"[\s\S]*?<\/script>/gi,'').replace(/\s+contenteditable="true"/gi,'');
  // Use regex (case-insensitive) + function replacement to avoid both case issues
  // and $ special-pattern interpretation in the replacement string
  if (/<\/body>/i.test(stripped)) {
    return stripped.replace(/<\/body>/i, () => WB_EDITOR_SCRIPT + '</body>');
  }
  // No </body> found (truncated HTML) — append at end so editor always loads
  return stripped + WB_EDITOR_SCRIPT;
};

const cleanHTML = (html) => {
  if (!html) return html;
  return html
    .replace(/<script\s+id="__kurb-editor"[\s\S]*?<\/script>/gi,'')
    .replace(/<style\s+id="__kurb-style"[^>]*>[\s\S]*?<\/style>/gi,'')
    .replace(/<div\s+id="__kurb-(?:dot|tb|badge|ring)"[^>]*>[\s\S]*?<\/div>/gi,'')
    .replace(/<div\s+id="__kurb-[^"]*"[^>]*>/gi,'')
    .replace(/\s+contenteditable="true"/gi,'')
    .replace(/\s+data-ke="[^"]*"/gi,'');
};

function SiteCard({ site, onEdit, onDelete }) {
  const [showMenu, setShowMenu] = useState(false);
  const homeHtml = site.pages?.[0]?.html;

  return (
    <div style={{ background:C.s1, border:`1px solid ${C.border}`, borderRadius:14, overflow:"hidden", cursor:"pointer", transition:"box-shadow 0.2s", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}
      onMouseEnter={e => e.currentTarget.style.boxShadow="0 8px 32px rgba(0,0,0,0.12)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow="0 2px 8px rgba(0,0,0,0.06)"}
      onClick={onEdit}>
      {/* Thumbnail — scaled iframe of home page */}
      <div style={{ position:"relative", width:"100%", paddingBottom:"56.25%", background:C.s3, overflow:"hidden" }}>
        {homeHtml ? (
          <div style={{ position:"absolute", inset:0, overflow:"hidden", pointerEvents:"none" }}>
            <iframe
              srcDoc={homeHtml}
              style={{ width:1200, height:800, border:"none", transform:"scale(0.233)", transformOrigin:"0 0", pointerEvents:"none" }}
              sandbox="allow-scripts"
            />
          </div>
        ) : (
          <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ fontSize:32, color:C.t4 }}>✦</span>
          </div>
        )}
      </div>
      {/* Info row */}
      <div style={{ padding:"14px 16px", display:"flex", alignItems:"center", gap:10 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:14, fontWeight:700, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{site.name || "Untitled Site"}</div>
          <div style={{ fontSize:11, color:C.t3, marginTop:2 }}>
            {site.pages?.length || 0} page{(site.pages?.length||0) !== 1 ? "s" : ""} · Updated {site.updatedAt ? new Date(site.updatedAt).toLocaleDateString() : "—"}
          </div>
        </div>
        <div style={{ position:"relative" }}>
          <button
            onClick={e => { e.stopPropagation(); setShowMenu(v => !v); }}
            style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:8, width:32, height:32, cursor:"pointer", fontSize:16, color:C.t2, display:"flex", alignItems:"center", justifyContent:"center" }}>
            ···
          </button>
          {showMenu && (
            <div style={{ position:"absolute", right:0, top:"calc(100% + 4px)", background:C.s1, border:`1px solid ${C.border}`, borderRadius:8, boxShadow:"0 8px 24px rgba(0,0,0,0.12)", zIndex:100, minWidth:140, overflow:"hidden" }}
              onMouseLeave={() => setShowMenu(false)}>
              <button onClick={e => { e.stopPropagation(); setShowMenu(false); onEdit(); }}
                style={{ display:"block", width:"100%", padding:"10px 14px", border:"none", background:"none", textAlign:"left", fontSize:13, color:C.text, cursor:"pointer", fontFamily:"inherit" }}>
                Edit Site
              </button>
              <button onClick={e => { e.stopPropagation(); setShowMenu(false); if(confirm("Delete this site?")) onDelete(); }}
                style={{ display:"block", width:"100%", padding:"10px 14px", border:"none", background:"none", textAlign:"left", fontSize:13, color:C.danger, cursor:"pointer", fontFamily:"inherit" }}>
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WebsiteBuilder({ profile, imageQuality = "standard" }) {
  const isMobile = useIsMobile();
  const [view, setView]       = useState("sites");
  const [wizStep, setWizStep] = useState(0);
  const [bizInfo, setBizInfo] = useState({
    type:"", name:profile?.name||"", tagline:"", phone:profile?.phone||"",
    email:"", address:profile?.address||"", city:profile?.city||"", hours:"",
    style:"modern", primaryColor:"#2563eb",
    services:["","",""], about:"", selectedPages:[],
  });
  // pages = [{id, name, html}] — one complete HTML file per page
  const [pages, setPages]         = useState([]);
  const [activePage, setActivePage] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [genStatus, setGenStatus]   = useState({ step:0, total:0, label:"" });
  const [device, setDevice]         = useState("desktop");
  const [versions, setVersions]     = useState([]);
  const [versionSaved, setVersionSaved] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [wbModifying, setWbModifying]   = useState(false);
  const [wbModifyDone, setWbModifyDone] = useState(false);
  const [modifyCounter, setModifyCounter] = useState(0);
  const [aiInput, setAiInput]           = useState("");
  const [sites, setSites]               = useState([]);
  const [activeSiteId, setActiveSiteId] = useState(null);
  const pagesRef        = useRef([]);
  const activePageRef   = useRef(0);
  const bizInfoRef      = useRef(bizInfo);
  const sitesRef        = useRef([]);
  const activeSiteIdRef = useRef(null);
  const [showPublish, setShowPublish]   = useState(false);
  const [publishDomain, setPublishDomain] = useState("");
  const [publishDone, setPublishDone]   = useState(false);
  const [brandGuidelines, setBrandGuidelines] = useState(null);
  // AI chat
  const [chatMsgs, setChatMsgs]     = useState([{ role:"ai", text:"I'm your website AI. Tell me what to change — \"make the hero darker\", \"add a FAQ section\", \"change the font to serif\", etc." }]);
  const [chatInput, setChatInput]   = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  // Hooks must be declared here unconditionally (not inside render branches)
  const chatEndRef = useRef(null);
  const iframeRef  = useRef(null);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior:"smooth" }); }, [chatMsgs]);
  const [applyAll, setApplyAll]     = useState(false);
  const [showImgPicker, setShowImgPicker] = useState(false);
  const [imgPickerTab, setImgPickerTab]   = useState("generate");
  const [pendingImgSrc, setPendingImgSrc] = useState(null);
  const [showImgUndo, setShowImgUndo] = useState(false);
  const imgUndoRef  = useRef(null); // { pageIdx, html } — memory only, never persisted
  const imgUndoTimer = useRef(null);

  useEffect(() => {
    // Load all sites; migrate old website-data if needed
    Promise.all([
      store.get("website-sites"),
      store.get("website-data"),   // legacy single-site format
      store.get("website-versions"),
      store.get("brand-guidelines"),
    ]).then(([sitesData, legacyData, versionsData, guidelinesData]) => {
      let allSites = sitesData || [];
      // Migrate legacy single site if no sites array yet
      if (!allSites.length && legacyData?.pages?.length && legacyData.pages[0]?.html) {
        const migrated = {
          id: "legacy",
          name: legacyData.bizInfo?.name || "My Website",
          pages: legacyData.pages,
          bizInfo: legacyData.bizInfo || {},
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        allSites = [migrated];
        store.set("website-sites", allSites);
      }
      setSites(allSites);
      if (versionsData) setVersions(versionsData);
      if (guidelinesData) setBrandGuidelines(guidelinesData);
      // Don't auto-navigate to editor — always show Sites dashboard first
    });
    // Listen for in-iframe messages (nav + inline edit)
    const handler = (e) => {
      if (e.data?.type === "wb-navigate") {
        const pid = e.data.pageId?.replace(".html","");
        setPages(ps => { const i = ps.findIndex(p=>p.id===pid); if(i>=0) setActivePage(i); return ps; });
      }
      if (e.data?.type === "wb-img-click") {
        setPendingImgSrc(e.data.src || null);
        setImgPickerTab("generate");
        setShowImgPicker(true);
      }
      if (e.data?.type === "wb-replace-failed") {
        // Fallback: postMessage body-swap failed, reload srcdoc (rare)
        const html = e.data.html || pagesRef.current[activePageRef.current]?.html;
        if (iframeRef.current && html) iframeRef.current.srcdoc = prepareHTML(html);
      }
      if (e.data?.type === "wb-inline-edit") {
        const clean = cleanHTML(e.data.html);
        if (!clean) return;
        const idx = activePageRef.current;
        const sid = activeSiteIdRef.current;
        setPages(ps => {
          const updated = ps.map((p,i) => i===idx ? {...p, html:clean} : p);
          if (sid) {
            const updatedSites = sitesRef.current.map(s => s.id === sid ? {...s, pages:updated, updatedAt:new Date().toISOString()} : s);
            sitesRef.current = updatedSites;
            setSites(updatedSites);
            store.set("website-sites", updatedSites);
          }
          return updated;
        });
      }
      if (e.data?.type === "wb-ai-rewrite") {
        const { text, instruction, tag } = e.data;
        const tagLabel = tag ? tag.toLowerCase() : "text";
        ai(
          `Rewrite this ${tagLabel} element for a website.\nOriginal text: "${text}"\nInstruction: ${instruction}\n\nReturn ONLY the rewritten text. No quotes, no explanation, no markdown.`,
          "You are a professional website copywriter. Rewrite text to be compelling and on-brand.",
          false, 300
        ).then(result => {
          iframeRef.current?.contentWindow?.postMessage({ type:"wb-ai-result", text: result.trim() }, "*");
        }).catch(() => {
          iframeRef.current?.contentWindow?.postMessage({ type:"wb-ai-result", text }, "*");
        });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Keep refs current
  useEffect(() => { pagesRef.current = pages; }, [pages]);
  useEffect(() => { activePageRef.current = activePage; }, [activePage]);
  useEffect(() => { bizInfoRef.current = bizInfo; }, [bizInfo]);
  useEffect(() => { sitesRef.current = sites; }, [sites]);
  useEffect(() => { activeSiteIdRef.current = activeSiteId; }, [activeSiteId]);

  // Update iframe srcdoc only on deliberate page/modify changes (not on inline edit saves)
  useEffect(() => {
    const html = pagesRef.current[activePage]?.html;
    if (iframeRef.current && html) iframeRef.current.srcdoc = prepareHTML(html);
  }, [activePage, modifyCounter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Register the modify function on the module-level bridge variable
  useEffect(() => {
    window._wbApplyChange = async (request) => {
      const currentPages = pagesRef.current;
      const idx = activePageRef.current;
      const pg = currentPages[idx];
      if (!pg?.html) return;
      setWbModifying(true);
      try {
        // Fast path: in-place text node replace via postMessage — instant, no reload
        // Accepts quoted or unquoted text: change "X" to Y  OR  change X to "Y"  OR  change "X" to "Y"
        const textSwap = request.match(/(?:change|update|rename|replace)\s+["']?(.+?)["']?\s+to\s+["']?(.+?)["']?(?:\s*$|\s+(?:on|in|at)\s)/i)
          || request.match(/(?:change|update|rename|replace)\s+["'](.+?)["']\s+to\s+(.+)/i)
          || request.match(/(?:change|update|rename|replace)\s+(.+?)\s+to\s+["'](.+?)["']/i);
        if (textSwap) {
          const from = textSwap[1]?.trim(), to = textSwap[2]?.trim();
          if (from && to && pg.html.includes(from)) {
            const regex = new RegExp(from.replace(/[.*+?^${}()|[\]\\]/g,'\\$&'), 'g');
            const injected = pg.html.replace(regex, to);
            if (iframeRef.current) iframeRef.current.srcdoc = prepareHTML(injected);
            const updated = pagesRef.current.map((p,i) => i===idx ? {...p, html:injected} : p);
            pagesRef.current = updated; setPages(updated);
            const sid = activeSiteIdRef.current;
            if (sid) {
              const us = sitesRef.current.map(s => s.id === sid ? {...s, pages:updated, updatedAt:new Date().toISOString()} : s);
              sitesRef.current = us; setSites(us); store.set("website-sites", us);
            }
            setWbModifyDone(true); setTimeout(() => setWbModifyDone(false), 2000);
            setWbModifying(false); return;
          }
        }
        // Full AI path
        const biz = bizInfoRef.current;
        const raw = await ai(
          `You are editing the "${pg.name}" page of ${biz?.name || "this business"}'s website.\n\nChange requested: "${request}"\n\nCurrent HTML:\n${pg.html}\n\nReturn the COMPLETE modified HTML. Only change what was requested. Keep all other content, design, and sections identical. Start with <!DOCTYPE html>.`,
          "You are an expert web designer editing a live business website. Apply the requested change precisely. Return complete untruncated HTML — never truncate or omit any section.", false, 12000
        );
        const rawHtml = raw.search(/<!doctype\s+html/i) >= 0 ? raw.slice(raw.search(/<!doctype\s+html/i)) : raw;
        const nav = `<script>document.addEventListener('click',function(e){var a=e.target.closest('a');if(a){var h=a.getAttribute('href')||'';if(!h.startsWith('http')&&!h.startsWith('mailto')&&!h.startsWith('tel')&&h.endsWith('.html')){e.preventDefault();window.parent.postMessage({type:'wb-navigate',pageId:h},'*');}}})<\/script>`;
        const injected = /<\/body>/i.test(rawHtml) ? rawHtml.replace(/<\/body>/i, () => nav + '</body>') : rawHtml + nav;
        if (iframeRef.current) iframeRef.current.srcdoc = prepareHTML(injected);
        const updated = pagesRef.current.map((p,i) => i===idx ? {...p, html:injected} : p);
        pagesRef.current = updated; setPages(updated);
        const sid = activeSiteIdRef.current;
        if (sid) {
          const us = sitesRef.current.map(s => s.id === sid ? {...s, pages:updated, updatedAt:new Date().toISOString()} : s);
          sitesRef.current = us; setSites(us); store.set("website-sites", us);
        }
        setWbModifyDone(true);
        setTimeout(() => setWbModifyDone(false), 3000);
      } catch(err) { console.warn("[WB] modify failed:", err); }
      setWbModifying(false);
    };
    window._wbGetContext = () => ({
      pageName: pagesRef.current[activePageRef.current]?.name || "Home",
      pages: pagesRef.current.map(p => p.name),
      bizName: bizInfoRef.current?.name || "",
      bizType: bizInfoRef.current?.type || "",
    });
    return () => { window._wbApplyChange = null; window._wbGetContext = null; };
  }, []);

  // Inject nav-intercept script into each page's HTML so internal links switch tabs instead of navigating
  const injectNavScript = (html) => {
    const script = `<script>document.addEventListener('click',function(e){var a=e.target.closest('a');if(a){var h=a.getAttribute('href')||'';if(!h.startsWith('http')&&!h.startsWith('mailto')&&!h.startsWith('tel')&&h.endsWith('.html')){e.preventDefault();window.parent.postMessage({type:'wb-navigate',pageId:h},'*');}}});<\/script>`;
    if (/<\/body>/i.test(html)) return html.replace(/<\/body>/i, () => script + '</body>');
    return html + script;
  };

  const extractCSS = (html) => {
    const m = html.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
    return m ? m[1] : "";
  };

  const handleImgSelect = (file) => {
    // 1. Tell iframe to swap the marked element in its live DOM
    iframeRef.current?.contentWindow?.postMessage({ type:"wb-img-replace", url: file.url }, "*");

    // 2. Also update the stored HTML directly in the parent (split/join avoids regex special-char issues)
    const idx = activePageRef.current;
    const pg  = pagesRef.current[idx];
    if (pg?.html && pendingImgSrc) {
      const newHtml = pg.html.split(pendingImgSrc).join(file.url);
      if (newHtml !== pg.html) {
        // Save previous state in memory for undo (no store write)
        imgUndoRef.current = { pageIdx: idx, html: pg.html };
        clearTimeout(imgUndoTimer.current);
        setShowImgUndo(true);
        imgUndoTimer.current = setTimeout(() => {
          setShowImgUndo(false);
          imgUndoRef.current = null;
        }, 8000);

        const updated = pagesRef.current.map((p,i) => i===idx ? {...p, html:newHtml} : p);
        pagesRef.current = updated;
        setPages(updated);
        const sid = activeSiteIdRef.current;
        if (sid) {
          const us = sitesRef.current.map(s => s.id===sid ? {...s, pages:updated, updatedAt:new Date().toISOString()} : s);
          sitesRef.current = us; setSites(us); store.set("website-sites", us);
        }
      }
    }

    setShowImgPicker(false);
    setPendingImgSrc(null);
  };

  const loadSite = (site) => {
    setPages(site.pages || []);
    setBizInfo(p => ({...p, ...(site.bizInfo||{})}));
    setActiveSiteId(site.id);
    setActivePage(0);
    setModifyCounter(c => c + 1);
    setView("editor");
  };

  const saveSiteToList = async (sitePages, siteBizInfo, siteId) => {
    const id = siteId || activeSiteId;
    if (!id) return;
    setSites(prev => {
      const exists = prev.find(s => s.id === id);
      const updated = exists
        ? prev.map(s => s.id === id ? {...s, pages:sitePages, bizInfo:siteBizInfo, updatedAt:new Date().toISOString()} : s)
        : [...prev, {id, name:siteBizInfo?.name||"My Website", pages:sitePages, bizInfo:siteBizInfo, createdAt:new Date().toISOString(), updatedAt:new Date().toISOString()}];
      store.set("website-sites", updated);
      return updated;
    });
  };

  const deleteSite = async (siteId) => {
    setSites(prev => {
      const updated = prev.filter(s => s.id !== siteId);
      store.set("website-sites", updated);
      return updated;
    });
  };

  const buildGenPrompt = (tpl, allTpls, isFirst, firstPageHTML, sharedCSS) => {
    const bizTypeLabel = WB_BIZ_TYPES.find(b=>b.id===bizInfo.type)?.label||"business";
    const navLinks = allTpls.map(p=>`${p.name}→${p.id}.html`).join(" | ");
    const svcList = bizInfo.services.filter(Boolean).join(", ");
    const guardrails = buildBrandGuardrails(brandGuidelines);
    const designContext = isFirst ? `
DESIGN SYSTEM TO CREATE:
• Import 2 Google Fonts via @import: one impactful display font for headings, one clean sans for body
• Hero: full-viewport-height, dramatic multi-stop CSS gradient background, NO solid colors
• Primary color: ${bizInfo.primaryColor} — use for CTAs, gradients, accents, highlights
• Theme: ${bizInfo.style}
• Cards: border-radius 12-16px, box-shadow 0 4px 24px rgba(0,0,0,0.08), hover: transform translateY(-4px) 0.2s ease
• Sections: alternate white / #f8f9fc / subtle gradient — 80-100px vertical padding
• Nav: sticky, backdrop-filter blur(12px), semi-transparent bg, smooth scroll
• Buttons: gradient bg using primary color, border-radius 8px, padding 14px 32px, hover scale(1.03)
• Typography: H1 56-68px weight 800, H2 36-44px weight 700, body 16px line-height 1.7
• Footer: dark background, multi-column, branded` : `
DESIGN CONSISTENCY — this page is part of a multi-page site. Match the Home page design EXACTLY:
• Same Google Fonts (recreate them, do not copy CSS verbatim)
• Same primary color: ${bizInfo.primaryColor}, same ${bizInfo.style} theme
• Same sticky blurred nav with identical links and branding
• Same footer structure (dark bg, multi-column)
• Same button styles, card styles, spacing rhythm, typography scale
• Write fresh CSS that achieves the same visual result — do NOT paste the Home page CSS
• IMPORTANT: generate ALL required sections fully — do not stop after the hero`;

    return `You are an award-winning web designer. Create a STUNNING, PROFESSIONAL ${tpl.name} page for ${bizInfo.name}.

BUSINESS INFO:
• Name: ${bizInfo.name}${bizInfo.tagline?"\n• Tagline: "+bizInfo.tagline:""}
• Type: ${bizTypeLabel}
• Phone: ${bizInfo.phone}${bizInfo.email?"\n• Email: "+bizInfo.email:""}
• Address: ${bizInfo.address}, ${bizInfo.city}${bizInfo.hours?"\n• Hours: "+bizInfo.hours:""}
• Services/products: ${svcList}
• About: ${bizInfo.about||""}

NAVIGATION (all pages): ${navLinks}
PAGE TO BUILD: ${tpl.name}
SECTIONS NEEDED: ${tpl.sections.map(s=>WB_SEC_LABELS[s]||s).join(", ")}

${designContext}

MANDATORY QUALITY RULES:
✦ Use real Unsplash photos for hero backgrounds and section imagery — format: https://images.unsplash.com/photo-PHOTOID?auto=format&fit=crop&w=1800&q=85 — pick relevant food/restaurant/business/interior photography based on business type
✦ Add graceful image fallbacks: img.onerror = function(){ this.style.display='none' }
✦ NO emoji as decorative elements — use CSS shapes, borders, geometric elements
✦ Every section must have visual depth — gradients, overlays, shadows, textures
✦ NO lorem ipsum — use real business info provided above
✦ Fully mobile responsive with media queries at 768px and 480px
✦ All CSS in one <style> block — Google Fonts via @import
✦ Scroll-reveal animations using IntersectionObserver
✦ Internal page links use {pageid}.html format
✦ Complete, untruncated HTML — do not stop generating until </html>
✦ Return ONLY the HTML, nothing else${guardrails}`;
  };

  const generateWebsite = async () => {
    const templates = (WB_PAGE_TEMPLATES[bizInfo.type]||[]).filter(t=>bizInfo.selectedPages.includes(t.id));
    if (!templates.length) return;
    setView("generating"); setGenerating(true);
    setGenStatus({ step:0, total:templates.length, label:"Preparing…" });
    const built = []; let firstHTML = "";
    let sharedPageCSS = "";
    for (let i=0; i<templates.length; i++) {
      const tpl = templates[i];
      setGenStatus({ step:i+1, total:templates.length, label:`Designing "${tpl.name}" page…` });
      try {
        const raw = await ai(
          buildGenPrompt(tpl, templates, i===0, firstHTML, sharedPageCSS),
          "You are an elite web designer creating stunning, award-winning local business websites. Use real Unsplash photography, beautiful typography, rich CSS animations. Return ONLY complete HTML starting with <!DOCTYPE html>. Never truncate — always finish with </body></html>.",
          false, 12000
        );
        const html = raw.includes("<!DOCTYPE") ? raw.slice(raw.indexOf("<!DOCTYPE")) : raw;
        if (i===0) {
          firstHTML = html;
          sharedPageCSS = extractCSS(html); // extract CSS from Home page for all subsequent pages
        }
        built.push({ id:tpl.id, name:tpl.name, html: injectNavScript(html) });
      } catch(e) {
        built.push({ id:tpl.id, name:tpl.name, html:`<!DOCTYPE html><html><body style="font-family:sans-serif;padding:40px"><p>Failed to generate ${tpl.name}: ${e.message}</p></body></html>` });
      }
    }
    const newSiteId = String(Date.now());
    setActiveSiteId(newSiteId);
    const newSite = {
      id: newSiteId,
      name: bizInfo.name || "My Website",
      pages: built,
      bizInfo,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setSites(prev => {
      const updated = [...prev, newSite];
      store.set("website-sites", updated);
      return updated;
    });
    setPages(built); setActivePage(0);
    setModifyCounter(c => c + 1); // trigger srcdoc useEffect after generation
    const v = { id:Date.now(), label:`v1 — Initial`, savedAt:new Date().toISOString(), pages:built };
    const newV = [v,...versions].slice(0,10); setVersions(newV);
    await store.set("website-versions", newV);
    setGenerating(false); setView("editor");
  };

  const modifyViaChat = async (request) => {
    if (!request.trim()) return;
    setChatLoading(true);
    setChatMsgs(h=>[...h,{role:"user",text:request}]);
    setChatInput("");
    const pagesToModify = applyAll ? pages : [pages[activePage]];
    try {
      const newPages = [...pages];
      for (const pg of pagesToModify) {
        const raw = await ai(
          `You are modifying a website page. Apply this change: "${request}"

${applyAll&&pages.length>1?`This is the "${pg.name}" page. Apply the change in context for this specific page.`:""}

Current HTML:
${pg.html}

Rules:
• Return the COMPLETE modified HTML starting with <!DOCTYPE html>
• Keep all existing content and design — only change what was requested
• Maintain beautiful, professional design quality
• Return ONLY the HTML, no explanations`,
          "You are an expert web designer. Modify the HTML as requested, maintaining professional quality. Return complete HTML — never truncate.", false, 12000
        );
        const html = raw.includes("<!DOCTYPE") ? raw.slice(raw.indexOf("<!DOCTYPE")) : raw;
        const pi = newPages.findIndex(p=>p.id===pg.id);
        if (pi>=0) newPages[pi] = {...newPages[pi], html: injectNavScript(html)};
      }
      setPages(newPages);
      await saveSiteToList(newPages, bizInfo);
      const scope = applyAll&&pages.length>1?` (applied to all ${pages.length} pages)`:" (this page)";
      setChatMsgs(h=>[...h,{role:"ai",text:`✓ Done${scope}. Preview updated.`}]);
    } catch(e) {
      setChatMsgs(h=>[...h,{role:"ai",text:"Something went wrong. Please try again."}]);
    }
    setChatLoading(false);
  };

  const saveVersion = async () => {
    const v = { id:Date.now(), label:`v${versions.length+1} — ${new Date().toLocaleString()}`, savedAt:new Date().toISOString(), pages };
    const newV = [v,...versions].slice(0,10); setVersions(newV);
    await store.set("website-versions", newV);
    await saveSiteToList(pages, bizInfo);
    setVersionSaved(true); setTimeout(()=>setVersionSaved(false),2500);
  };

  const applyAIChange = async () => {
    const req = aiInput.trim(); if (!req || wbModifying) return;
    setAiInput("");
    if (window._wbApplyChange) await window._wbApplyChange(req);
  };

  const downloadPage = () => {
    const pg = pages[activePage]; if (!pg) return;
    const b = new Blob([pg.html],{type:"text/html"});
    const a = document.createElement("a"); a.href=URL.createObjectURL(b); a.download=`${pg.id}.html`; a.click();
  };

  const openInBrowser = () => {
    if (!pages.length) return;
    const startId = pages[activePage]?.id || pages[0].id;

    // Single page — open directly
    if (pages.length === 1) {
      const blob = new Blob([pages[0].html], {type:"text/html"});
      window.open(URL.createObjectURL(blob), "_blank"); return;
    }

    // Multi-page — build a wrapper that intercepts wb-navigate postMessages
    // and swaps the iframe to the correct page (pages already have injectNavScript)
    const pageData = {};
    pages.forEach(pg => {
      try { pageData[pg.id] = btoa(unescape(encodeURIComponent(pg.html))); }
      catch(e) { pageData[pg.id] = btoa(pg.html); }
    });

    const wrapper = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>${bizInfo.name||"Website Preview"}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
html,body{width:100%;height:100%;overflow:hidden;background:#000}
iframe{width:100%;height:100%;border:none;display:block}
</style>
</head>
<body>
<iframe id="f" sandbox="allow-same-origin allow-scripts allow-forms allow-popups"></iframe>
<script>
var D=${JSON.stringify(pageData)};
var cache={};
function dec(s){
  try{return decodeURIComponent(escape(atob(s)));}
  catch(e){try{return atob(s);}catch(e2){return '';}}
}
function show(id){
  var pageId=id.replace('.html','');
  if(!D[pageId]){return;}
  var html=cache[pageId];
  if(!html){html=dec(D[pageId]);cache[pageId]=html;}
  var b=new Blob([html],{type:'text/html'});
  var url=URL.createObjectURL(b);
  document.getElementById('f').src=url;
}
window.addEventListener('message',function(e){
  if(e.data&&e.data.type==='wb-navigate'){show(e.data.pageId);}
});
show('${startId}');
</script>
</body>
</html>`;

    const blob = new Blob([wrapper], {type:"text/html"});
    window.open(URL.createObjectURL(blob), "_blank");
  };

  // ── SITES DASHBOARD ───────────────────────────────────────────────────
  if (view === "sites") {
    return (
      <div style={{ padding: isMobile ? "20px 16px" : "40px 48px" }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:32 }}>
          <div>
            <h2 style={S.h2}>Website</h2>
            <p style={{ ...S.sub, marginBottom:0 }}>Build, edit, and manage your business websites.</p>
          </div>
          <button onClick={() => { setBizInfo({type:"",name:profile?.name||"",tagline:"",phone:profile?.phone||"",email:"",address:profile?.address||"",city:profile?.city||"",hours:"",style:"modern",primaryColor:"#2563eb",services:["","",""],about:"",selectedPages:[]}); setWizStep(0); setPages([]); setActiveSiteId(null); setView("wizard"); }}
            style={{ ...S.btn, padding:"10px 20px", fontSize:14, fontWeight:700, flexShrink:0 }}>
            + Create New Site
          </button>
        </div>

        {/* Empty state */}
        {sites.length === 0 && (
          <div style={{ textAlign:"center", padding:"80px 20px", background:C.s1, borderRadius:16, border:`2px dashed ${C.border}` }}>
            <div style={{ fontSize:48, marginBottom:16 }}>✦</div>
            <div style={{ fontSize:18, fontWeight:700, color:C.text, marginBottom:8 }}>No websites yet</div>
            <div style={{ fontSize:14, color:C.t2, marginBottom:24 }}>Build a stunning, AI-generated website for your business in minutes.</div>
            <button onClick={() => setView("wizard")} style={{ ...S.btn, padding:"12px 28px", fontSize:15, fontWeight:700 }}>Build My First Website →</button>
          </div>
        )}

        {/* Site cards grid */}
        {sites.length > 0 && (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:20 }}>
            {sites.map(site => (
              <SiteCard key={site.id} site={site} onEdit={() => loadSite(site)} onDelete={() => deleteSite(site.id)} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── WIZARD ────────────────────────────────────────────────────────────
  if (view==="wizard") {
    const bi = bizInfo, set=(k,v)=>setBizInfo(p=>({...p,[k]:v}));
    const templates = WB_PAGE_TEMPLATES[bi.type]||[];
    return (
      <div style={{ padding: isMobile ? "20px 16px" : "40px 48px" }}>
        {/* Header — matches Content module pattern */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24, flexWrap:"wrap", gap:12 }}>
          <div>
            <button onClick={() => setView("sites")} style={{ background:"none", border:"none", color:C.t3, cursor:"pointer", fontFamily:"inherit", fontSize:12, padding:0, marginBottom:6, display:"flex", alignItems:"center", gap:4 }}>
              ← Back to Sites
            </button>
            <h2 style={S.h2}>Build Your Website</h2>
            <p style={{ ...S.sub, marginBottom:0 }}>Step {wizStep+1} of 4 — {["Choose your business type","Tell us about your business","Pick a design style","Select your pages"][wizStep]}</p>
          </div>
          {/* Step tabs */}
          <div style={{ display:"flex", gap:4, background:C.s3, padding:4, borderRadius:10, flexShrink:0, border:`1px solid ${C.border}` }}>
            {["Business Type","Your Info","Design","Pages"].map((label,i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 14px", borderRadius:7,
                background: i===wizStep ? C.s1 : "transparent",
                color: i<wizStep ? C.success : i===wizStep ? C.text : C.t3,
                fontSize:13, fontWeight: i===wizStep ? 600 : 400,
                boxShadow: i===wizStep ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
                cursor: i<wizStep ? "pointer" : "default", userSelect:"none",
                transition:"all 0.15s" }}
                onClick={() => i < wizStep && setWizStep(i)}>
                <span style={{ width:18, height:18, borderRadius:"50%", display:"inline-flex", alignItems:"center", justifyContent:"center",
                  background: i<wizStep ? C.success : i===wizStep ? C.accent : C.s4,
                  color:"#fff", fontSize:10, fontWeight:700, flexShrink:0 }}>
                  {i < wizStep ? "✓" : i+1}
                </span>
                {!isMobile && label}
              </div>
            ))}
          </div>
        </div>

        {wizStep===0 && (
          <div>
            <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4, 1fr)", gap:10, maxWidth:800 }}>
              {WB_BIZ_TYPES.map(bt=>(
                <button key={bt.id} onClick={()=>{set("type",bt.id);setWizStep(1);}}
                  style={{ display:"flex",alignItems:"center",gap:12,padding:"16px 18px",borderRadius:10,
                    border:`2px solid ${bi.type===bt.id?C.accent:C.border}`,
                    background:bi.type===bt.id?`${C.accent}10`:C.s1,cursor:"pointer",fontFamily:"inherit",textAlign:"left" }}>
                  <span style={{ fontSize:22 }}>{bt.icon}</span>
                  <span style={{ fontSize:13,fontWeight:600,color:C.text }}>{bt.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {wizStep===1 && (
          <div>
            <div style={{ display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:14 }}>
              <Field label="Business Name *"><input style={S.input} value={bi.name} onChange={e=>set("name",e.target.value)} placeholder="Kitchen Master"/></Field>
              <Field label="Tagline"><input style={S.input} value={bi.tagline} onChange={e=>set("tagline",e.target.value)} placeholder="Authentic Chinese cuisine since 2005"/></Field>
              <Field label="Phone *"><input style={S.input} value={bi.phone} onChange={e=>set("phone",e.target.value)} placeholder="+1 (770) 555-1234"/></Field>
              <Field label="Email"><input style={S.input} value={bi.email} onChange={e=>set("email",e.target.value)} placeholder="hello@yourbusiness.com"/></Field>
              <Field label="City / Area *"><input style={S.input} value={bi.city} onChange={e=>set("city",e.target.value)} placeholder="Suwanee, GA"/></Field>
              <Field label="Business Hours"><input style={S.input} value={bi.hours} onChange={e=>set("hours",e.target.value)} placeholder="Mon–Fri 9am–6pm"/></Field>
            </div>
            <Field label="Full Address"><input style={S.input} value={bi.address} onChange={e=>set("address",e.target.value)} placeholder="123 Main St, Suwanee, GA 30024"/></Field>
            <Field label="Services / Products (one per line)">
              <textarea style={{ ...S.input,height:80,resize:"vertical" }} value={bi.services.join("\n")}
                onChange={e=>set("services",e.target.value.split("\n"))}
                placeholder={"General Tso's Chicken\nHand-pulled Noodles\nPeking Duck"}/>
            </Field>
            <Field label="About your business">
              <textarea style={{ ...S.input,height:80,resize:"vertical" }} value={bi.about} onChange={e=>set("about",e.target.value)}
                placeholder="Your story, what makes you different, how long you've been operating…"/>
            </Field>
            <div style={{ display:"flex",gap:10,marginTop:8 }}>
              <button onClick={()=>setWizStep(0)} style={{ ...S.btnGhost,padding:"10px 20px" }}>← Back</button>
              <button onClick={()=>setWizStep(2)} disabled={!bi.name||!bi.phone||!bi.city}
                style={{ ...S.btn,flex:1,padding:"10px" }}>Next: Design Style →</button>
            </div>
          </div>
        )}

        {wizStep===2 && (
          <div>
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:10,marginBottom:20 }}>
              {WB_THEMES.map(th=>(
                <button key={th.id} onClick={()=>set("style",th.id)}
                  style={{ padding:"14px",borderRadius:10,border:`2px solid ${bi.style===th.id?C.accent:C.border}`,
                    background:bi.style===th.id?`${C.accent}10`:C.s1,cursor:"pointer",fontFamily:"inherit",textAlign:"left" }}>
                  <div style={{ display:"flex",gap:4,marginBottom:8 }}>
                    {th.colors.map((col,i)=>(<div key={i} style={{ flex:1,height:20,borderRadius:4,background:col,border:`1px solid ${C.border}` }}/>))}
                  </div>
                  <div style={{ fontSize:13,fontWeight:700,color:C.text }}>{th.label}</div>
                  <div style={{ fontSize:11,color:C.t3 }}>{th.desc}</div>
                </button>
              ))}
            </div>
            <Field label="Primary Brand Color">
              <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                <input type="color" value={bi.primaryColor} onChange={e=>set("primaryColor",e.target.value)}
                  style={{ width:44,height:36,border:`1px solid ${C.border}`,borderRadius:6,cursor:"pointer",padding:2 }}/>
                <input style={{ ...S.input,flex:1 }} value={bi.primaryColor} onChange={e=>set("primaryColor",e.target.value)} placeholder="#2563eb"/>
              </div>
            </Field>
            <div style={{ display:"flex",gap:10,marginTop:8 }}>
              <button onClick={()=>setWizStep(1)} style={{ ...S.btnGhost,padding:"10px 20px" }}>← Back</button>
              <button onClick={()=>{ set("selectedPages",(WB_PAGE_TEMPLATES[bi.type]||[]).map(p=>p.id)); setWizStep(3); }}
                style={{ ...S.btn,flex:1,padding:"10px" }}>Next: Select Pages →</button>
            </div>
          </div>
        )}

        {wizStep===3 && (
          <div>
            <div style={{ display:"flex",flexDirection:"column",gap:8,marginBottom:24 }}>
              {templates.map(tpl=>{
                const on=bi.selectedPages.includes(tpl.id);
                return (
                  <button key={tpl.id} onClick={()=>set("selectedPages",on?bi.selectedPages.filter(x=>x!==tpl.id):[...bi.selectedPages,tpl.id])}
                    style={{ display:"flex",alignItems:"center",gap:12,padding:"14px 16px",borderRadius:10,
                      border:`2px solid ${on?C.accent:C.border}`,background:on?`${C.accent}10`:C.s1,
                      cursor:"pointer",fontFamily:"inherit",textAlign:"left" }}>
                    <div style={{ width:20,height:20,borderRadius:5,border:`2px solid ${on?C.accent:C.border}`,
                      background:on?C.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                      {on&&<span style={{ color:"#fff",fontSize:12,fontWeight:700 }}>✓</span>}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:14,fontWeight:600,color:C.text }}>{tpl.name}</div>
                      <div style={{ fontSize:11,color:C.t3 }}>{tpl.sections.map(s=>WB_SEC_LABELS[s]||s).join(" · ")}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div style={{ display:"flex",gap:10 }}>
              <button onClick={()=>setWizStep(2)} style={{ ...S.btnGhost,padding:"10px 20px" }}>← Back</button>
              <button onClick={generateWebsite} disabled={bi.selectedPages.length===0}
                style={{ ...S.btn,flex:1,padding:"11px",fontSize:15,fontWeight:700 }}>
                ✦ Build My Website ({bi.selectedPages.length} page{bi.selectedPages.length!==1?"s":""}) →
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── GENERATING ──────────────────────────────────────────────────────────
  if (view==="generating") return (
    <div style={{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"60vh",gap:24 }}>
      <div style={{ fontSize:48 }}>✦</div>
      <div style={{ fontSize:20,fontWeight:700,color:C.text }}>Designing your website…</div>
      <div style={{ fontSize:14,color:C.t2 }}>{genStatus.label}</div>
      <div style={{ width:320,height:8,background:C.s4,borderRadius:4,overflow:"hidden" }}>
        <div style={{ height:"100%",borderRadius:4,transition:"width 0.5s ease",
          width:`${genStatus.total?Math.round((genStatus.step/genStatus.total)*100):0}%`,
          background:"linear-gradient(90deg,#7A5AF8,#6B21A8)" }}/>
      </div>
      <div style={{ fontSize:12,color:C.t3 }}>Page {genStatus.step} of {genStatus.total}</div>
    </div>
  );

  // ── EDITOR ─────────────────────────────────────────────────────────────
  const curPage = pages[activePage];

  return (
    <div style={{ display:"flex",flexDirection:"column",height:"calc(100vh - 60px)",overflow:"hidden" }}>
      {/* Top bar */}
      <div style={{ display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderBottom:`1px solid ${C.border}`,background:C.s1,flexShrink:0,flexWrap:"wrap" }}>
        <button onClick={()=>setView("sites")} style={{ ...S.btnGhost,padding:"5px 12px",fontSize:12 }}>← Sites</button>
        <button onClick={()=>setView("wizard")} style={{ ...S.btnGhost,padding:"5px 12px",fontSize:12 }}>← Rebuild</button>
        <div style={{ display:"flex",gap:2,flex:1,flexWrap:"wrap" }}>
          {pages.map((pg,i)=>(
            <button key={pg.id} onClick={()=>setActivePage(i)}
              style={{ padding:"5px 13px",borderRadius:6,border:"none",fontSize:13,
                fontWeight:i===activePage?600:400,cursor:"pointer",fontFamily:"inherit",
                background:i===activePage?C.accent:"transparent",
                color:i===activePage?"#fff":C.t2 }}>{pg.name}</button>
          ))}
        </div>
        <div style={{ display:"flex",alignItems:"center",gap:6,flexShrink:0 }}>
          <div style={{ display:"flex",background:C.s3,borderRadius:6,padding:2,gap:2 }}>
            {[["desktop","⊡"],["mobile","▭"]].map(([d,icon])=>(
              <button key={d} onClick={()=>setDevice(d)}
                style={{ padding:"4px 8px",borderRadius:5,border:"none",fontSize:13,fontFamily:"inherit",
                  background:device===d?C.s1:"transparent",color:device===d?C.text:C.t3,cursor:"pointer" }}>{icon}</button>
            ))}
          </div>
          {versionSaved&&<span style={{ fontSize:12,color:C.success,fontWeight:600 }}>✓ Saved</span>}
          <div style={{ position:"relative" }}>
            <div style={{ display:"flex", gap:0 }}>
              <button onClick={saveVersion} style={{ ...S.btnGhost,padding:"5px 12px",fontSize:12,borderRadius:"5px 0 0 5px" }}>Save Version</button>
              <button onClick={()=>setShowVersions(v=>!v)} disabled={!versions.length}
                style={{ ...S.btnGhost,padding:"5px 8px",fontSize:11,borderRadius:"0 5px 5px 0",borderLeft:"none" }}>
                {versions.length?`▾ ${versions.length}`:"▾"}
              </button>
            </div>
            {showVersions && versions.length>0 && (
              <div style={{ position:"absolute",top:"calc(100% + 6px)",right:0,background:C.s1,border:`1px solid ${C.border}`,borderRadius:8,boxShadow:"0 8px 24px rgba(0,0,0,0.12)",zIndex:200,minWidth:260,overflow:"hidden" }}
                onMouseLeave={()=>setShowVersions(false)}>
                <div style={{ padding:"8px 12px",fontSize:11,fontWeight:700,color:C.t3,letterSpacing:"0.06em",textTransform:"uppercase",borderBottom:`1px solid ${C.border}` }}>
                  Saved Versions
                </div>
                {versions.map((v,i)=>(
                  <button key={v.id} onClick={()=>{ setPages(v.pages); setShowVersions(false); saveSiteToList(v.pages, bizInfo); }}
                    style={{ display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",padding:"10px 14px",border:"none",background:i===0?`${C.accent}08`:"transparent",cursor:"pointer",fontFamily:"inherit",textAlign:"left",borderBottom:i<versions.length-1?`1px solid ${C.border}`:"none" }}>
                    <div>
                      <div style={{ fontSize:13,fontWeight:600,color:C.text }}>{v.label}</div>
                      <div style={{ fontSize:11,color:C.t3 }}>{new Date(v.savedAt).toLocaleString()}</div>
                    </div>
                    <span style={{ fontSize:11,color:C.accent,fontWeight:600 }}>Restore</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {wbModifying && <span style={{ fontSize:12,color:C.accent,fontWeight:600 }}>✦ AI is modifying…</span>}
          {wbModifyDone && <span style={{ fontSize:12,color:C.success,fontWeight:600 }}>✓ Applied</span>}
          <button onClick={()=>{ setImgPickerTab("generate"); setShowImgPicker(true); }} style={{ ...S.btnGhost,padding:"5px 12px",fontSize:12,color:C.accent,borderColor:`${C.accent}50` }}>✦ Image</button>
          <button onClick={openInBrowser} style={{ ...S.btnGhost,padding:"5px 12px",fontSize:12 }}>⊡ Full Preview</button>
          <button onClick={downloadPage} style={{ ...S.btnGhost,padding:"5px 12px",fontSize:12 }}>↓ Download</button>
          <button onClick={()=>setShowPublish(true)} style={{ ...S.btn,padding:"6px 16px",fontSize:13 }}>⊕ Publish →</button>
        </div>
      </div>

      <div style={{ display:"flex",flex:1,overflow:"hidden" }}>
        {/* Preview */}
        <div style={{ flex:1, overflow:device==="mobile"?"auto":"hidden", background:device==="mobile"?C.s3:"#fff",
          display:"flex", alignItems:"flex-start", justifyContent:"center",
          padding:device==="mobile"?"24px 0":0, position:"relative" }}>
          {curPage?.html ? (
            <iframe
              ref={iframeRef}
              key={`${activePage}-${modifyCounter}`}
              style={{ width:device==="mobile"?390:"100%",
                height:device==="mobile"?780:"100%",
                minHeight:device==="mobile"?780:"100%",
                border:"none", display:"block",
                borderRadius:device==="mobile"?16:0,
                boxShadow:device==="mobile"?"0 8px 48px rgba(0,0,0,0.2)":"none",
                position:device==="mobile"?"relative":"absolute",
                top:0, left:0 }}
              sandbox="allow-same-origin allow-scripts"
            />
          ) : (
            <div style={{ display:"flex",alignItems:"center",justifyContent:"center",width:"100%",height:"100%",color:C.t3 }}>No content yet</div>
          )}
        </div>
      </div>

      {/* AI edit bar — always visible at bottom */}
      <div style={{ borderTop:`1px solid ${C.border}`, background:C.s1, padding:"10px 16px", display:"flex", gap:10, alignItems:"center", flexShrink:0 }}>
        <div style={{ flex:1, display:"flex", gap:8, alignItems:"center", background:C.s2, border:`1px solid ${wbModifying?C.accent:C.border}`, borderRadius:8, padding:"8px 14px", transition:"border-color 0.2s" }}>
          <span style={{ fontSize:14, color:C.accent, flexShrink:0 }}>✦</span>
          <input
            value={aiInput}
            onChange={e=>setAiInput(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&applyAIChange()}
            placeholder={wbModifying ? "AI is updating the page…" : `Tell AI what to change on the ${curPage?.name||"current"} page…`}
            disabled={wbModifying}
            style={{ flex:1, border:"none", background:"transparent", fontSize:13, color:C.text, outline:"none", fontFamily:"inherit" }}
          />
          {wbModifyDone && <span style={{ fontSize:12, color:C.success, fontWeight:600, flexShrink:0 }}>✓ Applied</span>}
        </div>
        <button onClick={applyAIChange} disabled={!aiInput.trim()||wbModifying}
          style={{ ...S.btn, padding:"8px 18px", fontSize:13, flexShrink:0 }}>
          {wbModifying ? "…" : "Apply →"}
        </button>
      </div>

      {/* Publish modal */}
      {/* Undo toast — memory only, auto-dismisses in 8s */}
      {showImgUndo && (
        <div style={{ position:"fixed", bottom:80, left:"50%", transform:"translateX(-50%)",
          zIndex:3000, background:"#1c2437", color:"#fff", borderRadius:24, padding:"10px 20px",
          display:"flex", alignItems:"center", gap:12, boxShadow:"0 8px 32px rgba(0,0,0,0.3)",
          fontSize:13, fontWeight:500, whiteSpace:"nowrap" }}>
          <span>Image replaced</span>
          <button
            onClick={() => {
              const u = imgUndoRef.current;
              if (!u) return;
              // Restore previous HTML in iframe and state
              const restored = pagesRef.current.map((p,i) => i===u.pageIdx ? {...p, html:u.html} : p);
              pagesRef.current = restored; setPages(restored);
              if (iframeRef.current) iframeRef.current.srcdoc = prepareHTML(u.html);
              const sid = activeSiteIdRef.current;
              if (sid) {
                const us = sitesRef.current.map(s => s.id===sid ? {...s, pages:restored, updatedAt:new Date().toISOString()} : s);
                sitesRef.current = us; setSites(us); store.set("website-sites", us);
              }
              clearTimeout(imgUndoTimer.current);
              setShowImgUndo(false);
              imgUndoRef.current = null;
            }}
            style={{ background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.25)",
              color:"#fff", borderRadius:12, padding:"4px 12px", fontSize:12, fontWeight:700,
              cursor:"pointer", fontFamily:"inherit" }}>
            Undo
          </button>
          <button onClick={() => { clearTimeout(imgUndoTimer.current); setShowImgUndo(false); imgUndoRef.current = null; }}
            style={{ background:"none", border:"none", color:"rgba(255,255,255,0.5)", cursor:"pointer", fontSize:16, lineHeight:1, padding:0 }}>✕</button>
        </div>
      )}
      {showImgPicker && (
        <MediaPickerModal
          filter="image"
          title="Image for Website"
          profile={profile}
          defaultTab={imgPickerTab}
          imageQuality={imageQuality}
          onSelect={handleImgSelect}
          onClose={() => {
            setShowImgPicker(false);
            setPendingImgSrc(null);
            iframeRef.current?.contentWindow?.postMessage({ type:"wb-img-replace", url:null }, "*");
          }}
        />
      )}
      {showPublish&&(
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000 }}>
          <div style={{ background:C.s1,borderRadius:16,padding:32,width:480,maxWidth:"90vw" }}>
            <div style={{ fontSize:18,fontWeight:700,color:C.text,marginBottom:6 }}>Publish Your Website</div>
            <div style={{ fontSize:13,color:C.t2,marginBottom:24 }}>Enter your domain and we'll give you the DNS records to point it to your live site.</div>
            {publishDone?(
              <div>
                <div style={{ fontSize:14,color:C.success,fontWeight:600,marginBottom:10 }}>✓ Site is live!</div>
                <div style={{ background:C.s2,borderRadius:8,padding:"10px 14px",marginBottom:14 }}>
                  <div style={{ fontSize:11,color:C.t3,marginBottom:3 }}>Live preview URL (available immediately)</div>
                  <div style={{ fontSize:14,fontWeight:700,color:C.accent }}>{`${bizInfo.name.toLowerCase().replace(/[^a-z0-9]/g,"-")}.kurbai.site`}</div>
                </div>
                <div style={{ fontSize:13,color:C.text,fontWeight:600,marginBottom:8 }}>To connect {publishDomain}, add these DNS records:</div>
                <div style={{ background:"#1e1e2e",borderRadius:8,padding:"12px 16px",fontFamily:"monospace",fontSize:12,color:"#a9b1c6",marginBottom:6 }}>
                  <div>Type: CNAME &nbsp; Name: @</div>
                  <div style={{ color:"#7A5AF8" }}>Value: kurbai.site</div>
                  <div style={{ marginTop:8 }}>Type: CNAME &nbsp; Name: www</div>
                  <div style={{ color:"#7A5AF8" }}>Value: kurbai.site</div>
                </div>
                <div style={{ fontSize:11,color:C.t3,marginBottom:20 }}>DNS changes propagate in 15 min – 48 hours.</div>
                <button onClick={()=>{setShowPublish(false);setPublishDone(false);}} style={{ ...S.btn,width:"100%",padding:"10px" }}>Done</button>
              </div>
            ):(
              <div>
                <Field label="Your domain name">
                  <input style={S.input} value={publishDomain} onChange={e=>setPublishDomain(e.target.value)} placeholder="kitchenmaster.com"/>
                </Field>
                <div style={{ fontSize:12,color:C.t3,marginTop:-8,marginBottom:20 }}>No https:// — just the domain, e.g. kitchenmaster.com</div>
                <div style={{ display:"flex",gap:10 }}>
                  <button onClick={()=>setShowPublish(false)} style={{ ...S.btnGhost,flex:1,padding:"10px" }}>Cancel</button>
                  <button onClick={()=>setPublishDone(true)} disabled={!publishDomain}
                    style={{ ...S.btn,flex:2,padding:"10px",fontWeight:700 }}>⊕ Publish Site →</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
/* ─── CONTENT WRITER ────────────────────────────────────────────────── */

// mdToHtml, MiniEditor imported from ./shared/components.jsx

// parseInline, MarkdownView, CW_TYPE_COL imported from ./shared/components.jsx


// LibraryCard imported from ./shared/components.jsx

/* ─── RESERVATIONS ──────────────────────────────────────────────────── */
function Reservations() {
  const isMobile = useIsMobile();
  const TYPES = ["Reservation","Appointment","Service Call","Consultation","Event"];
  const blank = { name:"", email:"", phone:"", date:"", time:"", party:"2", type:"Reservation", notes:"" };
  const [f, setF] = useState(blank);
  const [list, setList] = useState([]); const [saved, setSaved] = useState(false); const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const set = k => e => setF(p => ({ ...p, [k]: e.target.value }));
  useEffect(() => {
    store.get("reservations").then(d => { if (d) setList(d); });
    const refresh = () => store.get("reservations").then(d => { if (d) setList(d); });
    window.addEventListener("reservations-updated", refresh);
    return () => window.removeEventListener("reservations-updated", refresh);
  }, []);
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    window._reservationGetContext = () => ({
      total: list.length,
      todayCount: list.filter(r => r.status === "confirmed" && r.date === today).length,
      upcoming: list.filter(r => r.status === "confirmed" && r.date >= today).slice(0, 5),
    });
    return () => { window._reservationGetContext = null; };
  }, [list]);

  const save = async () => {
    const r = { ...f, id: Date.now(), status: "confirmed", createdAt: new Date().toLocaleDateString() };
    const updated = [r, ...list]; setList(updated); await store.set("reservations", updated);
    setF(blank); setSaved(true); setTimeout(() => { setSaved(false); setShowForm(false); }, 1800);
  };
  const cancel = async id => { const u = list.map(r => r.id === id ? { ...r, status:"cancelled" } : r); setList(u); await store.set("reservations", u); };
  const del    = async id => { const u = list.filter(r => r.id !== id); setList(u); await store.set("reservations", u); };

  const today    = new Date().toISOString().split("T")[0];
  const filtered = filter === "all" ? list : list.filter(r => r.status === filter);
  const typeColor = { Reservation:C.accent, Appointment:"#6366f1", "Service Call":"#0ea5e9", Consultation:"#8b5cf6", Event:"#f59e0b" };

  const Form = () => (
    <div style={{ ...S.card, padding:24, marginBottom:24 }}>
      <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:20 }}>New Booking</div>
      <Field label="Booking Type">
        <select style={S.input} value={f.type} onChange={set("type")}>
          {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:12 }}>
        <Field label="Name"><input style={S.input} value={f.name} onChange={set("name")} placeholder="Full name" /></Field>
        <Field label="Phone"><input style={S.input} value={f.phone} onChange={set("phone")} placeholder="+1 (555) 000-0000" /></Field>
        <Field label="Email"><input style={S.input} type="email" value={f.email} onChange={set("email")} placeholder="email@example.com" /></Field>
        {f.type === "Reservation" || f.type === "Event"
          ? <Field label="Party Size"><select style={S.input} value={f.party} onChange={set("party")}>{[1,2,3,4,5,6,7,8,10,12,15,20].map(n=><option key={n} value={n}>{n} {n===1?"guest":"guests"}</option>)}</select></Field>
          : <Field label="Duration"><select style={S.input} value={f.party} onChange={set("party")}><option value="30">30 min</option><option value="60">1 hour</option><option value="90">1.5 hours</option><option value="120">2 hours</option></select></Field>
        }
        <Field label="Date"><input style={S.input} type="date" min={today} value={f.date} onChange={set("date")} /></Field>
        <Field label="Time"><input style={S.input} type="time" value={f.time} onChange={set("time")} /></Field>
      </div>
      <Field label="Notes / Special Requests">
        <textarea style={{ ...S.input, height:60, resize:"none" }} value={f.notes} onChange={set("notes")} placeholder="Any details, preferences, or requirements…" />
      </Field>
      <div style={{ display:"flex", gap:10 }}>
        <button style={{ ...S.btn, flex:1 }} onClick={save} disabled={!f.name || !f.date || !f.time}>{saved ? "✓ Saved!" : "Confirm Booking"}</button>
        <button style={S.btnGhost} onClick={() => setShowForm(false)}>Cancel</button>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div>
          <h2 style={S.h2}>Bookings</h2>
          <p style={{ ...S.sub, marginBottom:0 }}>Reservations, appointments, service calls, and events.</p>
        </div>
        <button style={S.btn} onClick={() => setShowForm(v => !v)}>+ New Booking</button>
      </div>
      {showForm && <Form />}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
        {["all","confirmed","cancelled"].map(s => <Chip key={s} label={s === "all" ? `All (${list.length})` : s.charAt(0).toUpperCase()+s.slice(1)} active={filter===s} onClick={() => setFilter(s)} />)}
      </div>
      {filtered.length === 0 ? (
        <div style={{ ...S.card, padding:48, textAlign:"center", color:C.t3, fontSize:13 }}>No bookings yet. Click "+ New Booking" to add one.</div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {filtered.map(r => (
            <div key={r.id} style={{ ...S.card, padding:16, display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, opacity:r.status==="cancelled"?0.45:1, flexWrap:"wrap" }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap" }}>
                  <span style={{ color:C.text, fontSize:14, fontWeight:600 }}>{r.name}</span>
                  <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:`${typeColor[r.type]||C.accent}20`, color:typeColor[r.type]||C.accent, fontWeight:600 }}>{r.type||"Reservation"}</span>
                </div>
                <div style={{ color:C.t2, fontSize:12 }}>{r.date} at {r.time}{r.party ? ` · ${r.party} ${(r.type==="Appointment"||r.type==="Service Call"||r.type==="Consultation") ? "min" : "guests"}` : ""}</div>
                {r.email && <div style={{ color:C.t3, fontSize:12, marginTop:2 }}>{r.email}{r.phone ? ` · ${r.phone}` : ""}</div>}
                {r.notes && <div style={{ color:C.accent, fontSize:12, marginTop:3, fontStyle:"italic" }}>{r.notes}</div>}
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
                <Badge label={r.status} color={r.status==="confirmed"?C.success:C.danger} />
                {r.status==="confirmed" && <button style={{ ...S.btnGhost, padding:"4px 10px", fontSize:12 }} onClick={() => cancel(r.id)}>Cancel</button>}
                <button style={{ ...S.btnGhost, padding:"4px 10px", fontSize:12, color:C.danger, borderColor:"transparent" }} onClick={() => del(r.id)}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── CRM HELPER COMPONENTS ─────────────────────────────────────────── */
// SP7: score badge + recommended-action chip helpers. Used by the leads table,
// the sidebar, and the detail-drawer breakdown panel.

/* ─── LEAD AGENT → src/modules/lead-agent.jsx (lazy-loaded) ─── */

// ─── CONVERSION ATTRIBUTION HELPERS ──────────────────────────────────────────
/* ─── CONVERSION → src/modules/conversion.jsx (lazy-loaded) ─── */

/* ─── CONTACTS ──────────────────────────────────────────────────────── */
/* ─── INQUIRIES ─────────────────────────────────────────────────────── */
function Inquiries() {
  const isMobile = useIsMobile();
  const blank = { name:"", email:"", phone:"", source:"website", subject:"", message:"" };
  const [list, setList]     = useState([]);
  const [f, setF]           = useState(blank);
  const [show, setShow]     = useState(false);
  const [filter, setFilter] = useState("new");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [aiReply, setAiReply]     = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const set = k => e => setF(p => ({ ...p, [k]: e.target.value }));

  useEffect(() => { store.get("inquiries").then(d => { if (d) setList(d); }); }, []);

  const add = async () => {
    const item = { ...f, id: Date.now(), status:"new", createdAt: new Date().toLocaleDateString(), createdTs: Date.now() };
    const u = [item, ...list]; setList(u); await store.set("inquiries", u);
    setF(blank); setShow(false);
  };

  const setStatus = async (id, status) => {
    const u = list.map(i => i.id === id ? { ...i, status } : i);
    setList(u); await store.set("inquiries", u);
    if (active?.id === id) setActive({ ...active, status });
  };

  const del = async id => {
    const u = list.filter(i => i.id !== id); setList(u); await store.set("inquiries", u);
    if (active?.id === id) setActive(null);
  };

  const genReply = async (inq) => {
    setAiLoading(true); setAiReply("");
    try {
      const r = await ai(`Write a professional, warm reply to this customer inquiry.\nFrom: ${inq.name}\nSubject: ${inq.subject||"(no subject)"}\nMessage: "${inq.message}"\n\nWrite a helpful, friendly response. 3-5 sentences. Do not use placeholders like [Your Name].`, "You are a helpful local business owner responding to a customer inquiry.");
      setAiReply(r);
    } catch(e) {}
    setAiLoading(false);
  };

  const STATUSES = ["new","in progress","resolved","spam"];
  const statusCol = { new:"#0ea5e9", "in progress":C.accent, resolved:C.success, spam:C.t3 };
  const SOURCES   = ["website","google","social","phone","walk-in","referral","other"];

  const counts  = Object.fromEntries(STATUSES.map(s => [s, list.filter(i => i.status === s).length]));
  const filtered = list.filter(i => {
    const matchStatus = filter === "all" || i.status === filter;
    const q = search.toLowerCase();
    const matchSearch = !q || i.name.toLowerCase().includes(q) || (i.email||"").toLowerCase().includes(q) || (i.subject||"").toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div>
          <h2 style={S.h2}>Inquiries</h2>
          <p style={{ ...S.sub, marginBottom:0 }}>Website forms, leads, and inbound messages.</p>
        </div>
        <button style={S.btn} onClick={() => setShow(v => !v)}>+ Add Inquiry</button>
      </div>

      {/* Status tabs */}
      <div style={{ display:"flex", gap:6, marginBottom:20, flexWrap:"wrap" }}>
        {[["all", list.length], ...STATUSES.map(s => [s, counts[s]])].map(([s, count]) => (
          <button key={s} onClick={() => setFilter(s)}
            style={{ padding:"6px 14px", borderRadius:20, border:`1px solid ${filter===s ? (statusCol[s]||C.accent) : C.border}`, background: filter===s ? `${statusCol[s]||C.accent}18` : "transparent", color: filter===s ? (statusCol[s]||C.accent) : C.t2, fontSize:12, cursor:"pointer", fontFamily:"inherit", textTransform:"capitalize", transition:"all 0.15s" }}>
            {s} {count > 0 && <span style={{ fontSize:10, opacity:0.8 }}>({count})</span>}
          </button>
        ))}
      </div>

      {show && (
        <div style={{ ...S.card, padding:24, marginBottom:24 }}>
          <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:18 }}>New Inquiry</div>
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:14 }}>
            <Field label="Name"><input style={S.input} value={f.name} onChange={set("name")} placeholder="Contact name" /></Field>
            <Field label="Email"><input style={S.input} type="email" value={f.email} onChange={set("email")} placeholder="email@example.com" /></Field>
            <Field label="Phone"><input style={S.input} value={f.phone} onChange={set("phone")} placeholder="+1 (555) 000-0000" /></Field>
            <Field label="Source">
              <select style={S.input} value={f.source} onChange={set("source")}>
                {SOURCES.map(s => <option key={s} value={s} style={{ textTransform:"capitalize" }}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Subject"><input style={S.input} value={f.subject} onChange={set("subject")} placeholder="What is this about?" /></Field>
          <Field label="Message">
            <textarea style={{ ...S.input, height:80, resize:"none" }} value={f.message} onChange={set("message")} placeholder="Details of the inquiry…" />
          </Field>
          <div style={{ display:"flex", gap:10 }}>
            <button style={S.btn} onClick={add} disabled={!f.name}>Save Inquiry</button>
            <button style={S.btnGhost} onClick={() => setShow(false)}>Cancel</button>
          </div>
        </div>
      )}

      <input style={{ ...S.input, maxWidth:280, marginBottom:16 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search inquiries…" />

      <div style={{ display:"flex", gap:16, alignItems:"flex-start", flexWrap: isMobile ? "wrap" : "nowrap" }}>
        {/* List */}
        <div style={{ flex:"1 1 320px", minWidth:0, display:"flex", flexDirection:"column", gap:8 }}>
          {filtered.length === 0 ? (
            <div style={{ ...S.card, padding:40, textAlign:"center", color:C.t3, fontSize:13 }}>
              {list.length ? "No matching inquiries." : "No inquiries yet. Add one manually or connect your website form."}
            </div>
          ) : filtered.map(inq => (
            <div key={inq.id} onClick={() => { setActive(inq); setAiReply(""); }}
              style={{ ...S.card, padding:16, cursor:"pointer", border:`1px solid ${active?.id===inq.id ? C.accent : C.border}`, background: active?.id===inq.id ? `${C.accent}08` : C.s1, transition:"all 0.12s" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:2 }}>{inq.name}</div>
                  {inq.subject && <div style={{ fontSize:12, color:C.t2, marginBottom:4 }}>{inq.subject}</div>}
                  <div style={{ fontSize:11, color:C.t3 }}>{inq.source} · {inq.createdAt}</div>
                </div>
                <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:`${statusCol[inq.status]}20`, color:statusCol[inq.status], fontWeight:600, textTransform:"capitalize", flexShrink:0 }}>{inq.status}</span>
              </div>
              {inq.message && <div style={{ fontSize:12, color:C.t2, marginTop:8, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{inq.message}</div>}
            </div>
          ))}
        </div>

        {/* Detail panel */}
        {active && (
          <div style={{ flex:"1 1 320px", minWidth:0, ...S.card, padding:20, position: isMobile ? "static" : "sticky", top:20 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:C.text }}>{active.name}</div>
                <div style={{ fontSize:12, color:C.t3, marginTop:2 }}>{active.email}{active.phone ? ` · ${active.phone}` : ""}</div>
              </div>
              <button onClick={() => setActive(null)} style={{ background:"none", border:"none", color:C.t3, cursor:"pointer", fontSize:16 }}>✕</button>
            </div>
            {active.subject && <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:8 }}>{active.subject}</div>}
            {active.message && <div style={{ fontSize:13, color:C.t2, lineHeight:1.6, marginBottom:16, padding:12, background:C.s2, borderRadius:8 }}>{active.message}</div>}
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:16 }}>
              {STATUSES.map(s => (
                <button key={s} onClick={() => setStatus(active.id, s)}
                  style={{ padding:"5px 12px", borderRadius:16, border:`1px solid ${active.status===s ? statusCol[s] : C.border}`, background: active.status===s ? `${statusCol[s]}20` : "transparent", color: active.status===s ? statusCol[s] : C.t2, fontSize:11, cursor:"pointer", fontFamily:"inherit", textTransform:"capitalize" }}>
                  {s}
                </button>
              ))}
            </div>
            <button style={{ ...S.btn, width:"100%", marginBottom:10 }} onClick={() => genReply(active)} disabled={aiLoading}>
              {aiLoading ? "Writing reply…" : "✦ Generate AI Reply"}
            </button>
            {aiReply && <AIBox content={aiReply} loading={false} />}
            <button style={{ ...S.btnGhost, width:"100%", marginTop:10, color:C.danger, borderColor:`${C.danger}40`, fontSize:12 }} onClick={() => del(active.id)}>Delete Inquiry</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── CONTENT ENGINE / CONTENT AGENT → src/modules/content-engine.jsx (lazy-loaded) ─── */

/* ─── SOCIAL MEDIA → src/modules/social.jsx (lazy-loaded) ───────────── */

/* ─── BUSINESS BRAIN → src/modules/business-brain.jsx (lazy-loaded) ───── */

// ─── CALL TRACKING ──────────────────────────────────────────────────────────
/* ─── CALL TRACKING → src/modules/call-tracking.jsx (lazy-loaded) ─── */

/* ─── BLOGGER FINDER ────────────────────────────────────────────────── */
/* ─── GOOGLE BUSINESS PROFILE ───────────────────────────────────────── */




/* ─── APP SHELL ─────────────────────────────────────────────────────── */
/* ─── ONBOARDING ────────────────────────────────────────────────────── */
// NICHES → src/shared/constants.js

function AgentOnboarding({ agentSubdomain, wlConfig, onComplete }) {
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const agentName = wlConfig?.app_name || (agentSubdomain === "ca" ? "Content Agent" : "Content Agent");
  const accent = wlConfig?.primary_color || "#6366f1";

  const submit = async () => {
    if (!name.trim()) return;
    setLoading(true);
    await onComplete({ name: name.trim(), website: website.trim(), niche: "other", goal: "content", category: "other" });
    setLoading(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: C.bg, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter',-apple-system,sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 480, padding: "0 24px" }}>
        <div style={{ marginBottom: 32, textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: C.text, marginBottom: 8 }}>{agentName}</div>
          <div style={{ fontSize: 15, color: C.t2 }}>Tell us about your business to get started.</div>
        </div>
        <div style={{ ...S.card, padding: 32 }}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: C.t2, display: "block", marginBottom: 8 }}>Business name</label>
            <input value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === "Enter" && submit()}
              placeholder="e.g. Mario's Pizza" autoFocus
              style={{ ...S.input, fontSize: 15, padding: "12px 14px" }} />
          </div>
          <div style={{ marginBottom: 28 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: C.t2, display: "block", marginBottom: 8 }}>Website URL <span style={{ color: C.t3, fontWeight: 400 }}>(optional — helps us tailor your content)</span></label>
            <input value={website} onChange={e => setWebsite(e.target.value)} onKeyDown={e => e.key === "Enter" && submit()}
              placeholder="https://yourwebsite.com"
              style={{ ...S.input, fontSize: 15, padding: "12px 14px" }} />
          </div>
          <button onClick={submit} disabled={!name.trim() || loading}
            style={{ ...S.btn, width: "100%", padding: 14, fontSize: 15, background: accent, opacity: !name.trim() || loading ? 0.6 : 1 }}>
            {loading ? "Setting up…" : "Get Started →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Onboarding({ onComplete, onCancel, existingProfile, editMode, wlConfig, inline = false }) {
  const [step, setStep]       = useState(editMode ? 2 : 1);
  const [dir,  setDir]        = useState(1);
  const [anim, setAnim]       = useState(false);
  const [profile, setProfile] = useState(existingProfile || {
    niche:"", name:"", category:"", vibe:"", description:"",
    address:"", city:"", phone:"", website:"", goal:"all",
  });
  const set = k => e => setProfile(p => ({...p, [k]: typeof e === "string" ? e : e.target.value}));

  // Business search autocomplete
  const [bizQuery,     setBizQuery]     = useState(existingProfile?.name || "");
  const [bizSearching, setBizSearching] = useState(false);
  const [bizResults,   setBizResults]   = useState([]);
  const [bizSelected,  setBizSelected]  = useState(!!existingProfile?.name);
  const bizSearchTimer = useRef(null);

  const searchBiz = async (q) => {
    if (!q.trim()) { setBizResults([]); return; }
    setBizSearching(true); setBizResults([]);
    try {
      const res  = await fetch("/api/business/search", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ query: q }) });
      const data = await res.json();
      if (res.ok) setBizResults(data.businesses || []);
    } catch {}
    setBizSearching(false);
  };

  const onBizQueryChange = (val) => {
    setBizQuery(val);
    setBizSelected(false);
    setProfile(p => ({...p, name: val}));
    clearTimeout(bizSearchTimer.current);
    if (val.trim().length >= 3) {
      bizSearchTimer.current = setTimeout(() => searchBiz(val), 600);
    } else {
      setBizResults([]);
    }
  };

  const selectBiz = (biz) => {
    setBizSelected(true);
    setBizQuery(biz.title);
    setBizResults([]);
    // Extract city: "3131 Main St Suite B5, Suwanee, GA 30024" → "Suwanee"
    // City is the first part that doesn't start with a digit and isn't a state/zip/country
    const addrParts = (biz.address || "").split(",").map(s => s.trim());
    const city = addrParts.find((p, i) => i > 0 && !/^\d/.test(p) && !/^(United States|USA|US)$/i.test(p) && !/^[A-Z]{2}\s*\d/.test(p)) || addrParts[1] || "";
    setProfile(p => ({
      ...p,
      name:     biz.title   || p.name,
      address:  biz.address || p.address,
      city:     city        || p.city,
      phone:    biz.phone   || p.phone,
      website:  biz.website || p.website,
      placeId:  biz.placeId || p.placeId,
    }));
  };

  const TOTAL = editMode ? 3 : 4; // edit skips step 1 (niche picker)
  const niche = NICHES.find(n => n.id === profile.niche);

  const go = (next) => {
    setDir(next > step ? 1 : -1);
    setAnim(true);
    setTimeout(() => { setStep(next); setAnim(false); }, 160);
  };

  const [duplicateErr, setDuplicateErr] = useState("");

  const finish = async () => {
    setDuplicateErr("");
    // Block creation if any other workspace on the platform already records
    // this website URL. editMode skips the check — the user is updating their
    // existing profile, not creating a new workspace.
    if (!editMode && profile.website) {
      const dup = await checkWebsiteDomain(profile.website);
      if (dup?.exists) {
        setDuplicateErr(`A workspace for ${dup.host} already exists ("${dup.workspaceName}"). Use a different website, or contact the existing owner.`);
        return;
      }
    }
    if (!inline) {
      await store.set("business-profile", profile);
      await store.set("onboarding-done", true);
    }
    onComplete(profile);
  };

  const canNext = () => {
    if (step === 1) return !!profile.niche;
    if (step === 2) return profile.name.trim().length > 1 && !!profile.category;
    if (step === 3) return profile.city.trim().length > 1;
    return true;
  };

  const stepLabels = [
    ["Business Type",                          "Restaurant, services or clinic"],
    [niche ? `Your ${niche.label}` : "Your Business", "Name, type & description"],
    ["Location & Contact",                     "Where you are"],
    ["Your Goal",                              "What matters most"],
  ];

  const renderStep = () => {

    // ── Step 1: Niche picker ────────────────────────────────────────────
    if (step === 1) return (
      <div>
        <div style={{ marginBottom:36 }}>
          <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:34, fontWeight:300, color:C.text, marginBottom:8, lineHeight:1.2 }}>
            What type of business<br />are you running?
          </div>
          <div style={{ fontSize:14, color:C.t2, lineHeight:1.6 }}>
            We'll tailor every tool, template, and AI prompt to your industry.
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {NICHES.map(n => {
            const active = profile.niche === n.id;
            return (
              <button key={n.id} onClick={() => set("niche")(n.id)}
                style={{ ...S.card, padding:"22px 24px", cursor:"pointer", textAlign:"left", border:`2px solid ${active?C.accent:C.border}`, background:active?`${C.accent}12`:C.s1, transition:"all 0.15s", display:"flex", alignItems:"center", gap:20 }}>
                <span style={{ fontSize:36, flexShrink:0 }}>{n.icon}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:16, fontWeight:700, color:C.text, marginBottom:3 }}>{n.label}</div>
                  <div style={{ fontSize:13, color:C.t2 }}>{n.desc}</div>
                </div>
                <div style={{ width:22, height:22, borderRadius:"50%", border:`2px solid ${active?C.accent:C.border}`, background:active?C.accent:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all 0.15s" }}>
                  {active && <div style={{ width:8, height:8, borderRadius:"50%", background:"#fff" }} />}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );

    // ── Step 2: Business details (niche-specific) ───────────────────────
    if (step === 2) return (
      <div>
        <div style={{ marginBottom:36 }}>
          <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:34, fontWeight:300, color:C.text, marginBottom:8, lineHeight:1.2 }}>
            Tell us about<br />your {niche?.label?.toLowerCase()}.
          </div>
          <div style={{ fontSize:14, color:C.t2, lineHeight:1.6 }}>
            Fill in a few basics — we'll pre-fill every tool so you can launch your first campaign right away.
          </div>
        </div>
        <Field label={`${niche?.nameLabel || "Business Name"} *`}>
          <div style={{ position:"relative" }}>
            <input
              style={{ ...S.input, fontSize:16, padding:"13px 16px", paddingRight: bizSearching ? 40 : bizSelected ? 40 : undefined }}
              value={bizQuery}
              onChange={e => onBizQueryChange(e.target.value)}
              onKeyDown={e => e.key === "Enter" && searchBiz(bizQuery)}
              placeholder={niche?.namePlaceholder || "Search your Google Business…"}
              readOnly={bizSelected}
            />
            {bizSearching && (
              <span style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", fontSize:12, color:C.t3 }}>…</span>
            )}
            {bizSelected && (
              <button onClick={() => { setBizSelected(false); setBizQuery(""); setBizResults([]); setProfile(p => ({...p, name:"", address:"", city:"", phone:"", website:"", placeId:""})); }}
                style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:C.t3, cursor:"pointer", fontSize:18, lineHeight:1 }}>×</button>
            )}
            {bizResults.length > 0 && !bizSelected && (
              <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, right:0, zIndex:100,
                background:"#1a1510", border:`1px solid ${C.accent}`, borderRadius:8, overflow:"hidden", boxShadow:"0 8px 24px rgba(0,0,0,0.5)" }}>
                {bizResults.map((biz, i) => (
                  <div key={i} onClick={() => selectBiz(biz)}
                    style={{ padding:"10px 14px", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center",
                      borderBottom: i < bizResults.length-1 ? `1px solid ${C.border}` : "none", transition:"background 0.1s" }}
                    onMouseEnter={e => e.currentTarget.style.background="#ffffff0d"}
                    onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:C.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{biz.title}</div>
                      <div style={{ fontSize:11, color:C.t3, marginTop:2 }}>{biz.address}</div>
                      {biz.phone && <div style={{ fontSize:11, color:C.t3 }}>{biz.phone}</div>}
                    </div>
                    {biz.rating && (
                      <div style={{ textAlign:"right", flexShrink:0, marginLeft:16 }}>
                        <div style={{ fontSize:13, color:RV, fontWeight:700 }}>★ {biz.rating}</div>
                        <div style={{ fontSize:11, color:C.t3 }}>{biz.reviewCount ?? "?"} reviews</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {bizSelected && (
            <div style={{ marginTop:6, fontSize:11, color:C.success, display:"flex", alignItems:"center", gap:5 }}>
              <span>✓</span> Matched from Google Business Profile
            </div>
          )}
          {!bizSelected && bizQuery.length < 3 && (
            <div style={{ marginTop:6, fontSize:11, color:C.t3 }}>Type at least 3 characters to search Google Business</div>
          )}
        </Field>
        <Field label={`${niche?.categoryLabel || "Category"} *`}>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {(niche?.categories || []).map(c => (
              <button key={c} onClick={() => set("category")(c)}
                style={{ padding:"7px 14px", borderRadius:20, border:`1px solid ${profile.category===c?C.accent:C.border}`, background:profile.category===c?`${C.accent}18`:C.s2, color:profile.category===c?C.accent:C.t2, fontSize:12, fontWeight:profile.category===c?600:400, cursor:"pointer", fontFamily:"inherit", transition:"all 0.1s" }}>
                {c}
              </button>
            ))}
          </div>
        </Field>
        {niche?.vibes?.length > 0 && (
          <Field label={niche.vibeLabel}>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {niche.vibes.map(v => (
                <button key={v} onClick={() => set("vibe")(v)}
                  style={{ padding:"7px 14px", borderRadius:20, border:`1px solid ${profile.vibe===v?C.accent:C.border}`, background:profile.vibe===v?`${C.accent}18`:C.s2, color:profile.vibe===v?C.accent:C.t2, fontSize:12, fontWeight:profile.vibe===v?600:400, cursor:"pointer", fontFamily:"inherit", transition:"all 0.1s" }}>
                  {v}
                </button>
              ))}
            </div>
          </Field>
        )}
        <Field label="Short Description (optional)">
          <textarea style={{ ...S.input, height:72, resize:"none" }} value={profile.description} onChange={set("description")}
            placeholder={niche?.descPlaceholder(profile.name, profile.category) || ""} />
        </Field>
      </div>
    );

    // ── Step 3: Location ────────────────────────────────────────────────
    if (step === 3) return (
      <div>
        <div style={{ marginBottom:36 }}>
          <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:34, fontWeight:300, color:C.text, marginBottom:8, lineHeight:1.2 }}>
            Where are you located?
          </div>
          <div style={{ fontSize:14, color:C.t2 }}>Used for local ad targeting, Google Business, and finding nearby influencers.</div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:14 }}>
          <Field label="City / Town *">
            <input style={S.input} value={profile.city} onChange={set("city")} placeholder="e.g. Austin, TX" />
          </Field>
          <Field label="Full Address (optional)">
            <input style={S.input} value={profile.address} onChange={set("address")} placeholder="123 Main Street" />
          </Field>
          <Field label="Phone Number (optional)">
            <input style={S.input} value={profile.phone} onChange={set("phone")} placeholder="+1 (555) 000-0000" />
          </Field>
          <Field label="Website URL (optional)">
            <input style={S.input} value={profile.website} onChange={set("website")} placeholder="https://yourdomain.com" />
          </Field>
        </div>
        {profile.city && (
          <div style={{ marginTop:16, padding:"12px 16px", background:C.s2, borderRadius:8, display:"flex", gap:10, alignItems:"center" }}>
            <span style={{ fontSize:20 }}>{niche?.icon || "📍"}</span>
            <div style={{ fontSize:12, color:C.t2 }}>
              <span style={{ color:C.text, fontWeight:600 }}>{profile.name||"Your business"}</span> in <span style={{ color:C.accent }}>{profile.city}</span>
              {profile.category && <span> · {profile.category}</span>}
            </div>
          </div>
        )}
      </div>
    );

    // ── Step 4: Goal ────────────────────────────────────────────────────
    const goals = niche?.goals || NICHES[0].goals;
    return (
      <div>
        <div style={{ marginBottom:36 }}>
          <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:34, fontWeight:300, color:C.text, marginBottom:8, lineHeight:1.2 }}>
            What's your biggest<br />goal right now?
          </div>
          <div style={{ fontSize:14, color:C.t2 }}>We'll prioritise the right tools and templates for you.</div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:12, marginBottom:32 }}>
          {goals.map(g => {
            const active = profile.goal === g.id;
            return (
              <button key={g.id} onClick={() => set("goal")(g.id)}
                style={{ ...S.card, padding:"20px", cursor:"pointer", textAlign:"left", border:`2px solid ${active?C.accent:C.border}`, background:active?`${C.accent}12`:C.s1, transition:"all 0.15s" }}>
                <div style={{ fontSize:28, marginBottom:10 }}>{g.icon}</div>
                <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:4 }}>{g.label}</div>
                <div style={{ fontSize:12, color:C.t2 }}>{g.desc}</div>
                {active && <div style={{ marginTop:10, fontSize:11, color:C.accent, fontWeight:600 }}>✓ Selected</div>}
              </button>
            );
          })}
        </div>
        <div style={{ ...S.card, padding:"20px 24px", borderColor:`${C.accent}40`, background:`${C.accent}06` }}>
          <div style={{ fontSize:11, color:C.accent, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:14 }}>✦ Your Business Profile</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:10 }}>
            {[
              ["Business",                       profile.name],
              [niche?.categoryLabel||"Category", profile.category],
              ["Location",                       profile.city],
              ["Type",                           niche?.label||"—"],
              ["Goal",                           goals.find(g=>g.id===profile.goal)?.label||"—"],
              ["Website",                        profile.website||"—"],
            ].map(([label,val]) => (
              <div key={label}>
                <div style={{ fontSize:10, color:C.t3, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:2 }}>{label}</div>
                <div style={{ fontSize:13, color:val==="—"?C.t4:C.text }}>{val}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ── Inline layout (Add Business inside the app shell) ──────────────────
  if (inline) return (
    <div style={{ maxWidth:680, margin:"0 auto", padding:"8px 0 40px" }}>
      <div style={{ marginBottom:24 }}>
        <h2 style={S.h2}>Add a Business</h2>
        <p style={{ ...S.sub, marginBottom:0 }}>Set up your new workspace — all tools will be tailored to this business.</p>
      </div>
      {/* Horizontal step progress */}
      <div style={{ display:"flex", borderBottom:`1px solid ${C.border}`, marginBottom:32 }}>
        {stepLabels.map(([title], i) => {
          const num = i + 1;
          const done = step > num;
          const active = step === num;
          return (
            <div key={num} style={{ flex:1, padding:"10px 8px", textAlign:"center", borderBottom:`2px solid ${active?C.accent:done?C.success:"transparent"}`, marginBottom:-1, transition:"border-color 0.2s" }}>
              <div style={{ fontSize:11, fontWeight:active||done?600:400, color:active?C.accent:done?C.success:C.t4, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                {done ? "✓ " : `${num}. `}{title}
              </div>
            </div>
          );
        })}
      </div>
      {/* Step content */}
      <div style={{ opacity:anim?0:1, transform:anim?`translateX(${dir*24}px)`:"translateX(0)", transition:"opacity 0.16s, transform 0.16s" }}>
        {renderStep()}
      </div>
      {duplicateErr && (
        <div style={{ marginTop:24, padding:"12px 16px", background:"#fef2f2", border:`1px solid ${C.danger}40`, borderRadius:8, color:C.danger, fontSize:13, lineHeight:1.5 }}>{duplicateErr}</div>
      )}
      {/* Navigation */}
      <div style={{ display:"flex", gap:12, marginTop:32, alignItems:"center" }}>
        {step > 1 ? (
          <button style={{ ...S.btnGhost, padding:"11px 24px" }} onClick={() => go(step-1)}>← Back</button>
        ) : (
          onCancel && <button style={{ ...S.btnGhost, padding:"11px 24px" }} onClick={onCancel}>Cancel</button>
        )}
        {step < TOTAL ? (
          <button style={{ ...S.btn, padding:"12px 40px", fontSize:15, opacity:canNext()?1:0.4 }}
            onClick={() => { if (canNext()) go(step+1); }} disabled={!canNext()}>
            Continue →
          </button>
        ) : (
          <button style={{ ...S.btn, padding:"13px 48px", fontSize:15, background:`linear-gradient(135deg,${C.accent},${C.accentD})` }}
            onClick={finish}>
            ⚡ Create Business →
          </button>
        )}
      </div>
      {/* Progress dots */}
      <div style={{ display:"flex", gap:8, justifyContent:"center", marginTop:24 }}>
        {Array.from({ length:4 }, (_, i) => i+1).map(n => (
          <div key={n} style={{ width:n===step?24:8, height:8, borderRadius:4, background:n===step?C.accent:n<step?C.success:C.border, transition:"all 0.3s" }} />
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ position:"fixed", inset:0, background:C.bg, zIndex:1000, display:"flex", alignItems:"stretch", fontFamily:"'Inter',-apple-system,sans-serif" }}>
      {/* Left brand panel */}
      <div style={{ width:340, background:C.s1, borderRight:`1px solid ${C.border}`, display:"flex", flexDirection:"column", padding:"48px 36px", flexShrink:0 }}>
        <div style={{ marginBottom:48, display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
          <div>
            <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:26, color:C.text, letterSpacing:"0.04em" }}>{wlConfig?.app_name||"Kurb AI"}</div>
            <div style={{ fontSize:11, color:wlConfig?.primary_color||C.accent, marginTop:4, fontWeight:500 }}>{wlConfig?.tagline||"Marketing on Autopilot for Local Businesses"}</div>
          </div>
          {onCancel && (
            <button onClick={onCancel} style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:8, color:C.t2, fontSize:12, cursor:"pointer", fontFamily:"inherit", padding:"6px 12px", lineHeight:1, transition:"all 0.15s", flexShrink:0 }}
              onMouseOver={e=>{e.currentTarget.style.background=C.s2;e.currentTarget.style.color=C.text;}}
              onMouseOut={e=>{e.currentTarget.style.background="none";e.currentTarget.style.color=C.t2;}}>
              {editMode ? "✕ Cancel" : "← Back to Dashboard"}
            </button>
          )}
        </div>

        {/* Step indicators — in edit mode skip step 1 */}
        <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
          {stepLabels.filter((_, i) => !editMode || i > 0).map(([title, sub], i) => {
            const num    = editMode ? i + 2 : i + 1;
            const done   = step > num;
            const active = step === num;
            const isLast = editMode ? i === stepLabels.length - 2 : i === stepLabels.length - 1;
            return (
              <div key={num} style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
                  <div style={{ width:32, height:32, borderRadius:"50%", border:`2px solid ${done?C.accent:active?C.accent:C.border}`, background:done?C.accent:active?`${C.accent}18`:"transparent", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:done?"#fff":active?C.accent:C.t3, transition:"all 0.3s", flexShrink:0 }}>
                    {done ? "✓" : editMode ? i + 1 : num}
                  </div>
                  {!isLast && <div style={{ width:2, height:40, background:done?C.accent:C.border, margin:"4px 0", transition:"background 0.3s" }} />}
                </div>
                <div style={{ paddingTop:4, paddingBottom:!isLast?48:0 }}>
                  <div style={{ fontSize:13, fontWeight:active||done?600:400, color:active?C.text:done?C.t2:C.t3, transition:"color 0.2s" }}>{title}</div>
                  <div style={{ fontSize:11, color:C.t3, marginTop:2 }}>{sub}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop:"auto" }}>
          <div style={{ fontSize:12, color:C.t3, lineHeight:1.7 }}>
            {niche?.testimonial || '"Set it up once, it runs itself."'}
          </div>
          <div style={{ fontSize:11, color:C.t4, marginTop:8 }}>{niche?.testimonialBy || "— Local business owner"}</div>
        </div>
      </div>

      {/* Right form panel */}
      <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column" }}>
        <div style={{ flex:1, maxWidth:620, margin:"0 auto", width:"100%", padding:"64px 48px" }}>
          <div style={{ opacity:anim?0:1, transform:anim?`translateX(${dir*24}px)`:"translateX(0)", transition:"opacity 0.16s, transform 0.16s" }}>
            {renderStep()}
          </div>

          {duplicateErr && (
            <div style={{ marginTop:28, padding:"12px 16px", background:"#fef2f2", border:`1px solid ${C.danger}40`, borderRadius:8, color:C.danger, fontSize:13, lineHeight:1.5 }}>{duplicateErr}</div>
          )}
          {/* Navigation */}
          <div style={{ display:"flex", gap:12, marginTop:36, alignItems:"center" }}>
            {step > (editMode ? 2 : 1) && (
              <button style={{ ...S.btnGhost, padding:"11px 24px" }} onClick={() => go(step-1)}>← Back</button>
            )}
            {step < (editMode ? 4 : TOTAL) ? (
              <button style={{ ...S.btn, padding:"12px 40px", fontSize:15, opacity:canNext()?1:0.4 }}
                onClick={() => { if (canNext()) go(step+1); }}
                disabled={!canNext()}>
                Continue →
              </button>
            ) : (
              <button style={{ ...S.btn, padding:"13px 48px", fontSize:15, background:`linear-gradient(135deg,${C.accent},${C.accentD})`, boxShadow:`0 4px 24px ${C.accent}40` }}
                onClick={finish}>
                {editMode ? "Save Changes →" : "⚡ Launch My First Campaign →"}
              </button>
            )}
            {existingProfile?.name && (
              <button style={{ marginLeft:"auto", fontSize:12, color:C.t4, background:"none", border:"none", cursor:"pointer", fontFamily:"inherit" }}
                onClick={finish}>
                ← Back to Dashboard
              </button>
            )}
          </div>

          {/* Progress dots */}
          <div style={{ display:"flex", gap:8, justifyContent:"center", marginTop:32 }}>
            {Array.from({ length: editMode ? 3 : 4 }, (_, i) => i + (editMode ? 2 : 1)).map(n => (
              <div key={n} style={{ width:n===step?24:8, height:8, borderRadius:4, background:n===step?C.accent:C.border, transition:"all 0.3s" }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── APP SHELL ─────────────────────────────────────────────────────── */

function AcceptInviteWelcome({ invite, authUser, onComplete }) {
  const [name, setName]         = useState(authUser?.user_metadata?.full_name || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  const workspaceName = invite?.workspace_name || "the workspace";
  const role = invite?.role ? (invite.role.charAt(0).toUpperCase() + invite.role.slice(1)) : "Member";

  const handleComplete = async () => {
    if (password && password !== confirm) { setError("Passwords don't match."); return; }
    if (password && password.length < 8)  { setError("Password must be at least 8 characters."); return; }
    setSaving(true); setError("");
    try {
      if (password) await updatePassword(password);
      if (name.trim()) await upsertProfile({ display_name: name.trim(), email: authUser?.email });
      // Mark invite fully accepted — invalidates the invite_token link
      if (authUser?.email) {
        await fetch("/api/team/complete-invite", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: authUser.email }),
        }).catch(() => {}); // Non-fatal — don't block completion if this fails
      }
      onComplete();
    } catch(e) { setError(e.message); setSaving(false); }
  };

  return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ width:"100%", maxWidth:480, background:C.s1, borderRadius:16, padding:48, boxShadow:"0 4px 32px rgba(0,0,0,0.08)" }}>
        <div style={{ fontSize:32, marginBottom:16 }}>🎉</div>
        <h2 style={{ fontSize:24, fontWeight:700, color:C.text, margin:"0 0 8px" }}>You've been invited!</h2>
        <p style={{ fontSize:14, color:C.t2, margin:"0 0 32px", lineHeight:1.6 }}>
          You've joined <strong style={{ color:C.text }}>{workspaceName}</strong> as <strong style={{ color:C.text }}>{role}</strong>. Set up your account to get started.
        </p>

        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:"block", marginBottom:6 }}>Your Name</label>
            <input style={{ ...S.input, width:"100%", boxSizing:"border-box" }} placeholder="First Last"
              value={name} onChange={e => setName(e.target.value)} autoFocus />
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:"block", marginBottom:6 }}>Set a Password <span style={{ color:C.t3, fontWeight:400 }}>(recommended)</span></label>
            <input type="password" style={{ ...S.input, width:"100%", boxSizing:"border-box" }} placeholder="At least 8 characters"
              value={password} onChange={e => setPassword(e.target.value)} />
          </div>
          {password && (
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:"block", marginBottom:6 }}>Confirm Password</label>
              <input type="password" style={{ ...S.input, width:"100%", boxSizing:"border-box" }} placeholder="Repeat password"
                value={confirm} onChange={e => setConfirm(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleComplete()} />
            </div>
          )}
          {error && <div style={{ fontSize:13, color:C.danger }}>{error}</div>}
          <button style={{ ...S.btn, width:"100%", padding:"12px 0", fontSize:15, marginTop:8 }}
            disabled={saving} onClick={handleComplete}>
            {saving ? "Setting up…" : "Go to Dashboard →"}
          </button>
          <button style={{ ...S.btnGhost, width:"100%", fontSize:13, color:C.t3 }}
            onClick={onComplete}>
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}

function AuthScreen({ wlConfig }) {
  const isMobile = useIsMobile();
  const [view,     setView]     = useState("signup");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  const go = (v) => { setView(v); setError(""); };

  const submit = async () => {
    setError("");
    setLoading(true);
    try {
      if (view === "signup") {
        if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
        if (password !== confirm) { setError("Passwords don't match"); return; }
        const r = await fetch("/api/auth/signup", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password, redirectTo: window.location.origin }),
        });
        const d = await r.json();
        if (!r.ok) { setError(d.error || "Signup failed"); return; }
        go("check-email");
      } else if (view === "signin") {
        const { error: err } = await signIn(email, password);
        if (err) { setError(err.message); return; }
        // onAuthStateChange in App() handles the rest
      } else if (view === "forgot") {
        const { error: err } = await resetPassword(email);
        if (err) { setError(err.message); return; }
        go("check-email");
      } else if (view === "magic") {
        const { error: err } = await signInWithEmail(email);
        if (err) { setError(err.message); return; }
        go("check-email");
      }
    } finally {
      setLoading(false);
    }
  };

  const AGENT_COPY = {
    "Content Agent": {
      headline: "Get customers from\nGoogle & ChatGPT.\nWhile you sleep.",
      features: [
        "SEO articles written and published automatically",
        "Ranks on Google and shows up in AI answers",
        "Publishes to WordPress, Wix, or Squarespace",
        "New keywords discovered and scheduled weekly",
        "Set it up once — it runs itself forever",
      ],
    },
  };
  const agentCopy = wlConfig?.app_name ? AGENT_COPY[wlConfig.app_name] : null;
  const FEATURES = agentCopy?.features || [
    "Multi-channel campaign automation",
    "AI content for every platform",
    "Google Business management",
    "CRM, reservations & reviews",
    "Email & SMS marketing",
  ];

  const TITLES = {
    signup:       ["Create your account",    "Start your 14-day free trial — no credit card required."],
    signin:       ["Welcome back",           `Sign in to your ${wlConfig?.app_name||"Kurb AI"} account.`],
    forgot:       ["Reset your password",    "We'll email you a reset link."],
    magic:        ["Magic link sign-in",     "We'll email you a one-click sign-in link."],
    "check-email":["Check your inbox",       ""],
  };
  const [title, subtitle] = TITLES[view] || TITLES.signup;

  return (
    <div style={{ position:"fixed", inset:0, background:C.bg, zIndex:1000,
      display:"flex", flexDirection: isMobile ? "column" : "row",
      alignItems:"stretch", fontFamily:"'Inter',-apple-system,sans-serif", overflowY: isMobile ? "auto" : "hidden" }}>

      {/* Left brand panel — hidden on mobile, replaced by compact header */}
      {!isMobile && (
        <div style={{ width:360, background:C.s1, borderRight:`1px solid ${C.border}`,
          display:"flex", flexDirection:"column", padding:"48px 40px", flexShrink:0, overflowY:"auto" }}>
          <div style={{ marginBottom:48 }}>
            {wlConfig?.logo_url
              ? <img src={wlConfig.logo_url} alt={wlConfig.app_name||"Logo"} style={{ height:40, maxWidth:160, objectFit:"contain", marginBottom:8 }} />
              : <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:26, color:C.text, letterSpacing:"0.04em" }}>{wlConfig?.app_name||"Kurb AI"}</div>
            }
            <div style={{ fontSize:11, color:wlConfig?.primary_color||C.accent, marginTop:4, fontWeight:500 }}>{wlConfig?.tagline||"Marketing on Autopilot for Local Businesses"}</div>
          </div>
          <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:28, color:C.text, fontWeight:300, lineHeight:1.3, marginBottom:32 }}>
            {agentCopy
              ? agentCopy.headline.split("\n").map((line, i, arr) => <React.Fragment key={i}>{line}{i < arr.length - 1 && <br />}</React.Fragment>)
              : <React.Fragment>One account.<br />Every channel.<br />On autopilot.</React.Fragment>}
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:14, marginBottom:"auto" }}>
            {FEATURES.map(f => (
              <div key={f} style={{ display:"flex", gap:10, alignItems:"center" }}>
                <span style={{ color:C.accent, fontSize:13, flexShrink:0 }}>✓</span>
                <span style={{ fontSize:13, color:C.t2 }}>{f}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop:48 }}>
            <div style={{ fontSize:12, color:C.t3, lineHeight:1.7, fontStyle:"italic" }}>
              "Set it up once, it runs itself. Best marketing decision I've made."
            </div>
            <div style={{ fontSize:11, color:C.t4, marginTop:8 }}>— Restaurant owner, Atlanta GA</div>
          </div>
        </div>
      )}

      {/* Mobile compact header */}
      {isMobile && (
        <div style={{ background:C.s1, borderBottom:`1px solid ${C.border}`, padding:"24px 24px 20px" }}>
          <div style={{ marginBottom:6 }}>
            {wlConfig?.logo_url
              ? <img src={wlConfig.logo_url} alt={wlConfig.app_name||"Logo"} style={{ height:32, maxWidth:140, objectFit:"contain" }} />
              : <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:22, color:C.text, fontWeight:600 }}>{wlConfig?.app_name||"Kurb AI"}</div>
            }
          </div>
          <div style={{ fontSize:11, color:wlConfig?.primary_color||C.accent, fontWeight:500 }}>{wlConfig?.tagline||"Marketing on Autopilot for Local Businesses"}</div>
        </div>
      )}

      {/* Form panel */}
      <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center",
        overflowY: isMobile ? "visible" : "auto",
        padding: isMobile ? "32px 24px 48px" : "48px" }}>
        <div style={{ width:"100%", maxWidth:420 }}>

          {view === "check-email" ? (
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:52, marginBottom:20 }}>✉</div>
              <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:30, color:C.text, marginBottom:10 }}>Check your inbox</div>
              <div style={{ fontSize:14, color:C.t2, lineHeight:1.7, marginBottom:28 }}>
                We sent a link to <strong style={{color:C.text}}>{email}</strong>.<br />Click it to continue.
              </div>
              <button style={{ ...S.btnGhost, width:"100%" }} onClick={() => go("signin")}>← Back to sign in</button>
            </div>
          ) : (
            <>
              <div style={{ marginBottom:32 }}>
                <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:32, color:C.text, fontWeight:300, marginBottom:6 }}>{title}</div>
                {subtitle && <div style={{ fontSize:14, color:C.t2 }}>{subtitle}</div>}
              </div>

              {/* Sign up / Sign in tab toggle */}
              {(view === "signup" || view === "signin") && (
                <div style={{ display:"flex", background:C.s2, borderRadius:10, padding:4, gap:3, border:`1px solid ${C.border}`, marginBottom:28 }}>
                  {[["signup","Sign up"],["signin","Sign in"]].map(([v,l]) => (
                    <button key={v} onClick={() => go(v)}
                      style={{ flex:1, padding:"9px", borderRadius:7, border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:13, fontWeight:600, background:view===v?C.s3:"transparent", color:view===v?C.text:C.t2, transition:"all 0.15s" }}>
                      {l}
                    </button>
                  ))}
                </div>
              )}

              {/* Google OAuth */}
              {(view === "signup" || view === "signin") && (
                <>
                  <button onClick={async () => { const { error: err } = await signInWithGoogle(); if (err) setError(err.message || "Google sign-in failed — check Supabase provider settings."); }}
                    style={{ width:"100%", padding:"11px 16px", borderRadius:8, border:`1px solid ${C.border}`, background:C.s2, color:C.text, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:10, marginBottom:18, transition:"all 0.15s" }}
                    onMouseOver={e=>{e.currentTarget.style.borderColor=`${C.accent}60`;e.currentTarget.style.background=C.s3;}}
                    onMouseOut={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.background=C.s2;}}>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
                      <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
                      <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
                      <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
                    </svg>
                    Continue with Google
                  </button>
                  <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18 }}>
                    <div style={{ flex:1, height:1, background:C.border }} />
                    <span style={{ fontSize:12, color:C.t3 }}>or continue with email</span>
                    <div style={{ flex:1, height:1, background:C.border }} />
                  </div>
                </>
              )}

              {/* Form fields */}
              <div style={{ display:"flex", flexDirection:"column", gap:14, marginBottom:error?12:20 }}>
                <div>
                  <label style={S.label}>Email</label>
                  <input type="email" style={S.input} value={email}
                    onChange={e => setEmail(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && submit()}
                    placeholder="you@example.com" autoFocus />
                </div>
                {(view === "signup" || view === "signin") && (
                  <div>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:7 }}>
                      <label style={{ ...S.label, marginBottom:0 }}>Password</label>
                      {view === "signin" && (
                        <button onClick={() => go("forgot")} style={{ fontSize:11, color:C.accent, background:"none", border:"none", cursor:"pointer", fontFamily:"inherit" }}>
                          Forgot password?
                        </button>
                      )}
                    </div>
                    <input type="password" style={S.input} value={password}
                      onChange={e => setPassword(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && submit()}
                      placeholder={view === "signup" ? "At least 6 characters" : "Your password"} />
                  </div>
                )}
                {view === "signup" && (
                  <div>
                    <label style={S.label}>Confirm Password</label>
                    <input type="password" style={S.input} value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && submit()}
                      placeholder="Repeat your password" />
                  </div>
                )}
              </div>

              {error && <div style={{ color:C.danger, fontSize:12, marginBottom:16, padding:"9px 12px", background:`${C.danger}10`, borderRadius:6, border:`1px solid ${C.danger}30` }}>{error}</div>}

              <button style={{ ...S.btn, width:"100%", fontSize:14, padding:"13px", opacity:loading?0.6:1 }}
                onClick={submit} disabled={loading}>
                {loading ? "Please wait…"
                  : view === "signup"  ? "Create Account →"
                  : view === "signin"  ? "Sign In →"
                  : view === "forgot"  ? "Send Reset Email →"
                  : "Send Magic Link →"}
              </button>

              {/* Secondary options */}
              {(view === "signup" || view === "signin") && (
                <button onClick={() => go("magic")}
                  style={{ width:"100%", marginTop:14, padding:"8px", background:"transparent", border:"none", color:C.t3, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>
                  Sign in without a password →
                </button>
              )}
              {(view === "forgot" || view === "magic") && (
                <button onClick={() => go("signin")}
                  style={{ width:"100%", marginTop:14, padding:"8px", background:"transparent", border:"none", color:C.t3, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>
                  ← Back to sign in
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PasswordResetModal({ onClose }) {
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [done,     setDone]     = useState(false);

  const submit = async () => {
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm)  { setError("Passwords don't match"); return; }
    setLoading(true);
    const { error: err } = await updatePassword(password);
    setLoading(false);
    if (err) { setError(err.message); return; }
    setDone(true);
    setTimeout(onClose, 1800);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ ...S.card, width:420, padding:36, background:C.s1 }}>
        {done ? (
          <div style={{ textAlign:"center", padding:"12px 0" }}>
            <div style={{ fontSize:44, marginBottom:12 }}>✓</div>
            <div style={{ fontSize:16, fontWeight:600, color:C.success }}>Password updated!</div>
          </div>
        ) : (
          <>
            <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:26, color:C.text, marginBottom:6 }}>Set new password</div>
            <div style={{ fontSize:13, color:C.t2, marginBottom:24 }}>Choose a new password for your account.</div>
            <Field label="New Password">
              <input type="password" style={S.input} value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 6 characters" autoFocus />
            </Field>
            <Field label="Confirm Password">
              <input type="password" style={S.input} value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password" onKeyDown={e => e.key === "Enter" && submit()} />
            </Field>
            {error && <div style={{ color:C.danger, fontSize:12, marginBottom:14 }}>{error}</div>}
            <button style={{ ...S.btn, width:"100%", opacity:loading?0.6:1 }} onClick={submit} disabled={loading}>
              {loading ? "Saving…" : "Update Password →"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── ResearchStrategy → src/modules/research.jsx (lazy-loaded) ──────────────
// ── Integrations ──────────────────────────────────────────────────────────────
// SOCIAL_CHAR_LIMITS, SOCIAL_IMAGE_LIMITS, SOCIAL_PLATFORMS, INTEGRATION_PLATFORMS
// moved to ./shared/constants.js (shared by SocialMedia + Integrations).

/* ─── MEDIA PICKER MODAL ────────────────────────────────────────────── */
// MediaPickerModal imported from ./shared/components.jsx
// MediaLibrary moved to ./modules/media.jsx (lazy-loaded)
function Integrations() {
  const isMobile = useIsMobile();
  const [configs, setConfigs]         = useState({});
  const [editing, setEditing]         = useState(null);
  const [testing, setTesting]         = useState(false);
  const [testResult, setTestResult]   = useState(null);
  const [form, setForm]               = useState({});
  // Social connections
  const [socialConns, setSocialConns]   = useState({});
  const [socialModal, setSocialModal]   = useState(null); // platform id
  const [socialHandle, setSocialHandle] = useState("");
  const [confirmDisconnect, setConfirmDisconnect] = useState(null);

  useEffect(() => {
    store.get("integrations").then(d => { if (d) setConfigs(d); });
    store.get("social-connections").then(d => { if (d) setSocialConns(d); });
  }, []);

  const connectSocial = async (platformId) => {
    const updated = { ...socialConns, [platformId]: { handle: socialHandle.trim(), connectedAt: new Date().toISOString() } };
    setSocialConns(updated);
    await store.set("social-connections", updated);
    setSocialModal(null); setSocialHandle("");
  };

  const disconnectSocial = async (platformId) => {
    const updated = { ...socialConns };
    delete updated[platformId];
    setSocialConns(updated);
    await store.set("social-connections", updated);
    setConfirmDisconnect(null);
  };

  const saveConfig = async (platformId, data) => {
    const updated = { ...configs, [platformId]: { ...data, savedAt: new Date().toISOString() } };
    setConfigs(updated);
    await store.set("integrations", updated);
  };

  const disconnect = async (platformId) => {
    const updated = { ...configs };
    delete updated[platformId];
    setConfigs(updated);
    await store.set("integrations", updated);
    if (editing === platformId) setEditing(null);
  };

  const openEdit = (id) => {
    setEditing(id);
    setForm(configs[id] || {});
    setTestResult(null);
  };

  const testWordPress = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await fetch("/api/integrations/wordpress/test", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ siteUrl: form.siteUrl, username: form.username, appPassword: form.appPassword }),
      });
      const data = await r.json();
      setTestResult(data.success ? { ok: true, msg: `Connected as ${data.user}` } : { ok: false, msg: data.error });
    } catch (e) { setTestResult({ ok: false, msg: e.message }); }
    setTesting(false);
  };

  const autoConnectWordPress = async () => {
    setTesting(true); setTestResult(null);
    try {
      const ws = window.__activeWorkspaceId;
      const r = await fetch("/api/integrations/wordpress/auto-connect", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspaceId: ws, siteUrl: form.siteUrl?.trim(), username: form.username?.trim(), password: form.wpPassword }),
      });
      const data = await r.json();
      if (!r.ok) { setTestResult({ ok: false, msg: data.error }); setTesting(false); return; }
      const fresh = await store.get("integrations") || {};
      setConfigs(fresh);
      setForm(f => ({ ...f, wpPassword: "" }));
      setEditing(null);
      setTestResult({ ok: true, msg: `Connected as ${data.user || form.username}` });
    } catch (e) { setTestResult({ ok: false, msg: e.message }); }
    setTesting(false);
  };

  const CF = { col:C.s1, border:`1px solid ${C.border}` };

  return (
    <div>
      <div style={{ marginBottom:28 }}>
        <h2 style={S.h2}>Integrations</h2>
        <p style={{ ...S.sub, marginBottom:0 }}>Connect your social accounts and publishing platforms to Kurb AI.</p>
      </div>

      {/* ── Social Media Connections ── */}
      <div style={{ marginBottom:40 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:C.text }}>Social Media Connections</div>
            <div style={{ fontSize:13, color:C.t3, marginTop:2 }}>
              Connect your accounts so Kurb AI can tailor posts for each platform and open them ready to publish.
            </div>
          </div>
          <div style={{ fontSize:12, color:C.t3 }}>
            {Object.keys(socialConns).length} of {SOCIAL_PLATFORMS.length} connected
          </div>
        </div>

        {/* Platform table */}
        <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
          {/* Header */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 120px 140px 120px", gap:0,
            padding:"9px 16px", background:C.s2, borderBottom:`1px solid ${C.border}` }}>
            {["Platform","Status","Account",""].map((h,i) => (
              <div key={i} style={{ fontSize:11, fontWeight:700, color:C.t2, textTransform:"uppercase", letterSpacing:"0.05em", textAlign: i===3?"right":"left" }}>{h}</div>
            ))}
          </div>
          {SOCIAL_PLATFORMS.map((p, i) => {
            const conn = socialConns[p.id];
            const isLast = i === SOCIAL_PLATFORMS.length - 1;
            return (
              <div key={p.id} style={{ display:"grid", gridTemplateColumns:"1fr 120px 140px 120px",
                gap:0, padding:"12px 16px", borderBottom: isLast?"none":`1px solid ${C.border}`,
                alignItems:"center", background: conn ? `${p.color}04` : C.s1 }}>
                {/* Platform */}
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ width:34, height:34, borderRadius:8, background:`${p.color}15`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:13, fontWeight:900, color:p.color, flexShrink:0, fontFamily:"serif" }}>
                    {p.icon}
                  </div>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{p.name}</div>
                    <div style={{ fontSize:11, color:C.t3 }}>{p.desc}</div>
                  </div>
                </div>
                {/* Status */}
                <div>
                  {conn
                    ? <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:10, background:`${C.success}15`, color:C.success }}>● Connected</span>
                    : <span style={{ fontSize:11, color:C.t4 }}>Not connected</span>}
                </div>
                {/* Account handle */}
                <div style={{ fontSize:12, color:C.t2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {conn?.handle || "—"}
                </div>
                {/* Actions */}
                <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
                  {conn ? (
                    confirmDisconnect === p.id ? (
                      <>
                        <button onMouseDown={e=>e.preventDefault()} onClick={() => disconnectSocial(p.id)}
                          style={{ ...S.btnGhost, fontSize:11, padding:"3px 10px", color:C.danger, borderColor:`${C.danger}40` }}>Yes</button>
                        <button onMouseDown={e=>e.preventDefault()} onClick={() => setConfirmDisconnect(null)}
                          style={{ ...S.btnGhost, fontSize:11, padding:"3px 10px" }}>No</button>
                      </>
                    ) : (
                      <button onMouseDown={e=>e.preventDefault()} onClick={() => setConfirmDisconnect(p.id)}
                        style={{ ...S.btnGhost, fontSize:11, padding:"4px 12px", color:C.danger, borderColor:`${C.danger}30` }}>
                        Disconnect
                      </button>
                    )
                  ) : (
                    <button onMouseDown={e=>e.preventDefault()} onClick={() => { setSocialModal(p.id); setSocialHandle(""); }}
                      style={{ ...S.btnGhost, fontSize:11, padding:"4px 12px", color:p.color, borderColor:`${p.color}40` }}>
                      Connect
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ fontSize:11, color:C.t3, marginTop:10 }}>
          ✦ Direct publishing requires OAuth — coming soon. For now, Kurb AI generates platform-optimized content and opens each platform for you to paste and publish.
        </div>
      </div>

      {/* ── Connect social modal ── */}
      {socialModal && (() => {
        const p = SOCIAL_PLATFORMS.find(x => x.id === socialModal);
        if (!p) return null;
        return (
          <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:3000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
            onClick={() => setSocialModal(null)}>
            <div style={{ background:C.s1, borderRadius:14, width:"100%", maxWidth:420, padding:28, boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}
              onClick={e => e.stopPropagation()}>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
                <div style={{ width:42, height:42, borderRadius:10, background:`${p.color}15`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:900, color:p.color }}>
                  {p.icon}
                </div>
                <div>
                  <div style={{ fontSize:15, fontWeight:700, color:C.text }}>Connect {p.name}</div>
                  <div style={{ fontSize:12, color:C.t3 }}>Enter your account name for personalisation</div>
                </div>
                <button onMouseDown={e=>e.preventDefault()} onClick={() => setSocialModal(null)}
                  style={{ marginLeft:"auto", background:"none", border:"none", fontSize:20, color:C.t3, cursor:"pointer" }}>×</button>
              </div>
              <div style={{ marginBottom:16 }}>
                <label style={{ fontSize:12, fontWeight:600, color:C.t2, display:"block", marginBottom:6 }}>
                  {p.id === "gbp" ? "Business name" : `${p.name} page or handle`}
                </label>
                <input autoFocus value={socialHandle} onChange={e => setSocialHandle(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && socialHandle.trim() && connectSocial(p.id)}
                  placeholder={p.id === "gbp" ? "Kitchen Master" : p.id === "youtube" ? "Kitchen Master Restaurant" : "@kitchenmaster"}
                  style={S.input} />
              </div>
              <div style={{ fontSize:12, color:C.t3, marginBottom:20, padding:"10px 14px", background:C.s2, borderRadius:8, lineHeight:1.6 }}>
                ✦ Kurb AI will use this to personalise post previews and open {p.name} when you're ready to publish.
                Direct publishing requires OAuth — coming in a future update.
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={() => setSocialModal(null)} style={{ ...S.btnGhost, flex:1, padding:"9px" }}>Cancel</button>
                <button onClick={() => connectSocial(p.id)} disabled={!socialHandle.trim()}
                  style={{ ...S.btn, flex:2, padding:"9px", background:p.color }}>
                  Connect {p.name}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Website & CMS Platforms ── */}
      <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:16 }}>Website & CMS Platforms</div>
      {/* Platform cards grid */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px,1fr))", gap:16, marginBottom:32 }}>
        {INTEGRATION_PLATFORMS.map(p => {
          const cfg       = configs[p.id];
          const connected = !!cfg;
          return (
            <div key={p.id} style={{ ...S.card, padding:20, opacity:p.live?1:0.65 }}>
              <div style={{ display:"flex", alignItems:"flex-start", gap:14, marginBottom:12 }}>
                <div style={{ width:42, height:42, borderRadius:10, background:`${p.color}20`, border:`1px solid ${p.color}40`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, fontWeight:900, color:p.color, flexShrink:0 }}>{p.icon}</div>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{p.name}</div>
                    {connected && <span style={{ fontSize:10, fontWeight:700, background:`${C.success}18`, color:C.success, borderRadius:10, padding:"1px 7px" }}>● Connected</span>}
                    {!p.live && <span style={{ fontSize:10, color:C.t3, background:C.s2, borderRadius:10, padding:"1px 7px" }}>Coming Soon</span>}
                  </div>
                  <div style={{ fontSize:12, color:C.t3, marginTop:3, lineHeight:1.5 }}>{p.desc}</div>
                </div>
              </div>
              {p.live && (
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={() => openEdit(p.id)}
                    style={{ ...S.btnGhost, flex:1, padding:"7px 0", fontSize:12, borderColor: connected?C.border:`${p.color}50`, color: connected?C.t2:p.color }}>
                    {connected ? "⚙ Settings" : "Connect"}
                  </button>
                  {connected && (
                    <button onClick={() => disconnect(p.id)}
                      style={{ ...S.btnGhost, padding:"7px 12px", fontSize:12, borderColor:`${C.danger}40`, color:C.danger }}>
                      Disconnect
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Config panels */}
      {editing === "wordpress" && (
        <div style={{ ...S.card, padding:24, marginBottom:24 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
            <div style={{ fontSize:15, fontWeight:700, color:C.text }}>WordPress Configuration</div>
            <button onClick={() => setEditing(null)} style={{ marginLeft:"auto", background:"none", border:"none", color:C.t3, cursor:"pointer", fontSize:18 }}>✕</button>
          </div>
          {/* Step-by-step guide */}
          <div style={{ background:C.s2, borderRadius:8, padding:"12px 14px", marginBottom:16, fontSize:12, color:C.t2, lineHeight:1.7 }}>
            <div style={{ fontWeight:700, color:C.text, marginBottom:8, fontSize:13 }}>How to connect in 3 steps:</div>
            {[
              ["1","Enter your site URL and username below, then click Open WP Profile to go directly to the right page."],
              ["2","Scroll to Application Passwords. Type Content Agent in the name box, click Add New Application Password."],
              ["3","Copy the password WordPress shows you and paste it in the Application Password field below."],
            ].map(([n,text]) => (
              <div key={n} style={{ display:"flex", gap:10, marginBottom:6 }}>
                <div style={{ width:20, height:20, borderRadius:"50%", background:"#6366f1", color:"#fff", fontSize:11, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1 }}>{n}</div>
                <div>{text}</div>
              </div>
            ))}
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:14, marginBottom:14 }}>
            <div>
              <label style={S.label}>WordPress Site URL</label>
              <input style={S.input} value={form.siteUrl||""} onChange={e=>setForm(f=>({...f,siteUrl:e.target.value}))} placeholder="https://yoursite.com" />
            </div>
            <div>
              <label style={S.label}>Username</label>
              <div style={{ display:"flex", gap:8 }}>
                <input style={{ ...S.input, flex:1 }} value={form.username||""} onChange={e=>setForm(f=>({...f,username:e.target.value}))} placeholder="admin" autoComplete="username" />
                {form.siteUrl && (
                  <a href={`${(form.siteUrl||"").replace(/\/$/,"")}/wp-admin/profile.php`} target="_blank" rel="noreferrer"
                    style={{ ...S.btnGhost, fontSize:12, padding:"7px 12px", whiteSpace:"nowrap", textDecoration:"none", color:"#6366f1", borderColor:"#6366f1" }}>
                    Open WP Profile →
                  </a>
                )}
              </div>
            </div>
            <div>
              <label style={S.label}>Application Password</label>
              <input style={S.input} type="password" value={form.appPassword||""} onChange={e=>setForm(f=>({...f,appPassword:e.target.value}))} placeholder="xxxx xxxx xxxx xxxx xxxx xxxx" />
              <div style={{ fontSize:11, color:C.t3, marginTop:4 }}>Paste the password WordPress generated — not your regular login password.</div>
            </div>
          </div>
          {testResult && (
            <div style={{ padding:"10px 14px", borderRadius:8, background:testResult.ok?`${C.success}12`:`${C.danger}12`, border:`1px solid ${testResult.ok?C.success:C.danger}40`, fontSize:12, color:testResult.ok?C.success:C.danger, marginBottom:14 }}>
              {testResult.ok ? "✓" : "✕"} {testResult.msg}
            </div>
          )}
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={testWordPress} disabled={testing||!form.siteUrl||!form.username||!form.appPassword}
              style={{ ...S.btnGhost, padding:"9px 20px", fontSize:13, opacity:testing?0.7:1 }}>
              {testing ? "Testing…" : "Test Connection"}
            </button>
            <button onClick={() => { saveConfig("wordpress", form); setEditing(null); setTestResult(null); }}
              disabled={!form.siteUrl||!form.username||!form.appPassword}
              style={{ ...S.btn, padding:"9px 24px", fontSize:13 }}>
              Save
            </button>
          </div>
        </div>
      )}

      {editing === "webhook" && (
        <div style={{ ...S.card, padding:24, marginBottom:24 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
            <div style={{ fontSize:15, fontWeight:700, color:C.text }}>Webhook Configuration</div>
            <button onClick={() => setEditing(null)} style={{ marginLeft:"auto", background:"none", border:"none", color:C.t3, cursor:"pointer", fontSize:18 }}>✕</button>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:14, marginBottom:14 }}>
            <div>
              <label style={S.label}>Webhook URL</label>
              <input style={S.input} value={form.url||""} onChange={e=>setForm(f=>({...f,url:e.target.value}))} placeholder="https://hooks.zapier.com/hooks/catch/..." />
              <div style={{ fontSize:11, color:C.t3, marginTop:4 }}>Works with Zapier, Make (Integromat), n8n, or any HTTP endpoint</div>
            </div>
            <div>
              <label style={S.label}>Secret Key (optional)</label>
              <input style={S.input} value={form.secret||""} onChange={e=>setForm(f=>({...f,secret:e.target.value}))} placeholder="Optional — sent as X-Webhook-Secret header" />
            </div>
          </div>
          <div style={{ ...S.card, padding:14, marginBottom:16, background:C.s2 }}>
            <div style={{ fontSize:11, color:C.t3, fontWeight:700, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.06em" }}>Payload sent on publish:</div>
            <pre style={{ fontSize:11, color:C.t2, margin:0, lineHeight:1.6 }}>{`{
  "title": "Content title",
  "content": "Full HTML content",
  "type": "blog",
  "business": "Kitchen Master",
  "publishedAt": "2026-04-30T..."
}`}</pre>
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={() => { saveConfig("webhook", form); setEditing(null); }}
              disabled={!form.url}
              style={{ ...S.btn, padding:"9px 24px", fontSize:13 }}>
              Save
            </button>
          </div>
        </div>
      )}

      {/* How to use */}
      {Object.keys(configs).length > 0 && (
        <div style={{ ...S.card, padding:20, borderColor:`${C.accent}30`, background:`${C.accent}05` }}>
          <div style={{ fontSize:13, fontWeight:700, color:C.accent, marginBottom:8 }}>✦ Publishing from Content Library</div>
          <div style={{ fontSize:13, color:C.t2, lineHeight:1.6 }}>
            Go to <strong style={{ color:C.text }}>Content → Library</strong>, open any saved piece, and click <strong style={{ color:C.text }}>Publish →</strong> to push it directly to your connected platforms. You can set it as draft, publish immediately, or schedule a date.
          </div>
        </div>
      )}
    </div>
  );
}


// ── ChangeLog ─────────────────────────────────────────────────────────────────

// IMAGE_PROVIDERS, IMAGE_PROVIDER_LABEL imported from ./shared/components.jsx


const RULE_TYPES = [
  { id:"keyword_research",    icon:"⊹", label:"Keyword Research",       desc:"Find top profit keywords via DataForSEO" },
  { id:"seo_audit",           icon:"◑", label:"SEO Site Audit",          desc:"Full OnPage audit via DataForSEO" },
  { id:"competitor_analysis", icon:"◈", label:"Competitor Analysis",     desc:"Map top local competitors via SERP" },
  { id:"content_ideas",       icon:"✦", label:"Generate Content Ideas",  desc:"AI-generated content ideas based on keywords" },
  { id:"draft_campaign",      icon:"✉", label:"Draft Email Campaign",    desc:"AI writes a welcome email campaign draft" },
];

const RULE_DESTINATIONS = {
  keyword_research:    { mod:"research", tab:"history",     label:"View in Research" },
  content_ideas:       { mod:"content",  tab:"write",       label:"View Content Queue" },
  seo_audit:           { mod:"research", tab:"audit",       label:"View Audit" },
  competitor_analysis: { mod:"research", tab:"competitors", label:"View Competitors" },
  draft_campaign:      { mod:"marketing",tab:null,          label:"View Campaigns" },
};

function Autopilot({ profile, onNavigate }) {
  const [status, setStatus]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [polling, setPolling]   = useState(false);
  const [plan, setPlan]         = useState("growth");
  // Custom rules per plan — starts from PLAYBOOKS defaults
  const [customRules, setCustomRules] = useState(() =>
    Object.fromEntries(Object.entries(PLAYBOOKS).map(([k,v]) => [k, v.rules.map(r => ({...r}))]))
  );
  const [elapsed, setElapsed]     = useState(0);
  const elapsedRef                = useRef(null);
  const [editingId, setEditingId] = useState(null); // rule id being edited
  const [editLabel, setEditLabel] = useState("");
  const [addingRule, setAddingRule] = useState(false);
  const [newRuleType, setNewRuleType] = useState(RULE_TYPES[0].id);
  const [newRuleLabel, setNewRuleLabel] = useState("");
  const pollingRef = useRef(null);

  useEffect(() => {
    store.get("autopilot-playbook-status").then(d => { if (d) setStatus(d); });
    store.get("autopilot-custom-rules").then(d => { if (d) setCustomRules(d); });
    return () => clearInterval(pollingRef.current);
  }, []);

  const saveRules = async (updated) => {
    setCustomRules(updated);
    await store.set("autopilot-custom-rules", updated);
  };

  const deleteRule = async (ruleId) => {
    const updated = { ...customRules, [plan]: customRules[plan].filter(r => r.id !== ruleId) };
    await saveRules(updated);
  };

  const startEdit = (r) => { setEditingId(r.id); setEditLabel(r.label); };

  const saveEdit = async () => {
    const updated = { ...customRules, [plan]: customRules[plan].map(r => r.id === editingId ? { ...r, label: editLabel } : r) };
    await saveRules(updated);
    setEditingId(null);
  };

  const addRule = async () => {
    const rt = RULE_TYPES.find(t => t.id === newRuleType);
    const rule = { id: `${newRuleType}-${Date.now()}`, type: newRuleType, icon: rt.icon, label: newRuleLabel || rt.label, config: {} };
    const updated = { ...customRules, [plan]: [...customRules[plan], rule] };
    await saveRules(updated);
    setAddingRule(false); setNewRuleLabel(""); setNewRuleType(RULE_TYPES[0].id);
  };

  const moveRule = async (idx, dir) => {
    const rules = [...customRules[plan]];
    const to = idx + dir;
    if (to < 0 || to >= rules.length) return;
    [rules[idx], rules[to]] = [rules[to], rules[idx]];
    await saveRules({ ...customRules, [plan]: rules });
  };

  // Store wid at trigger time, reuse in polling to avoid cache/timing issues
  const widRef = useRef(null);

  const startPolling = (wid) => {
    widRef.current = wid;
    clearInterval(pollingRef.current); setPolling(true);
    setElapsed(0); clearInterval(elapsedRef.current);
    elapsedRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await fetch("/api/autopilot/playbook-status", {
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({ workspaceId: widRef.current }),
        });
        if (!res.ok) return;
        const d = await res.json();
        if (d.status) {
          setStatus(d.status);
          store.set("autopilot-playbook-status", d.status);
          if (d.status.status === "completed" || d.status.status === "completed_with_errors") {
            clearInterval(pollingRef.current); clearInterval(elapsedRef.current); setPolling(false);
          }
        }
      } catch {}
    }, 2000);
  };

  const triggerPlaybook = async () => {
    setLoading(true);
    const wid = await getWorkspaceId();
    if (!wid) { setLoading(false); alert("Could not get workspace ID — please refresh and try again."); return; }
    const rules = customRules[plan];
    const initialStatus = { plan, triggeredAt: new Date().toISOString(), status: "running",
      rules: rules.map((r, i) => ({ ...r, status: i === 0 ? "running" : "pending" })) };
    setStatus(initialStatus);
    const resp = await fetch("/api/autopilot/trigger-playbook", {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ workspaceId: wid, plan, rules }),
    });
    if (!resp.ok) { setLoading(false); alert("Failed to start playbook — check server is running."); return; }
    setLoading(false);
    startPolling(wid);
    await logActivity("research", `Autopilot playbook started: ${PLAYBOOKS[plan].name}`, { plan });
  };

  const resetToDefault = async () => {
    const updated = { ...customRules, [plan]: PLAYBOOKS[plan].rules.map(r => ({...r})) };
    await saveRules(updated);
  };

  const statusIcon = (s) => {
    if (s === "completed") return <span style={{ color:C.success }}>✓</span>;
    if (s === "running")   return <div style={{ width:12, height:12, border:`2px solid ${C.accent}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />;
    if (s === "error")     return <span style={{ color:C.danger }}>✕</span>;
    return <span style={{ color:C.t4 }}>○</span>;
  };

  const pb = PLAYBOOKS[plan];
  const rules = customRules[plan] || [];

  return (
    <div>
      <div style={{ marginBottom:28 }}>
        <h2 style={S.h2}>Autopilot</h2>
        <p style={{ ...S.sub, marginBottom:0 }}>Configure onboarding playbooks per plan. Runs automatically when a new client completes setup.</p>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:20 }}>
        {/* Left: plan + editable rules */}
        <div style={{ ...S.card, padding:20 }}>
          <label style={S.label}>Plan</label>
          <div style={{ display:"flex", gap:8, marginBottom:20 }}>
            {Object.entries(PLAYBOOKS).map(([id, p]) => (
              <button key={id} onClick={() => { setPlan(id); setEditingId(null); setAddingRule(false); }}
                style={{ flex:1, padding:"8px 0", borderRadius:8, border:`2px solid ${plan===id?p.color:C.border}`, background:plan===id?`${p.color}18`:"transparent", color:plan===id?p.color:C.t2, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", transition:"all 0.1s" }}>
                {p.name}
              </button>
            ))}
          </div>

          {/* Rules list */}
          <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:14 }}>
            {rules.map((r, idx) => (
              <div key={r.id}>
                {editingId === r.id ? (
                  <div style={{ padding:"10px 12px", background:C.s2, borderRadius:8, border:`1px solid ${C.accent}50` }}>
                    <div style={{ fontSize:11, color:C.t3, marginBottom:6 }}>Edit label</div>
                    <input value={editLabel} onChange={e=>setEditLabel(e.target.value)}
                      style={{ ...S.input, fontSize:13, marginBottom:8 }}
                      onKeyDown={e=>{ if(e.key==="Enter") saveEdit(); if(e.key==="Escape") setEditingId(null); }}
                      autoFocus />
                    <div style={{ display:"flex", gap:6 }}>
                      <button onClick={saveEdit} style={{ ...S.btn, fontSize:11, padding:"4px 12px" }}>Save</button>
                      <button onClick={()=>setEditingId(null)} style={{ ...S.btnGhost, fontSize:11, padding:"4px 10px" }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 12px", background:C.s2, borderRadius:8, group:true }}>
                    {/* Reorder */}
                    <div style={{ display:"flex", flexDirection:"column", gap:1, flexShrink:0 }}>
                      <button onClick={()=>moveRule(idx,-1)} disabled={idx===0} style={{ background:"none", border:"none", color:idx===0?C.t4:C.t3, cursor:idx===0?"default":"pointer", fontSize:9, lineHeight:1, padding:"1px 3px" }}>▲</button>
                      <button onClick={()=>moveRule(idx,1)} disabled={idx===rules.length-1} style={{ background:"none", border:"none", color:idx===rules.length-1?C.t4:C.t3, cursor:idx===rules.length-1?"default":"pointer", fontSize:9, lineHeight:1, padding:"1px 3px" }}>▼</button>
                    </div>
                    <span style={{ fontSize:15, color:pb.color, width:18, textAlign:"center", flexShrink:0 }}>{r.icon}</span>
                    <span style={{ fontSize:13, color:C.text, flex:1 }}>{r.label}</span>
                    <span style={{ fontSize:10, color:C.t3, marginRight:4 }}>{RULE_TYPES.find(t=>t.id===r.type)?.label}</span>
                    <button onClick={()=>startEdit(r)} style={{ background:"none", border:"none", color:C.t3, cursor:"pointer", fontSize:13, padding:"0 4px" }}
                      onMouseOver={e=>e.currentTarget.style.color=C.accent} onMouseOut={e=>e.currentTarget.style.color=C.t3}>✎</button>
                    <button onClick={()=>deleteRule(r.id)} style={{ background:"none", border:"none", color:C.t4, cursor:"pointer", fontSize:15, padding:"0 4px" }}
                      onMouseOver={e=>e.currentTarget.style.color=C.danger} onMouseOut={e=>e.currentTarget.style.color=C.t4}>✕</button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add rule */}
          {addingRule ? (
            <div style={{ padding:"12px", background:C.s2, borderRadius:8, border:`1px solid ${C.border}`, marginBottom:14 }}>
              <div style={{ fontSize:11, color:C.t3, marginBottom:8 }}>Add New Rule</div>
              <div style={{ marginBottom:8 }}>
                <label style={S.label}>Rule Type</label>
                <select value={newRuleType} onChange={e=>{setNewRuleType(e.target.value); setNewRuleLabel(RULE_TYPES.find(t=>t.id===e.target.value)?.label||"");}} style={S.input}>
                  {RULE_TYPES.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label} — {t.desc}</option>)}
                </select>
              </div>
              <div style={{ marginBottom:10 }}>
                <label style={S.label}>Label (optional)</label>
                <input value={newRuleLabel} onChange={e=>setNewRuleLabel(e.target.value)} placeholder={RULE_TYPES.find(t=>t.id===newRuleType)?.label} style={S.input} />
              </div>
              <div style={{ display:"flex", gap:6 }}>
                <button onClick={addRule} style={{ ...S.btn, fontSize:12, padding:"6px 16px" }}>Add</button>
                <button onClick={()=>setAddingRule(false)} style={{ ...S.btnGhost, fontSize:12, padding:"6px 12px" }}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{ display:"flex", gap:8, marginBottom:16 }}>
              <button onClick={()=>{ setAddingRule(true); setNewRuleLabel(""); }} style={{ ...S.btnGhost, fontSize:12, padding:"7px 14px", flex:1 }}>+ Add Rule</button>
              <button onClick={resetToDefault} style={{ ...S.btnGhost, fontSize:12, padding:"7px 14px", color:C.t3 }}>↺ Reset</button>
            </div>
          )}

          <button onClick={triggerPlaybook} disabled={loading||polling||rules.length===0}
            style={{ ...S.btn, width:"100%", padding:"12px 0", background:pb.color, opacity:(loading||polling||rules.length===0)?0.6:1 }}>
            {loading?"Starting…":polling?"Running playbook…":"▶ Run Playbook Now"}
          </button>
        </div>

        {/* Right: status */}
        <div style={{ ...S.card, padding:20 }}>
          <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:16 }}>Run Status</div>
          {!status ? (
            <div style={{ textAlign:"center", padding:"40px 0", color:C.t3, fontSize:13 }}>
              <div style={{ fontSize:28, marginBottom:8 }}>⚡</div>
              No playbook has been run yet.<br />Select a plan and click Run.
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
                <span style={{ fontSize:11, padding:"3px 10px", borderRadius:10, fontWeight:700,
                  background:status.status==="completed"?`${C.success}18`:status.status==="running"?`${C.accent}18`:`${C.danger}18`,
                  color:status.status==="completed"?C.success:status.status==="running"?C.accent:C.danger }}>
                  {status.status==="running" ? "RUNNING" : status.status?.replace(/_/g," ").toUpperCase()}
                </span>
                <span style={{ fontSize:11, color:C.t3 }}>{PLAYBOOKS[status.plan]?.name || status.plan} plan</span>
                <span style={{ fontSize:11, color:C.t4, marginLeft:"auto" }}>
                  {status.status === "running" ? `${elapsed}s elapsed` : new Date(status.triggeredAt).toLocaleTimeString()}
                </span>
              </div>

              {/* Progress bar */}
              {(() => {
                const total = status.rules?.length || 1;
                const done  = status.rules?.filter(r => r.status === "completed" || r.status === "error").length || 0;
                const pct   = Math.round((done / total) * 100);
                return (
                  <div style={{ marginBottom:16 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:C.t3, marginBottom:5 }}>
                      <span>Step {Math.min(done+1, total)} of {total}</span>
                      <span>{pct}%</span>
                    </div>
                    <div style={{ height:6, background:C.s3, borderRadius:3, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${pct}%`, background: status.status==="completed"?C.success:C.accent, borderRadius:3, transition:"width 0.6s ease" }} />
                    </div>
                    {status.status === "running" && (
                      <div style={{ fontSize:10, color:C.t4, marginTop:5 }}>
                        Each step takes 15–30s · typically 1–2 min total
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Steps */}
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {(status.rules||[]).map((r, idx) => {
                  const dest = RULE_DESTINATIONS[r.type];
                  const goToDetails = () => {
                    if (!dest || !onNavigate) return;
                    if (dest.tab) sessionStorage.setItem("research-pending-tab", dest.tab);
                    onNavigate(dest.mod);
                  };
                  return (
                  <div key={r.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px",
                    background: r.status==="running" ? `${C.accent}10` : C.s2,
                    border: `1px solid ${r.status==="running" ? `${C.accent}40` : "transparent"}`,
                    borderRadius:8, transition:"all 0.3s" }}>
                    <div style={{ width:22, height:22, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
                      background: r.status==="completed"?`${C.success}20`:r.status==="running"?`${C.accent}20`:r.status==="error"?`${C.danger}20`:C.s3 }}>
                      {statusIcon(r.status)}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, color: r.status==="pending"?C.t3:C.text, fontWeight: r.status==="running"?700:400 }}>{r.label}</div>
                      {r.status==="running" && (
                        <div style={{ fontSize:11, color:C.accent, marginTop:2, display:"flex", alignItems:"center", gap:5 }}>
                          <span style={{ animation:"pulse 1.2s infinite" }}>●</span> Working…
                        </div>
                      )}
                      {r.status==="completed" && r.result?.count!=null && (
                        <div style={{ fontSize:11, color:C.success, marginTop:2 }}>✓ {r.result.count} items found</div>
                      )}
                      {r.status==="completed" && r.result?.taskId && (
                        <div style={{ fontSize:11, color:C.success, marginTop:2 }}>✓ Audit started</div>
                      )}
                      {r.status==="completed" && r.result?.drafted && (
                        <div style={{ fontSize:11, color:C.success, marginTop:2 }}>✓ Draft saved</div>
                      )}
                      {r.status==="error" && <div style={{ fontSize:11, color:C.danger, marginTop:2 }}>Failed — will skip</div>}
                    </div>
                    {r.status === "completed" && dest && (
                      <button onClick={goToDetails}
                        style={{ fontSize:11, padding:"4px 10px", borderRadius:6, background:`${C.accent}18`, border:`1px solid ${C.accent}40`, color:C.accent, cursor:"pointer", fontFamily:"inherit", fontWeight:600, whiteSpace:"nowrap", flexShrink:0 }}>
                        {dest.label} →
                      </button>
                    )}
                    {!dest && <span style={{ fontSize:11, color:C.t4, flexShrink:0 }}>#{idx+1}</span>}
                  </div>
                )})}
              </div>

              {status.status==="completed" && (
                <div style={{ marginTop:14, padding:"10px 14px", background:`${C.success}12`, border:`1px solid ${C.success}30`, borderRadius:8, fontSize:12, color:C.success }}>
                  ✓ All done — check Research, Content Queue, and Messaging for results.
                </div>
              )}
              {status.status==="completed_with_errors" && (
                <div style={{ marginTop:14, padding:"10px 14px", background:`${C.danger}12`, border:`1px solid ${C.danger}30`, borderRadius:8, fontSize:12, color:C.danger }}>
                  Completed with some errors. Successful steps were still saved.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Scheduler({ profile }) {
  const [schedules, setSchedules] = useState([]);
  const [showForm, setShowForm]   = useState(false);
  const [running, setRunning]     = useState(null);
  const [form, setForm]           = useState({ name:"", type:"email_newsletter", frequency:"weekly", dayOfWeek:1, dayOfMonth:1, time:"09:00", status:"active", config:{ goal:"", topic:"", tone:"friendly" } });

  useEffect(() => {
    store.get("autopilot-schedules").then(d => { if (d) setSchedules(d); });
  }, []);

  const save = async (scheds) => { setSchedules(scheds); await store.set("autopilot-schedules", scheds); };

  const nextRunDisplay = (s) => {
    if (!s.nextRun) return "—";
    const d = new Date(s.nextRun);
    const days = Math.round((d - new Date()) / 86400000);
    if (days === 0) return "Today";
    if (days === 1) return "Tomorrow";
    return `In ${days} days · ${d.toLocaleDateString("en-US",{month:"short",day:"numeric"})}`;
  };

  const calcNextRun = (f) => {
    const now = new Date(); const [h, m] = f.time.split(":").map(Number);
    let d = new Date(now); d.setHours(h, m, 0, 0);
    if (d <= now) d.setDate(d.getDate() + 1);
    if (f.frequency === "weekly" || f.frequency === "biweekly") {
      while (d.getDay() !== parseInt(f.dayOfWeek)) d.setDate(d.getDate() + 1);
      if (f.frequency === "biweekly") d.setDate(d.getDate() + 14);
    } else if (f.frequency === "monthly") {
      d = new Date(now.getFullYear(), now.getMonth() + 1, parseInt(f.dayOfMonth) || 1, h, m);
    } else if (f.frequency === "quarterly") {
      d = new Date(now.getFullYear(), now.getMonth() + 3, parseInt(f.dayOfMonth) || 1, h, m);
    }
    return d.toISOString();
  };

  const addSchedule = async () => {
    if (!form.name || !form.type) return;
    const s = { ...form, id: Date.now(), nextRun: calcNextRun(form), lastRun: null, history: [], createdAt: new Date().toISOString() };
    await save([...schedules, s]);
    setShowForm(false);
    setForm({ name:"", type:"email_newsletter", frequency:"weekly", dayOfWeek:1, dayOfMonth:1, time:"09:00", status:"active", config:{ goal:"", topic:"", tone:"friendly" } });
    await logActivity("campaign", `Scheduled job created: ${s.name}`, { frequency: s.frequency, type: s.type });
  };

  const toggle = async (id) => {
    await save(schedules.map(s => s.id === id ? { ...s, status: s.status === "active" ? "paused" : "active" } : s));
  };

  const remove = async (id) => { await save(schedules.filter(s => s.id !== id)); };

  const runNow = async (job) => {
    setRunning(job.id);
    const wid = await getWorkspaceId();
    const res = await fetch("/api/autopilot/run-now", {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ workspaceId: wid, jobId: job.id }),
    });
    const d = await res.json();
    if (d.ok) {
      await store.get("autopilot-schedules").then(fresh => { if (fresh) setSchedules(fresh); });
      await logActivity("campaign", `Ran schedule: ${job.name}`, { type: job.type });
    }
    setRunning(null);
  };

  const sf = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const sc = (k, v) => setForm(p => ({ ...p, config: { ...p.config, [k]: v } }));
  const typeInfo = (id) => SCHEDULE_TYPES.find(t => t.id === id) || SCHEDULE_TYPES[0];

  return (
    <div>
      <div style={{ marginBottom:24, display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
        <div>
          <h2 style={S.h2}>Scheduler</h2>
          <p style={{ ...S.sub, marginBottom:0 }}>Define recurring marketing jobs that run on autopilot — no login required.</p>
        </div>
        <button onClick={() => setShowForm(v => !v)} style={{ ...S.btn, padding:"9px 20px", fontSize:13 }}>
          {showForm ? "✕ Cancel" : "+ New Schedule"}
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div style={{ ...S.card, padding:22, marginBottom:24 }}>
          <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:18 }}>New Scheduled Job</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:14 }}>
            <div style={{ gridColumn:"span 2" }}>
              <Field label="Job Name"><input value={form.name} onChange={e=>sf("name",e.target.value)} placeholder='e.g. "Weekly Newsletter"' style={S.input} /></Field>
            </div>
            <div>
              <label style={S.label}>Content Type</label>
              <select value={form.type} onChange={e=>sf("type",e.target.value)} style={S.input}>
                {SCHEDULE_TYPES.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Frequency</label>
              <select value={form.frequency} onChange={e=>sf("frequency",e.target.value)} style={S.input}>
                {FREQUENCIES.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            </div>
            {(form.frequency === "weekly" || form.frequency === "biweekly") && (
              <div>
                <label style={S.label}>Day of Week</label>
                <select value={form.dayOfWeek} onChange={e=>sf("dayOfWeek",parseInt(e.target.value))} style={S.input}>
                  {DAYS.map((d,i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
            )}
            {(form.frequency === "monthly" || form.frequency === "quarterly") && (
              <div>
                <label style={S.label}>Day of Month</label>
                <input type="number" min={1} max={28} value={form.dayOfMonth} onChange={e=>sf("dayOfMonth",e.target.value)} style={S.input} />
              </div>
            )}
            <div>
              <label style={S.label}>Run Time</label>
              <input type="time" value={form.time} onChange={e=>sf("time",e.target.value)} style={S.input} />
            </div>
            <div style={{ gridColumn:"span 2" }}>
              <Field label="Goal / Instructions"><input value={form.config.goal} onChange={e=>sc("goal",e.target.value)} placeholder="e.g. Bring back guests who haven't visited in 30 days" style={S.input} /></Field>
            </div>
            {(form.type === "social_post" || form.type === "blog_post") && (
              <div style={{ gridColumn:"span 2" }}>
                <Field label="Topic / Angle"><input value={form.config.topic} onChange={e=>sc("topic",e.target.value)} placeholder='e.g. "Seasonal specials, chef tips, local events"' style={S.input} /></Field>
              </div>
            )}
          </div>
          <div style={{ marginTop:18, display:"flex", gap:10 }}>
            <button onClick={addSchedule} style={{ ...S.btn, padding:"10px 28px" }}>Save Schedule</button>
            <button onClick={() => setShowForm(false)} style={{ ...S.btnGhost, padding:"10px 20px" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Schedules list */}
      {schedules.length === 0 && !showForm ? (
        <div style={{ ...S.card, padding:40, textAlign:"center" }}>
          <div style={{ fontSize:32, marginBottom:12 }}>◷</div>
          <div style={{ fontSize:15, color:C.text, marginBottom:6 }}>No schedules yet</div>
          <div style={{ fontSize:13, color:C.t3, marginBottom:20 }}>Create your first recurring marketing job — it will run automatically, even when you're not logged in.</div>
          <button onClick={() => setShowForm(true)} style={{ ...S.btn, padding:"10px 24px" }}>+ Create First Schedule</button>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {schedules.map(s => {
            const t = typeInfo(s.type);
            const isRunning = running === s.id;
            return (
              <div key={s.id} style={{ ...S.card, padding:"16px 20px", display:"flex", alignItems:"center", gap:14 }}>
                <div style={{ width:38, height:38, borderRadius:10, background:`${t.color}18`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, color:t.color, flexShrink:0 }}>{t.icon}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                    <span style={{ fontSize:14, fontWeight:700, color:C.text }}>{s.name}</span>
                    <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background: s.status==="active"?`${C.success}18`:`${C.t3}18`, color: s.status==="active"?C.success:C.t3, fontWeight:700 }}>{s.status.toUpperCase()}</span>
                  </div>
                  <div style={{ fontSize:12, color:C.t2 }}>
                    {t.label} · {FREQUENCIES.find(f=>f.id===s.frequency)?.label}
                    {(s.frequency==="weekly"||s.frequency==="biweekly") && ` · ${DAYS[s.dayOfWeek]}`}
                    {` · ${s.time}`}
                  </div>
                  <div style={{ fontSize:11, color:C.t3, marginTop:2 }}>
                    Next run: <span style={{ color:C.accent }}>{nextRunDisplay(s)}</span>
                    {s.lastRun && <> · Last: {new Date(s.lastRun).toLocaleDateString()}</>}
                    {s.history?.length > 0 && <> · {s.history.length} runs completed</>}
                  </div>
                </div>
                <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                  <button onClick={() => runNow(s)} disabled={isRunning}
                    style={{ ...S.btnGhost, fontSize:12, padding:"6px 14px", opacity:isRunning?0.6:1, borderColor:t.color, color:t.color }}>
                    {isRunning ? "Running…" : "▶ Run Now"}
                  </button>
                  <button onClick={() => toggle(s.id)}
                    style={{ ...S.btnGhost, fontSize:12, padding:"6px 12px" }}>
                    {s.status === "active" ? "⏸ Pause" : "▶ Resume"}
                  </button>
                  <button onClick={() => remove(s.id)}
                    style={{ background:"none", border:"none", color:C.t4, cursor:"pointer", fontSize:18, padding:"0 4px" }}
                    onMouseOver={e=>e.currentTarget.style.color=C.danger} onMouseOut={e=>e.currentTarget.style.color=C.t4}>✕</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Reports() {
  const [log, setLog]             = useState([]);
  const [loading, setLoading]     = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateRange, setDateRange] = useState("7d");
  const [from, setFrom]           = useState("");
  const [to, setTo]               = useState("");
  const [shareMsg, setShareMsg]   = useState("");

  useEffect(() => {
    store.get("activity_log").then(d => { setLog(d || []); setLoading(false); });
  }, []);

  const DATE_PRESETS = [
    { id: "7d",    label: "Last 7 days" },
    { id: "30d",   label: "Last 30 days" },
    { id: "month", label: "Last month" },
    { id: "60d",   label: "Last 60 days" },
    { id: "custom",label: "Custom range" },
  ];

  const getPresetBounds = (id) => {
    const now = new Date(); const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    if (id === "7d")    return { from: new Date(y, m, d - 6), to: null };
    if (id === "30d")   return { from: new Date(y, m, d - 29), to: null };
    if (id === "60d")   return { from: new Date(y, m, d - 59), to: null };
    if (id === "month") return { from: new Date(y, m - 1, 1), to: new Date(y, m, 0) };
    return null;
  };

  const filtered = log.filter(e => {
    if (typeFilter !== "all" && e.type !== typeFilter) return false;
    if (dateRange === "custom") {
      if (from && new Date(e.timestamp) < new Date(from)) return false;
      if (to   && new Date(e.timestamp) > new Date(to + "T23:59:59")) return false;
    } else {
      const bounds = getPresetBounds(dateRange);
      if (bounds?.from && new Date(e.timestamp) < bounds.from) return false;
      if (bounds?.to   && new Date(e.timestamp) > bounds.to)   return false;
    }
    return true;
  });

  const exportPDF = () => {
    const win = window.open("", "_blank");
    const rows = filtered.map(e => {
      const det = Object.entries(e.details || {}).filter(([k]) => k !== "status").map(([k,v]) => `${k}: ${v}`).join(" · ");
      return `<tr><td>${new Date(e.timestamp).toLocaleString()}</td><td>${ACT_TYPES[e.type]?.label || e.type}</td><td>${e.action}</td><td>${det}</td><td style="color:${e.status==="success"?"#2d7a4f":"#bb3b2a"}">${e.status}</td></tr>`;
    }).join("");
    win.document.write(`<!DOCTYPE html><html><head><title>Activity Report</title><style>
      body{font-family:-apple-system,sans-serif;padding:40px;color:#111;font-size:13px}
      h1{font-size:20px;margin:0 0 4px}
      .meta{color:#666;font-size:12px;margin-bottom:24px}
      table{width:100%;border-collapse:collapse}
      th{text-align:left;padding:8px 10px;background:#f5f5f5;border-bottom:2px solid #ddd;font-size:11px;text-transform:uppercase;letter-spacing:.05em}
      td{padding:7px 10px;border-bottom:1px solid #eee;vertical-align:top}
      tr:nth-child(even) td{background:#fafafa}
      @media print{body{padding:20px}}
    </style></head><body>
      <h1>Activity Report</h1>
      <div class="meta">Generated ${new Date().toLocaleString()} &nbsp;·&nbsp; ${filtered.length} activities${from||to ? ` &nbsp;·&nbsp; ${from||"start"} – ${to||"now"}` : ""}</div>
      <table><thead><tr><th>Date & Time</th><th>Type</th><th>Action</th><th>Details</th><th>Status</th></tr></thead><tbody>${rows||"<tr><td colspan=5 style='color:#999;padding:20px'>No activities found</td></tr>"}</tbody></table>
    </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 400);
  };

  const shareEmail = () => {
    const lines = filtered.slice(0, 30).map(e =>
      `• [${ACT_TYPES[e.type]?.label || e.type}] ${e.action} — ${new Date(e.timestamp).toLocaleString()}`
    ).join("\n");
    const subj = encodeURIComponent(`Activity Report — ${new Date().toLocaleDateString()}`);
    const body = encodeURIComponent(`Activity Report\n${"─".repeat(40)}\n${filtered.length} activities${from||to ? ` · ${from||"start"} to ${to||"now"}` : ""}\n\n${lines}${filtered.length > 30 ? "\n\n...and more." : ""}\n\n— Sent from Kurb AI`)  // activity report footer — not white-labelled (internal tool);
    window.open(`mailto:?subject=${subj}&body=${body}`);
  };

  const copyLink = async () => {
    const params = new URLSearchParams({ type: typeFilter, from, to }).toString();
    const url = `${window.location.origin}${window.location.pathname}?report=1&${params}`;
    try { await navigator.clipboard.writeText(url); } catch { }
    setShareMsg("Link copied!"); setTimeout(() => setShareMsg(""), 2500);
  };

  const fmtTime = ts => {
    const d = new Date(ts);
    return d.toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }) + " · " + d.toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit" });
  };

  return (
    <div>
      <div style={{ marginBottom: 24, display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div>
          <h2 style={S.h2}>Activity Reports</h2>
          <p style={{ ...S.sub, marginBottom:0 }}>Track all actions performed across modules.</p>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
          {shareMsg && <span style={{ fontSize:12, color:C.success }}>{shareMsg}</span>}
          <button onClick={copyLink} style={{ ...S.btnGhost, fontSize:12, padding:"7px 14px" }}>⊞ Copy Link</button>
          <button onClick={shareEmail} style={{ ...S.btnGhost, fontSize:12, padding:"7px 14px" }}>✉ Email Report</button>
          <button onClick={exportPDF} style={{ ...S.btn, fontSize:12, padding:"7px 16px" }}>↓ Export PDF</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ background:C.s1, border:`1px solid ${C.border}`, borderRadius:12, padding:"16px 20px", marginBottom:20, display:"flex", flexDirection:"column", gap:14 }}>
        {/* Date presets */}
        <div>
          <label style={S.label}>Date Range</label>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {DATE_PRESETS.map(p => (
              <button key={p.id} onClick={() => { setDateRange(p.id); if (p.id !== "custom") { setFrom(""); setTo(""); } }}
                style={{ padding:"6px 14px", borderRadius:20, fontSize:12, cursor:"pointer", fontFamily:"inherit", border:`1px solid ${dateRange===p.id ? C.accent : C.border}`, background:dateRange===p.id ? `${C.accent}18` : "transparent", color:dateRange===p.id ? C.accent : C.t2, fontWeight:dateRange===p.id?700:400, transition:"all 0.1s" }}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        {/* Custom date inputs — only shown when Custom is selected */}
        {dateRange === "custom" && (
          <div style={{ display:"flex", gap:12 }}>
            <div style={{ flex:1 }}>
              <label style={S.label}>From</label>
              <input type="date" value={from} onChange={e=>setFrom(e.target.value)} style={S.input} />
            </div>
            <div style={{ flex:1 }}>
              <label style={S.label}>To</label>
              <input type="date" value={to} onChange={e=>setTo(e.target.value)} style={S.input} />
            </div>
          </div>
        )}
        {/* Type filter */}
        <div>
          <label style={S.label}>Type</label>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {[["all","All"], ...Object.entries(ACT_TYPES).map(([id,t]) => [id, t.label])].map(([id, label]) => (
              <button key={id} onClick={() => setTypeFilter(id)}
                style={{ padding:"5px 12px", borderRadius:20, fontSize:12, cursor:"pointer", fontFamily:"inherit", border:`1px solid ${typeFilter===id ? (ACT_TYPES[id]?.color||C.accent) : C.border}`, background:typeFilter===id ? `${ACT_TYPES[id]?.color||C.accent}18` : "transparent", color:typeFilter===id ? (ACT_TYPES[id]?.color||C.accent) : C.t2, fontWeight:typeFilter===id?700:400, transition:"all 0.1s" }}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {(typeFilter !== "all" || dateRange !== "7d") && (
          <div>
            <button onClick={() => { setDateRange("7d"); setFrom(""); setTo(""); setTypeFilter("all"); }}
              style={{ ...S.btnGhost, fontSize:12, padding:"5px 12px" }}>✕ Reset filters</button>
          </div>
        )}
      </div>

      {/* Summary bar */}
      <div style={{ display:"flex", gap:12, marginBottom:20, flexWrap:"wrap" }}>
        {Object.entries(ACT_TYPES).map(([id, t]) => {
          const count = filtered.filter(e => e.type === id).length;
          if (!count) return null;
          return (
            <div key={id} style={{ background:C.s1, border:`1px solid ${C.border}`, borderRadius:10, padding:"10px 16px", display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:14, color:t.color }}>{t.icon}</span>
              <div>
                <div style={{ fontSize:16, fontWeight:700, color:C.text, lineHeight:1 }}>{count}</div>
                <div style={{ fontSize:11, color:C.t3, marginTop:2 }}>{t.label}</div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && !loading && (
          <div style={{ fontSize:13, color:C.t3, padding:"10px 0" }}>No activities match the selected filters.</div>
        )}
      </div>

      {/* Activity list */}
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {loading && <div style={{ color:C.t3, fontSize:13, padding:20 }}>Loading…</div>}
        {!loading && filtered.length === 0 && log.length === 0 && (
          <div style={{ background:C.s1, border:`1px solid ${C.border}`, borderRadius:12, padding:40, textAlign:"center" }}>
            <div style={{ fontSize:32, marginBottom:12 }}>◑</div>
            <div style={{ fontSize:15, color:C.text, marginBottom:6 }}>No activity yet</div>
            <div style={{ fontSize:13, color:C.t3 }}>Actions you take across modules will appear here — publishing content, responding to reviews, running campaigns, and more.</div>
          </div>
        )}
        {filtered.map(e => {
          const t = ACT_TYPES[e.type] || { label: e.type, color: C.t2, icon: "◈" };
          const det = Object.entries(e.details || {}).filter(([k]) => k !== "status" && k !== "link");
          return (
            <div key={e.id} style={{ background:C.s1, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ width:34, height:34, borderRadius:8, background:`${t.color}18`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, color:t.color, flexShrink:0 }}>{t.icon}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  <span style={{ fontSize:13, fontWeight:600, color:C.text }}>{e.action}</span>
                  <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:`${t.color}18`, color:t.color, fontWeight:600 }}>{t.label}</span>
                  <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background: e.status==="success" ? `${C.success}15` : `${C.danger}15`, color: e.status==="success" ? C.success : C.danger, fontWeight:600 }}>{e.status}</span>
                </div>
                {det.length > 0 && (
                  <div style={{ fontSize:12, color:C.t2, marginTop:3 }}>
                    {det.map(([k,v]) => <span key={k} style={{ marginRight:12 }}><span style={{ color:C.t3 }}>{k}:</span> {String(v).slice(0,60)}</span>)}
                  </div>
                )}
                {e.details?.link && (
                  <a href={e.details.link} target="_blank" rel="noopener noreferrer" style={{ fontSize:11, color:"#4285f4", marginTop:2, display:"inline-block" }}>View →</a>
                )}
              </div>
              <div style={{ fontSize:11, color:C.t3, textAlign:"right", flexShrink:0, whiteSpace:"nowrap" }}>{fmtTime(e.timestamp)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── SUPER ADMIN WORKSPACES ─────────────────────────────────────────── */
/* ─── ADMIN DASHBOARD + PLATFORM VIEWS → src/modules/admin.jsx (lazy-loaded) ─── */

/* ─── INBOX MODULE ───────────────────────────────────────────────────── */
/* ─── INBOX → src/modules/inbox.jsx (lazy-loaded) ─── */


// ── SEO History Panel ─────────────────────────────────────────────────────────
/* ─── SEO History + Index Agent Help → src/modules/seo.jsx ─── */

/* ─── IMAGE STUDIO ──────────────────────────────────────────────────── */
/* ─── IMAGE STUDIO → src/modules/image-studio.jsx (lazy-loaded) ─── */

/* ─── SEO / INDEX AGENT / AEO → src/modules/seo.jsx (lazy-loaded) ─── */

/* ─── KURB AI ASSISTANT → src/modules/ai.jsx (AIAssistant + AI_MODULE_REGISTRY + buildAppContext + ReviewReplyCard) ─── */

function AddWebsiteModal({ onPurchase, onComplete, onClose }) {
  const [step, setStep]             = useState("pricing");
  const [plan, setPlan]             = useState("yearly");
  const [processing, setProcessing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName]             = useState("");
  const [url, setUrl]               = useState("");
  const [err, setErr]               = useState("");

  const handlePurchase = async () => {
    setProcessing(true);
    setProcessing(false);
    setStep("form");
  };

  const handleSubmit = async () => {
    if (!name.trim()) { setErr("Please enter a website name."); return; }
    if (!url.trim())  { setErr("Please enter your website URL."); return; }
    setErr("");
    setSubmitting(true);
    // Block creation if any other workspace on the platform already records
    // this website URL.
    const dup = await checkWebsiteDomain(url.trim());
    if (dup?.exists) {
      setErr(`A workspace for ${dup.host} already exists ("${dup.workspaceName}"). Use a different website, or contact the existing owner.`);
      setSubmitting(false);
      return;
    }
    await onComplete(name.trim(), url.trim(), plan);
    setSubmitting(false);
  };

  const yearlyTotal   = 216;
  const monthlyTotal  = 39;

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 }}>
      <div style={{ background:"#fff", borderRadius:20, padding:"32px 28px", maxWidth:440, width:"100%", boxShadow:"0 20px 60px rgba(0,0,0,0.25)", position:"relative" }}>

        {/* Close */}
        <button onMouseDown={e=>e.preventDefault()} onClick={onClose}
          style={{ position:"absolute", top:16, right:16, width:32, height:32, borderRadius:8, border:`1px solid ${C.border}`, background:C.s2, cursor:"pointer", fontSize:16, color:C.t2, display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>

        {step === "pricing" ? (
          <>
            <div style={{ textAlign:"center", marginBottom:24 }}>
              <div style={{ fontSize:22, fontWeight:800, color:C.text, marginBottom:8 }}>Add Another Website</div>
              <div style={{ fontSize:14, color:C.t2, lineHeight:1.5 }}>Each website gets its own content agent with dedicated keyword research and articles.</div>
            </div>

            {/* Plan options */}
            {[
              { id:"yearly",  label:"Yearly",  price:"$18/month", sub:"billed annually",  badge:"Save $252/yr", badgeColor:"#f0fdf4", badgeText:"#166534", pill:"50% OFF", pillSelected:"#ef4444", pillSelectedText:"#fff" },
              { id:"monthly", label:"Monthly", price:"$39/month", sub:null,                badge:null,           pill:"50% OFF", pillSelected:C.s3, pillSelectedText:C.t2 },
            ].map(opt => (
              <div key={opt.id} onClick={() => setPlan(opt.id)}
                style={{ border: plan===opt.id ? "2px solid #6366f1" : `1px solid ${C.border}`, borderRadius:12, padding:"14px 16px", marginBottom:10, cursor:"pointer", background: plan===opt.id ? "#f5f3ff" : C.s1, display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ width:18, height:18, borderRadius:"50%", border: plan===opt.id ? "5px solid #6366f1" : `2px solid ${C.t4}`, flexShrink:0 }} />
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:15, fontWeight:700, color:C.text }}>{opt.label}</span>
                    {opt.badge && <span style={{ fontSize:11, fontWeight:700, background:opt.badgeColor, color:opt.badgeText, borderRadius:6, padding:"2px 8px" }}>{opt.badge}</span>}
                  </div>
                  <div style={{ fontSize:13, color:C.t2, marginTop:2 }}>{opt.price}{opt.sub ? `, ${opt.sub}` : ""}</div>
                </div>
                <span style={{ fontSize:11, fontWeight:700, background: plan===opt.id ? (opt.id==="yearly"?"#ef4444":C.s3) : C.s3, color: plan===opt.id ? (opt.id==="yearly"?"#fff":C.t2) : C.t2, borderRadius:20, padding:"3px 10px", flexShrink:0 }}>{opt.pill}</span>
              </div>
            ))}

            {/* Features */}
            <div style={{ marginBottom:20 }}>
              {["Separate keyword strategy for each site","Independent publishing schedule","Same dashboard, easy switching","Cancel anytime"].map(f => (
                <div key={f} style={{ display:"flex", alignItems:"center", gap:10, padding:"5px 0" }}>
                  <span style={{ color:C.success, fontSize:16, flexShrink:0 }}>✓</span>
                  <span style={{ fontSize:14, color:C.t2 }}>{f}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <button onClick={handlePurchase} disabled={processing}
              style={{ ...S.btn, width:"100%", padding:14, fontSize:15, background:"#6366f1", borderRadius:12, opacity: processing ? 0.7 : 1 }}>
              {processing ? "Setting up…" : "Add Website →"}
            </button>

            <div style={{ fontSize:11, color:C.t3, textAlign:"center", marginTop:10, lineHeight:1.5 }}>
              By clicking "Add Website", billing starts today for{" "}
              <strong style={{ color:C.text }}>${plan==="yearly" ? yearlyTotal : monthlyTotal}</strong>{" "}
              and renews {plan==="yearly" ? "every year" : "every month"} until you cancel.
            </div>
          </>
        ) : (
          <>
            <div style={{ textAlign:"center", marginBottom:24 }}>
              <div style={{ fontSize:22, fontWeight:800, color:C.text, marginBottom:6 }}>Set Up Your Website</div>
              <div style={{ fontSize:14, color:C.t2 }}>We'll analyse it and build your content plan.</div>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12, fontWeight:700, color:C.t2, textTransform:"uppercase", letterSpacing:"0.5px", display:"block", marginBottom:6 }}>Website Name</label>
              <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Route Advisors Blog"
                style={{ width:"100%", fontSize:14, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 12px", fontFamily:"inherit", boxSizing:"border-box" }} />
            </div>

            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:12, fontWeight:700, color:C.t2, textTransform:"uppercase", letterSpacing:"0.5px", display:"block", marginBottom:6 }}>Website URL</label>
              <input value={url} onChange={e=>setUrl(e.target.value)} placeholder="https://yourdomain.com"
                type="url"
                style={{ width:"100%", fontSize:14, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 12px", fontFamily:"inherit", boxSizing:"border-box" }} />
            </div>

            {err && <div style={{ fontSize:13, color:C.danger, marginBottom:12 }}>{err}</div>}

            <button onClick={handleSubmit} disabled={submitting}
              style={{ ...S.btn, width:"100%", padding:14, fontSize:15, background:"#6366f1", borderRadius:12, opacity: submitting ? 0.7 : 1 }}>
              {submitting ? "Setting up…" : "Set Up My Website →"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── WorkspacePicker dropdown ──────────────────────────────────────────────────
function WorkspacePicker({ workspaces, activeId, onSelect, onAdd, addLabel = "Add Business" }) {
  const ICONS = { restaurant:"🍽", health:"🏥", home:"🏠", beauty:"💅", fitness:"🏋", default:"🏢" };
  return (
    <div style={{ position:"absolute", top:"calc(100% + 6px)", left:0, right:0, background:C.s1, border:`1px solid ${C.border}`, borderRadius:10, boxShadow:"0 8px 32px rgba(0,0,0,0.5)", overflow:"hidden", zIndex:199 }}>
      <div style={{ padding:"8px 0", maxHeight:240, overflowY:"auto" }}>
        {workspaces.map(ws => (
          <button key={ws.id} onClick={() => onSelect(ws)}
            style={{ width:"100%", padding:"9px 14px", background: ws.id === activeId ? `${C.accent}18` : "transparent", border:"none", cursor:"pointer", fontFamily:"inherit", textAlign:"left", display:"flex", alignItems:"center", gap:10, transition:"background 0.1s" }}
            onMouseOver={e => { if (ws.id !== activeId) e.currentTarget.style.background = C.s2; }}
            onMouseOut={e => { if (ws.id !== activeId) e.currentTarget.style.background = "transparent"; }}>
            <span style={{ fontSize:16 }}>{ICONS[ws.category] || ICONS.default}</span>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:12, fontWeight:600, color: ws.id === activeId ? C.accent : C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{ws.name}</div>
              {ws.category && <div style={{ fontSize:10, color:C.t3, textTransform:"capitalize" }}>{ws.category}</div>}
            </div>
            {ws.id === activeId && <span style={{ fontSize:11, color:C.accent }}>✓</span>}
          </button>
        ))}
      </div>
      <div style={{ borderTop:`1px solid ${C.border}` }}>
        <button onClick={onAdd}
          style={{ width:"100%", padding:"10px 14px", background:"transparent", border:"none", cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:10, color:C.accent, fontSize:12, fontWeight:600, transition:"background 0.1s" }}
          onMouseOver={e => e.currentTarget.style.background = `${C.accent}12`}
          onMouseOut={e => e.currentTarget.style.background = "transparent"}>
          <span style={{ fontSize:16, lineHeight:1 }}>＋</span>
          {addLabel}
        </button>
      </div>
    </div>
  );
}

// ── Notification Panel ────────────────────────────────────────────────────────
function NotifItem({ n, severityColor, relativeTime, onMarkRead, onClose, onNavigate }) {
  return (
    <div style={{ padding:"12px 16px", borderBottom:`1px solid ${C.border}`, background:n.read_at ? C.s2 : C.s1, display:"flex", gap:12, alignItems:"flex-start" }}>
      <div style={{ width:3, borderRadius:2, alignSelf:"stretch", flexShrink:0, background:severityColor(n.severity) }} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:n.read_at ? 400 : 600, color:C.text, marginBottom:2 }}>{n.title}</div>
        {n.body && <div style={{ fontSize:12, color:C.t2, lineHeight:1.4, marginBottom:6 }}>{n.body}</div>}
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:11, color:C.t3 }}>{relativeTime(n.created_at)}</span>
          {!n.read_at && n.action_mod && (
            <button
              onMouseDown={e => e.preventDefault()}
              onClick={() => { onMarkRead(n.id); onClose(); onNavigate(n.action_mod); }}
              style={{ fontSize:11, padding:"2px 8px", borderRadius:5, border:`1px solid ${C.border}`, background:C.s1, color:C.accent, cursor:"pointer", fontFamily:"inherit", fontWeight:500 }}>
              {n.action_label || "View"} →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function NotifPanel({ notifications, onClose, onMarkRead, onMarkAllRead, onNavigate }) {
  const unread = notifications.filter(n => !n.read_at);
  const read   = notifications.filter(n => n.read_at);

  const severityColor = (s) => s === "urgent" ? "#ef4444" : s === "warning" ? "#f59e0b" : "#3b82f6";

  const relativeTime = (isoStr) => {
    if (!isoStr) return "";
    const diff = Date.now() - new Date(isoStr).getTime();
    if (!Number.isFinite(diff)) return "";
    const m = Math.floor(diff / 60000);
    if (m < 1)  return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  return (
    <div style={{ position:"absolute", top:44, right:0, width:"min(360px, calc(100vw - 16px))", maxHeight:480, overflowY:"auto", background:C.s1, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:"0 8px 32px rgba(28,36,55,0.16)", zIndex:1000 }}>
      {/* Header */}
      <div style={{ padding:"14px 16px 10px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, background:C.s1, zIndex:1 }}>
        <span style={{ fontSize:14, fontWeight:700, color:C.text }}>Notifications</span>
        {unread.length > 0 && (
          <button
            onMouseDown={e => e.preventDefault()}
            onClick={onMarkAllRead}
            style={{ fontSize:11, color:C.t2, background:"none", border:"none", cursor:"pointer", fontFamily:"inherit", padding:0 }}>
            Mark all read
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div style={{ padding:"32px 24px", textAlign:"center" }}>
          <div style={{ fontSize:24, marginBottom:8 }}>✓</div>
          <div style={{ fontSize:13, color:C.t2 }}>All caught up! Kurb AI is watching for things that need attention.</div>
        </div>
      ) : (
        <>
          {unread.map(n => <NotifItem key={n.id} n={n} severityColor={severityColor} relativeTime={relativeTime} onMarkRead={onMarkRead} onClose={onClose} onNavigate={onNavigate} />)}
          {unread.length > 0 && read.length > 0 && (
            <div style={{ padding:"8px 16px", fontSize:11, color:C.t3, textAlign:"center", background:C.s2, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}` }}>── Earlier ──</div>
          )}
          {read.map(n => <NotifItem key={n.id} n={n} severityColor={severityColor} relativeTime={relativeTime} onMarkRead={onMarkRead} onClose={onClose} onNavigate={onNavigate} />)}
        </>
      )}
    </div>
  );
}

// ── Top Bar ───────────────────────────────────────────────────────────────────
function TopBar({ profile, authUser, userRole, userMode, showAIPanel, onToggleAI, onNavigate, onSignOut, onEditProfile, onSwitchMode, notifications = [], showNotifPanel, onToggleNotifPanel, onMarkRead, onMarkAllRead, onNotifNavigate, onToggleMobileNav }) {
  const isMobile = useIsMobile();
  const [showProfile, setShowProfile] = useState(false);
  const unreadCount = notifications.filter(n => !n.read_at).length;
  const niche = NICHES.find(n => n.id === profile?.niche);

  const avatarLetter = (authUser?.email?.[0] || profile?.name?.[0] || "K").toUpperCase();

  const dropdownItems = [
    { label: "Edit Business Profile", icon: "◈", action: () => { onEditProfile(); setShowProfile(false); } },
    { label: "Settings",              icon: "⚙",  action: () => { onNavigate("settings"); setShowProfile(false); } },
    { label: "Plans & Billing",       icon: "⚡", action: () => { onNavigate("pricing"); setShowProfile(false); } },
    null, // divider
    { label: userMode === "default" ? "Switch to Advanced Mode" : "Switch to Default Mode", icon: "⇄",
      action: () => { onSwitchMode(); setShowProfile(false); } },
    null, // divider
    { label: "Sign Out", icon: "↩", action: () => { onSignOut(); setShowProfile(false); }, danger: true },
  ];

  return (
    <div style={{ height:56, background:C.s1, borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", paddingLeft: isMobile ? 12 : 20, paddingRight: isMobile ? 12 : 20, gap: isMobile ? 8 : 16, flexShrink:0, zIndex:100, boxShadow:"0 1px 3px rgba(28,36,55,0.07)" }}>
      {/* Hamburger — mobile only */}
      {isMobile && onToggleMobileNav && (
        <button onClick={onToggleMobileNav}
          style={{ width:36, height:36, borderRadius:8, border:"none", background:"transparent",
            display:"flex", alignItems:"center", justifyContent:"center",
            cursor:"pointer", fontSize:20, color:C.t2, flexShrink:0, lineHeight:1 }}>
          ☰
        </button>
      )}

      {/* Search / Ask bar — full on desktop, icon-only tap on mobile */}
      {isMobile ? (
        <button onClick={onToggleAI}
          style={{ flex:1, background: showAIPanel ? `${C.accent}10` : C.s2,
            border:`1px solid ${showAIPanel ? C.accent+"60" : C.border}`,
            borderRadius:8, padding:"8px 12px", display:"flex", alignItems:"center", gap:8,
            cursor:"pointer", color:C.t3, fontFamily:"inherit" }}>
          <span style={{ fontSize:14, color: showAIPanel ? C.accent : C.t3 }}>✦</span>
          <span style={{ fontSize:13, color:C.t3, flex:1, textAlign:"left" }}>Ask anything…</span>
        </button>
      ) : (
        <button onClick={onToggleAI} style={{ flex:1, maxWidth:440, background: showAIPanel ? `${C.accent}10` : C.s2, border:`1px solid ${showAIPanel ? C.accent+"60" : C.border}`, borderRadius:8, padding:"8px 14px", display:"flex", alignItems:"center", gap:10, cursor:"pointer", color:C.t3, fontFamily:"inherit", transition:"all 0.15s" }}>
          <span style={{ fontSize:14, color:C.t3 }}>✦</span>
          <span style={{ fontSize:13, color:C.t3, flex:1, textAlign:"left" }}>Find or ask anything…</span>
          <span style={{ fontSize:10, background:C.s3, color:C.t3, padding:"2px 7px", borderRadius:5, fontWeight:600, flexShrink:0 }}>⌘K</span>
        </button>
      )}

      {!isMobile && <div style={{ flex:1 }} />}

      {/* Right actions */}
      <div style={{ display:"flex", alignItems:"center", gap:4 }}>
        {/* AI toggle */}
        <button onClick={onToggleAI} title="AI Assistant" style={{ width:34, height:34, borderRadius:8, border:`1px solid ${showAIPanel ? C.accent : C.border}`, background:showAIPanel ? `${C.accent}12` : "transparent", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:16, color:showAIPanel ? C.accent : C.t2, transition:"all 0.15s" }}>✦</button>
        {/* Notifications */}
        <div style={{ position:"relative" }}>
          <button title="Notifications" onClick={onToggleNotifPanel}
            style={{ width:34, height:34, borderRadius:8, border:`1px solid ${showNotifPanel ? C.accent : "transparent"}`, background:showNotifPanel ? `${C.accent}12` : "transparent", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:15, color:showNotifPanel ? C.accent : C.t2, transition:"all 0.15s" }}
            onMouseOver={e=>{if(!showNotifPanel){e.currentTarget.style.background=C.s2;e.currentTarget.style.borderColor=C.border;}}}
            onMouseOut={e=>{if(!showNotifPanel){e.currentTarget.style.background="transparent";e.currentTarget.style.borderColor="transparent";}}}>
            🔔
            {unreadCount > 0 && (
              <span style={{ position:"absolute", top:-4, right:-4, background:"#ef4444", color:"#fff", borderRadius:"50%", fontSize:9, fontWeight:700, minWidth:16, height:16, display:"flex", alignItems:"center", justifyContent:"center", pointerEvents:"none" }}>
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
          {showNotifPanel && (
            <>
              <div style={{ position:"fixed", inset:0, zIndex:999 }} onMouseDown={onToggleNotifPanel} />
              <NotifPanel
                notifications={notifications}
                onClose={onToggleNotifPanel}
                onMarkRead={onMarkRead}
                onMarkAllRead={onMarkAllRead}
                onNavigate={onNotifNavigate || onNavigate}
              />
            </>
          )}
        </div>
        {/* Settings shortcut */}
        <button onClick={()=>onNavigate("settings")} title="Settings" style={{ width:34, height:34, borderRadius:8, border:`1px solid transparent`, background:"transparent", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:18, color:C.t2, transition:"all 0.15s" }}
          onMouseOver={e=>{e.currentTarget.style.background=C.s2;e.currentTarget.style.borderColor=C.border;}}
          onMouseOut={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.borderColor="transparent";}}>⚙</button>

        {/* Profile */}
        <div style={{ position:"relative", marginLeft:4 }}>
          <button onClick={()=>setShowProfile(v=>!v)} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 10px 5px 5px", borderRadius:8, border:`1px solid ${showProfile ? C.accent : C.border}`, background:showProfile ? `${C.accent}08` : C.s2, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s" }}>
            <div style={{ width:26, height:26, borderRadius:"50%", background:C.accent, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"#fff", flexShrink:0 }}>{avatarLetter}</div>
            <div style={{ textAlign:"left" }}>
              <div style={{ fontSize:12, fontWeight:600, color:C.text, lineHeight:1.2 }}>{profile?.name || authUser?.email?.split("@")[0] || "Account"}</div>
              {niche && <div style={{ fontSize:10, color:C.t3 }}>{niche.label}</div>}
            </div>
            <span style={{ fontSize:9, color:C.t3, transform:showProfile?"rotate(180deg)":"rotate(0deg)", transition:"transform 0.2s" }}>▲</span>
          </button>

          {showProfile && (
            <>
              <div style={{ position:"fixed", inset:0, zIndex:198 }} onClick={()=>setShowProfile(false)} />
              <div style={{ position:"absolute", top:"calc(100% + 6px)", right:0, width:260, background:C.s1, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:"0 8px 32px rgba(28,36,55,0.14)", overflow:"hidden", zIndex:199 }}>
                {/* Header */}
                <div style={{ padding:"14px 16px", borderBottom:`1px solid ${C.border}`, background:C.s2 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:34, height:34, borderRadius:"50%", background:C.accent, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:700, color:"#fff", flexShrink:0 }}>{avatarLetter}</div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{profile?.name || "My Business"}</div>
                      <div style={{ fontSize:11, color:C.t3 }}>{authUser?.email || ""}</div>
                    </div>
                  </div>
                  {userRole && <div style={{ marginTop:8, display:"inline-flex" }}><span style={{ fontSize:10, fontWeight:700, color:ROLES[userRole]?.color||C.accent, background:`${ROLES[userRole]?.color||C.accent}15`, borderRadius:5, padding:"2px 8px", textTransform:"uppercase", letterSpacing:"0.05em" }}>{ROLES[userRole]?.label||userRole}</span></div>}
                </div>
                {/* Menu items */}
                {dropdownItems.map((item, i) => item === null ? (
                  <div key={i} style={{ height:1, background:C.border, margin:"4px 0" }} />
                ) : (
                  <button key={item.label} onClick={item.action} style={{ width:"100%", padding:"10px 16px", background:"transparent", border:"none", cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:10, textAlign:"left", color:item.danger ? C.danger : C.text, fontSize:13, transition:"background 0.1s" }}
                    onMouseOver={e=>e.currentTarget.style.background=item.danger?`${C.danger}08`:C.s2}
                    onMouseOut={e=>e.currentTarget.style.background="transparent"}>
                    <span style={{ color:item.danger?C.danger:C.accent, fontSize:13, width:16 }}>{item.icon}</span>
                    {item.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Default Mode ──────────────────────────────────────────────────────────────

// DEFAULT_OUTCOMES → src/shared/constants.js
DEFAULT_OUTCOMES.generic = DEFAULT_OUTCOMES.restaurant.slice(0, 5);

// ── Default Mode: Dashboard ───────────────────────────────────────────────────
function DefaultDashboard({ profile, activeWorkspace, onNav, onEditProfile, campaigns, contacts, reservations, messages }) {
  const niche = NICHES.find(n => n.id === profile?.niche);
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const displayName = profile?.name || activeWorkspace?.name;
  const activeCampaigns = (campaigns||[]).filter(c => c.status === "active").length;
  const unreadMessages  = (messages||[]).filter(m => m.unread).length;

  const metrics = [
    { label:"Active Campaigns", value:activeCampaigns, icon:"⚡", color:CM_COL,   action:()=>onNav("campaigns") },
    { label:"Customers",        value:(contacts||[]).length, icon:"◈", color:C.accent, action:()=>onNav("customers") },
    { label:"New Messages",     value:unreadMessages,   icon:"✉",  color:GM,       action:()=>onNav("inbox") },
  ];

  const actions = [
    { id:"campaigns", icon:"⚡", color:CM_COL,   title:"Launch a Campaign",  sub:"Get more customers in minutes" },
    { id:"customers", icon:"◈",  color:C.accent, title:"View Customers",     sub:"Contacts, bookings, and messages" },
    { id:"inbox",     icon:"✉",  color:GM,       title:"Check Messages",     sub:"FB Messenger, Instagram, SMS", badge: unreadMessages > 0 ? unreadMessages : null },
  ];

  return (
    <div style={{ maxWidth:700, margin:"0 auto" }}>
      {/* Greeting */}
      <div style={{ marginBottom: !profile?.city ? 16 : 36 }}>
        <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:32, color:C.text, marginBottom:6 }}>{greeting}, {displayName || "there"} 👋</div>
        <div style={{ fontSize:14, color:C.t2 }}>{niche?.label || "Your business"} · {new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}</div>
      </div>

      {/* Setup prompt for incomplete profiles */}
      {!profile?.city && (
        <div style={{ marginBottom:28, padding:"14px 18px", background:`${C.accent}10`, border:`1px solid ${C.accent}30`, borderRadius:10, display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:600, color:C.accent }}>Complete your business profile</div>
            <div style={{ fontSize:12, color:C.t2, marginTop:2 }}>Add your location, contact info and goals so Kurb AI can personalise every tool for you.</div>
          </div>
          <button onClick={onEditProfile} style={{ ...S.btn, padding:"8px 18px", fontSize:12, flexShrink:0 }}>Set Up Now →</button>
        </div>
      )}

      {/* Metrics */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16, marginBottom:36 }}>
        {metrics.map(m => (
          <button key={m.label} onClick={m.action} style={{ ...S.card, padding:"24px 16px", textAlign:"center", cursor:"pointer", border:`1px solid ${C.border}`, background:C.s1, transition:"all 0.15s" }}
            onMouseOver={e=>{e.currentTarget.style.borderColor=m.color+"70";e.currentTarget.style.background=m.color+"0d";}}
            onMouseOut={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.background=C.s1;}}>
            <div style={{ width:44, height:44, borderRadius:12, background:`${m.color}1a`, border:`1px solid ${m.color}35`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, margin:"0 auto 12px", color:m.color }}>{m.icon}</div>
            <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:34, fontWeight:400, color:C.text, lineHeight:1, marginBottom:6 }}>{m.value}</div>
            <div style={{ fontSize:12, color:C.t2, fontWeight:500 }}>{m.label}</div>
          </button>
        ))}
      </div>

      {/* Quick actions */}
      <div style={{ marginBottom:36 }}>
        <div style={{ fontSize:12, fontWeight:700, color:C.t2, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:14 }}>What do you want to do?</div>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {actions.map(a => (
            <button key={a.id} onClick={()=>onNav(a.id)} style={{ ...S.card, padding:"18px 20px", display:"flex", alignItems:"center", gap:16, cursor:"pointer", border:`1px solid ${C.border}`, background:C.s1, textAlign:"left", transition:"all 0.15s" }}
              onMouseOver={e=>{e.currentTarget.style.borderColor=a.color+"60";e.currentTarget.style.background=a.color+"0a";}}
              onMouseOut={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.background=C.s1;}}>
              <div style={{ width:40, height:40, borderRadius:10, background:`${a.color}18`, border:`1px solid ${a.color}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, color:a.color, flexShrink:0 }}>{a.icon}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14, fontWeight:600, color:C.text, display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                  {a.title}
                  {a.badge && <span style={{ fontSize:11, background:a.color, color:"#fff", borderRadius:10, padding:"1px 7px", fontWeight:700 }}>{a.badge}</span>}
                </div>
                <div style={{ fontSize:12, color:C.t2 }}>{a.sub}</div>
              </div>
              <span style={{ color:C.t2, fontSize:18, flexShrink:0 }}>›</span>
            </button>
          ))}
        </div>
      </div>

      {/* Active campaigns */}
      {activeCampaigns > 0 && (
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:C.t2, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:14 }}>Running Now</div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {(campaigns||[]).filter(c=>c.status==="active").slice(0,3).map(c => (
              <div key={c.id} style={{ ...S.card, padding:"14px 18px", display:"flex", alignItems:"center", gap:14 }}>
                <div style={{ width:10, height:10, borderRadius:"50%", background:C.success, flexShrink:0, boxShadow:`0 0 6px ${C.success}80` }} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.name}</div>
                  <div style={{ fontSize:12, color:C.t2, marginTop:2 }}>{c.runs?.length||0} run{c.runs?.length!==1?"s":""} · Started {c.created}</div>
                </div>
                <span style={{ fontSize:12, color:C.success, fontWeight:700, background:`${C.success}15`, padding:"3px 10px", borderRadius:6 }}>Active</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Default Mode: Campaign Launcher ──────────────────────────────────────────
function DefaultCampaigns({ profile, onDone }) {
  const niche    = profile?.niche || "restaurant";
  const outcomes = DEFAULT_OUTCOMES[niche] || DEFAULT_OUTCOMES.generic;
  const biz      = profile?.name || "My Business";
  const [tab,      setTab]      = useState("launch"); // "launch" | "mine"
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [answers,  setAnswers]  = useState({});
  const [phase,    setPhase]    = useState("pick"); // "pick" | "ask" | "running" | "done"
  const [steps,    setSteps]    = useState([]);
  const [campaign, setCampaign] = useState(null);

  const setAns = k => e => setAnswers(p => ({ ...p, [k]: e.target.value }));

  useEffect(() => { store.get("campaigns").then(d => { if (d) setCampaigns(d); }); }, [tab]);
  useEffect(() => {
    const handler = (e) => { setTab(e.detail || "mine"); store.get("campaigns").then(d => { if (d) setCampaigns(d); }); };
    window.addEventListener("campaigns-show-tab", handler);
    return () => window.removeEventListener("campaigns-show-tab", handler);
  }, []);

  const startCampaign = async () => {
    if (!selected) return;
    const topic = selected.topicFn(biz, answers);
    const camp = {
      id: Date.now(), name: `${biz} — ${selected.title}`,
      restaurant: biz, goal: selected.goal, topic, tone: selected.tone,
      schedule: { type:"once", time:"09:00", day:1, date:1 },
      actions: Object.fromEntries(selected.channels.map(c => [c, true])),
      status:"active", runs:[], created:new Date().toLocaleDateString(), lastRun:null, source:"default",
    };
    const enabledActions = ACTIONS.filter(a => camp.actions[a.id]);
    setSteps(enabledActions.map(a => ({ ...a, status:"pending" })));
    setPhase("running");
    const results = {};
    for (let i = 0; i < enabledActions.length; i++) {
      setSteps(prev => prev.map((s,idx) => idx===i ? {...s,status:"running"} : s));
      try {
        const text = await ai(getCampaignPrompt(enabledActions[i].id, camp), "You are an expert marketing AI. Generate high-quality, ready-to-publish content specific to this business and campaign.");
        results[enabledActions[i].id] = text;
        setSteps(prev => prev.map((s,idx) => idx===i ? {...s,status:"done"} : s));
      } catch {
        setSteps(prev => prev.map((s,idx) => idx===i ? {...s,status:"error"} : s));
      }
    }
    const run = { id:Date.now(), timestamp:new Date().toISOString(), campaignName:camp.name, restaurant:biz, results, actionCount:enabledActions.length };
    camp.lastRun = new Date().toISOString(); camp.runs = [run];
    const existing = await store.get("campaigns") || [];
    await store.set("campaigns", [camp, ...existing]);
    await logActivity("campaign", `Launched: ${camp.name}`, { source:"default" });
    setCampaign(camp);
    setPhase("done");
  };

  if (phase === "running" || phase === "done") {
    const done = phase === "done";
    const completed = steps.filter(s => s.status === "done" || s.status === "error").length;
    const pct = steps.length ? Math.round(completed / steps.length * 100) : 0;
    return (
      <div style={{ padding:"32px 28px", maxWidth:560, margin:"0 auto" }}>
        <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:24, color:C.text, marginBottom:6 }}>{done ? "🎉 Campaign Ready!" : "Launching your campaign…"}</div>
        <div style={{ fontSize:13, color:C.t2, marginBottom:28 }}>{done ? `${steps.filter(s=>s.status==="done").length} channels generated and saved.` : `Writing content for each channel — this takes about 2 minutes.`}</div>
        {!done && (
          <div style={{ marginBottom:24 }}>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:6 }}>
              <span style={{ color:C.t2 }}>Generating content…</span>
              <span style={{ color:CM_COL, fontWeight:700 }}>{pct}%</span>
            </div>
            <div style={{ height:6, background:C.s3, borderRadius:4, overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${pct}%`, background:`linear-gradient(90deg,${CM_COL},#a78bfa)`, borderRadius:4, transition:"width 0.5s ease" }} />
            </div>
          </div>
        )}
        <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:28 }}>
          {steps.map(s => (
            <div key={s.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", background:s.status==="done"?`${s.col}10`:s.status==="running"?`${s.col}08`:C.s2, borderRadius:10, border:`1px solid ${s.status==="done"?s.col+"40":s.status==="running"?s.col+"60":C.border}`, transition:"all 0.3s" }}>
              <span style={{ width:20, textAlign:"center", fontSize:14, color:s.status==="done"?s.col:s.status==="running"?s.col:C.t4 }}>
                {s.status==="done"?"✓":s.status==="running"?<span style={{ display:"inline-block", animation:"spin 0.8s linear infinite" }}>◌</span>:"·"}
              </span>
              <span style={{ fontSize:13, color:s.status==="pending"?C.t2:C.text, fontWeight:s.status==="done"?600:400 }}>{s.label}</span>
            </div>
          ))}
        </div>
        {done && (
          <div style={{ display:"flex", gap:10 }}>
            <button style={{ ...S.btn, background:CM_COL, flex:1, justifyContent:"center" }} onClick={onDone}>View My Campaigns →</button>
            <button style={{ ...S.btnGhost }} onClick={()=>{ setPhase("pick"); setSelected(null); setAnswers({}); }}>Launch Another</button>
          </div>
        )}
      </div>
    );
  }

  if (phase === "ask" && selected) {
    return (
      <div style={{ padding:"32px 28px", maxWidth:520, margin:"0 auto" }}>
        <button onClick={()=>setPhase("pick")} style={{ background:"none", border:"none", color:C.accent, fontSize:13, cursor:"pointer", fontFamily:"inherit", fontWeight:600, marginBottom:20, padding:0 }}>← Back</button>
        <div style={{ fontSize:28, marginBottom:12 }}>{selected.icon}</div>
        <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:26, color:C.text, marginBottom:6 }}>{selected.title}</div>
        <div style={{ fontSize:13, color:C.t2, marginBottom:28 }}>{selected.subtitle}</div>
        <div style={{ display:"flex", flexDirection:"column", gap:16, marginBottom:28 }}>
          {selected.questions.map(q => (
            <div key={q.key}>
              <label style={{ ...S.label, fontSize:13 }}>{q.label}</label>
              <input style={{ ...S.input, fontSize:14 }} value={answers[q.key]||""} onChange={setAns(q.key)} placeholder={q.placeholder} />
            </div>
          ))}
        </div>
        <div style={{ ...S.card, padding:"14px 16px", marginBottom:24, background:`${selected.color}08`, borderColor:`${selected.color}30` }}>
          <div style={{ fontSize:12, color:C.t2, marginBottom:8, fontWeight:500 }}>Will launch across {selected.channels.length} channels:</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {selected.channels.map(ch => (
              <span key={ch} style={{ fontSize:11, padding:"2px 8px", borderRadius:10, background:`${selected.color}15`, color:selected.color, fontWeight:600, textTransform:"capitalize" }}>{ch.replace(/_/g," ")}</span>
            ))}
          </div>
        </div>
        <button style={{ ...S.btn, background:selected.color, width:"100%", justifyContent:"center", fontSize:15, padding:"14px" }} onClick={startCampaign}>
          ⚡ Launch Now
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth:680, margin:"0 auto" }}>
      {/* Tabs */}
      <div style={{ display:"flex", borderBottom:`1px solid ${C.border}`, marginBottom:28, padding:"0 28px" }}>
        {[["launch","⚡ Launch Campaign"],["mine","My Campaigns"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{ padding:"16px 20px", border:"none", background:"transparent", color:tab===id?C.text:C.t2, fontSize:14, fontWeight:tab===id?600:400, cursor:"pointer", fontFamily:"inherit", borderBottom:`2px solid ${tab===id?CM_COL:"transparent"}`, marginBottom:-1, transition:"all 0.15s", display:"flex", alignItems:"center", gap:6 }}>
            {label}
            {id==="mine"&&campaigns.length>0&&<span style={{ fontSize:10, background:C.s3, color:C.t3, borderRadius:8, padding:"1px 6px" }}>{campaigns.length}</span>}
          </button>
        ))}
      </div>

      {tab === "launch" && (
        <div style={{ padding:"0 28px 32px" }}>
          <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:26, color:C.text, marginBottom:6 }}>What do you want to achieve?</div>
          <div style={{ fontSize:14, color:C.t2, marginBottom:24 }}>Pick a goal. We handle the rest — ads, social, email, and more.</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:14 }}>
            {outcomes.map(o => (
              <button key={o.id} onClick={()=>{ setSelected(o); setAnswers({}); setPhase("ask"); }}
                style={{ ...S.card, padding:"22px 20px", textAlign:"left", cursor:"pointer", border:`2px solid ${C.border}`, background:C.s1, transition:"all 0.15s", position:"relative", overflow:"hidden" }}
                onMouseOver={e=>{e.currentTarget.style.borderColor=o.color+"80";e.currentTarget.style.background=o.color+"0a";}}
                onMouseOut={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.background=C.s1;}}>
                <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:o.color }} />
                <div style={{ fontSize:28, marginBottom:10, marginTop:4 }}>{o.icon}</div>
                <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:5 }}>{o.title}</div>
                <div style={{ fontSize:12, color:C.t2, lineHeight:1.55, marginBottom:12 }}>{o.subtitle}</div>
                <div style={{ fontSize:11, color:o.color, fontWeight:600 }}>{o.channels.length} channels · ~2 min →</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {tab === "mine" && (
        <div style={{ padding:"0 28px 32px" }}>
          {campaigns.length === 0 ? (
            <div style={{ textAlign:"center", padding:48, color:C.t2 }}>
              <div style={{ fontSize:32, marginBottom:12 }}>⚡</div>
              <div style={{ fontSize:15, fontWeight:600, marginBottom:6 }}>No campaigns yet</div>
              <div style={{ fontSize:13, color:C.t3, marginBottom:20 }}>Launch your first campaign to get started</div>
              <button onClick={()=>setTab("launch")} style={{ ...S.btn }}>Launch a Campaign</button>
            </div>
          ) : campaigns.map(c => (
            <div key={c.id} style={{ ...S.card, padding:"16px 18px", marginBottom:10, display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ width:10, height:10, borderRadius:"50%", background:c.status==="active"?C.success:C.t3, flexShrink:0, boxShadow:c.status==="active"?`0 0 6px ${C.success}80`:"none" }} />
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:600, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.name}</div>
                <div style={{ fontSize:12, color:C.t2, marginTop:2 }}>{c.runs?.length||0} run{c.runs?.length!==1?"s":""} · {c.goal} · {c.created}</div>
              </div>
              <span style={{ fontSize:11, padding:"3px 10px", borderRadius:6, background:c.status==="active"?`${C.success}15`:C.s3, color:c.status==="active"?C.success:C.t3, fontWeight:600, textTransform:"capitalize" }}>{c.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Default Mode: Messaging Inbox ─────────────────────────────────────────────
const INBOX_CHANNEL_ICONS = { facebook:"💬", instagram:"📸", sms:"📱", google:"G", email:"✉" };
const INBOX_CHANNEL_COLORS = { facebook:"#1877f2", instagram:"#e1306c", sms:"#10b981", google:"#4285f4", email:"#8b5cf6" };

function MessagingInbox() {
  const [threads, setThreads] = useState([]);
  const [active, setActive]   = useState(null);
  const [reply, setReply]     = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [filter, setFilter]   = useState("all");

  useEffect(() => {
    store.get("inbox-threads").then(d => {
      if (d?.length) { setThreads(d); return; }
      // Seed mock data
      const mock = [
        { id:1, channel:"facebook", contactName:"Sarah M.",    initials:"SM", lastMessage:"Hi, do you have availability this Saturday?", timestamp:"2m ago",  unread:true,  status:"new",      messages:[{from:"them",text:"Hi, do you have availability this Saturday?",time:"2m ago"}] },
        { id:2, channel:"instagram", contactName:"James K.",   initials:"JK", lastMessage:"Love your food! Can I book a table for 6?", timestamp:"18m ago", unread:true,  status:"lead",     messages:[{from:"them",text:"Love your food! Can I book a table for 6?",time:"18m ago"}] },
        { id:3, channel:"sms",       contactName:"+1 404 555 0192", initials:"?", lastMessage:"Confirming my 7pm reservation for tonight", timestamp:"1h ago",  unread:false, status:"handled",  messages:[{from:"them",text:"Confirming my 7pm reservation for tonight",time:"1h ago"},{from:"us",text:"Confirmed! See you tonight at 7pm. Table for 2 is ready.",time:"55m ago"}] },
        { id:4, channel:"facebook", contactName:"Maria R.",    initials:"MR", lastMessage:"What are your hours on Sunday?",             timestamp:"3h ago",  unread:false, status:"handled",  messages:[{from:"them",text:"What are your hours on Sunday?",time:"3h ago"},{from:"us",text:"We're open Sunday 11am–9pm. Hope to see you!",time:"2h ago"}] },
        { id:5, channel:"google",   contactName:"Tom B.",      initials:"TB", lastMessage:"Is the parking lot free?",                  timestamp:"5h ago",  unread:false, status:"handled",  messages:[{from:"them",text:"Is the parking lot free?",time:"5h ago"},{from:"us",text:"Yes, free parking in our lot! See you soon.",time:"4h ago"}] },
      ];
      setThreads(mock);
      store.set("inbox-threads", mock);
    });
  }, []);

  const persist = async (updated) => { setThreads(updated); await store.set("inbox-threads", updated); };

  const openThread = (t) => {
    setActive(t);
    setReply("");
    if (t.unread) {
      const updated = threads.map(x => x.id === t.id ? { ...x, unread:false } : x);
      persist(updated);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    const msg = { from:"us", text:reply.trim(), time:"Just now" };
    const updated = threads.map(t => t.id === active.id ? { ...t, lastMessage:reply.trim(), timestamp:"Just now", status:"handled", messages:[...t.messages, msg] } : t);
    setActive(prev => ({ ...prev, messages:[...prev.messages, msg], status:"handled" }));
    persist(updated);
    setReply("");
  };

  const draftAIReply = async () => {
    if (!active) return;
    setAiLoading(true);
    const lastMsg = active.messages.filter(m => m.from==="them").slice(-1)[0]?.text || "";
    try {
      const draft = await ai(`Draft a friendly, professional reply to this customer message: "${lastMsg}"\nBusiness context: local business owner. Keep it short (1-2 sentences), warm, and helpful.`, "You are a helpful customer service assistant for a local business.");
      setReply(draft.trim());
    } catch {}
    setAiLoading(false);
  };

  const markStatus = (id, status) => {
    const updated = threads.map(t => t.id === id ? { ...t, status } : t);
    persist(updated);
    if (active?.id === id) setActive(prev => ({ ...prev, status }));
  };

  const filtered = filter === "all" ? threads : threads.filter(t => t.channel === filter || t.status === filter);
  const unreadCount = threads.filter(t => t.unread).length;

  const STATUS_COLORS = { new:`${CM_COL}`, lead:C.success, handled:C.t3 };

  return (
    <div style={{ display:"flex", height:"100%", minHeight:500 }}>
      {/* Thread list */}
      <div style={{ width:active?260:"100%", maxWidth:active?260:640, borderRight:active?`1px solid ${C.border}`:"none", display:"flex", flexDirection:"column" }}>
        <div style={{ padding:"16px 16px 12px", borderBottom:`1px solid ${C.border}` }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
            <div style={{ fontSize:16, fontWeight:700, color:C.text }}>Inbox {unreadCount>0 && <span style={{ fontSize:11, background:GM, color:"#fff", borderRadius:10, padding:"1px 7px", marginLeft:6 }}>{unreadCount}</span>}</div>
          </div>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {[["all","All"],["new","New"],["lead","Leads"],["facebook","FB"],["instagram","IG"],["sms","SMS"]].map(([val,lbl])=>(
              <button key={val} onClick={()=>setFilter(val)} style={{ fontSize:10, padding:"3px 9px", borderRadius:10, border:`1px solid ${filter===val?GM:C.border}`, background:filter===val?`${GM}18`:"transparent", color:filter===val?GM:C.t3, cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>{lbl}</button>
            ))}
          </div>
        </div>
        <div style={{ flex:1, overflowY:"auto" }}>
          {filtered.length === 0 ? (
            <div style={{ padding:32, textAlign:"center", color:C.t2, fontSize:13 }}>No messages yet.<br/>Connect your channels in Settings.</div>
          ) : filtered.map(t => (
            <button key={t.id} onClick={()=>openThread(t)} style={{ width:"100%", padding:"14px 16px", background:active?.id===t.id?C.s3:"transparent", border:"none", borderBottom:`1px solid ${C.border}20`, cursor:"pointer", textAlign:"left", fontFamily:"inherit", transition:"background 0.1s" }}
              onMouseOver={e=>{if(active?.id!==t.id)e.currentTarget.style.background=C.s2;}}
              onMouseOut={e=>{if(active?.id!==t.id)e.currentTarget.style.background="transparent";}}>
              <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                <div style={{ width:36, height:36, borderRadius:"50%", background:`${INBOX_CHANNEL_COLORS[t.channel]}20`, border:`2px solid ${INBOX_CHANNEL_COLORS[t.channel]}40`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:INBOX_CHANNEL_COLORS[t.channel], flexShrink:0 }}>{t.initials}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:3 }}>
                    <span style={{ fontSize:13, fontWeight:t.unread?700:600, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:120 }}>{t.contactName}</span>
                    <span style={{ fontSize:10, color:C.t4, flexShrink:0 }}>{t.timestamp}</span>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                    <span style={{ fontSize:11 }}>{INBOX_CHANNEL_ICONS[t.channel]}</span>
                    <span style={{ fontSize:12, color:t.unread?C.t2:C.t3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontWeight:t.unread?600:400 }}>{t.lastMessage}</span>
                    {t.unread && <div style={{ width:7, height:7, borderRadius:"50%", background:GM, flexShrink:0, marginLeft:"auto" }} />}
                  </div>
                  <span style={{ fontSize:10, padding:"1px 6px", borderRadius:6, background:`${STATUS_COLORS[t.status]}18`, color:STATUS_COLORS[t.status], fontWeight:600, textTransform:"capitalize" }}>{t.status}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Thread view */}
      {active && (
        <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0 }}>
          {/* Thread header */}
          <div style={{ padding:"14px 18px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:12 }}>
            <button onClick={()=>setActive(null)} style={{ background:"none", border:"none", color:C.accent, cursor:"pointer", fontSize:16, padding:"2px 6px", lineHeight:1 }}>←</button>
            <div style={{ width:32, height:32, borderRadius:"50%", background:`${INBOX_CHANNEL_COLORS[active.channel]}20`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:INBOX_CHANNEL_COLORS[active.channel] }}>{active.initials}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{active.contactName}</div>
              <div style={{ fontSize:11, color:INBOX_CHANNEL_COLORS[active.channel] }}>{INBOX_CHANNEL_ICONS[active.channel]} {active.channel}</div>
            </div>
            <div style={{ display:"flex", gap:6 }}>
              {["new","lead","handled"].map(s => (
                <button key={s} onClick={()=>markStatus(active.id,s)} style={{ fontSize:10, padding:"3px 9px", borderRadius:6, border:`1px solid ${active.status===s?STATUS_COLORS[s]:C.border}`, background:active.status===s?`${STATUS_COLORS[s]}18`:"transparent", color:active.status===s?STATUS_COLORS[s]:C.t3, cursor:"pointer", fontFamily:"inherit", fontWeight:600, textTransform:"capitalize" }}>{s}</button>
              ))}
            </div>
          </div>
          {/* Messages */}
          <div style={{ flex:1, overflowY:"auto", padding:"18px 20px", display:"flex", flexDirection:"column", gap:10 }}>
            {active.messages.map((m,i) => (
              <div key={i} style={{ display:"flex", justifyContent:m.from==="us"?"flex-end":"flex-start" }}>
                <div style={{ maxWidth:"75%", padding:"9px 13px", borderRadius:m.from==="us"?"14px 14px 3px 14px":"3px 14px 14px 14px", background:m.from==="us"?C.accent:C.s2, color:m.from==="us"?"#fff":C.text, fontSize:13, lineHeight:1.55 }}>
                  {m.text}
                  <div style={{ fontSize:10, opacity:0.6, marginTop:4, textAlign:m.from==="us"?"right":"left" }}>{m.time}</div>
                </div>
              </div>
            ))}
          </div>
          {/* Reply box */}
          <div style={{ padding:"12px 16px", borderTop:`1px solid ${C.border}`, display:"flex", flexDirection:"column", gap:8 }}>
            <textarea value={reply} onChange={e=>setReply(e.target.value)} placeholder="Write a reply…"
              style={{ ...S.input, height:72, resize:"none", fontSize:13 }}
              onKeyDown={e=>{if(e.key==="Enter"&&(e.metaKey||e.ctrlKey))sendReply();}} />
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <button onClick={draftAIReply} disabled={aiLoading} style={{ ...S.btnGhost, fontSize:12, padding:"6px 12px", display:"flex", alignItems:"center", gap:5 }}>
                {aiLoading?<><span style={{ display:"inline-block", animation:"spin 0.8s linear infinite" }}>◌</span> Drafting…</>:<><span style={{ color:C.accent }}>✦</span> AI Draft</>}
              </button>
              <button onClick={sendReply} disabled={!reply.trim()} style={{ ...S.btn, fontSize:12, padding:"6px 16px", opacity:reply.trim()?1:0.5 }}>Send</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Default Mode: Customers ───────────────────────────────────────────────────
function DefaultCustomers({ profile }) {
  const [tab, setTab]           = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [search, setSearch]     = useState("");

  useEffect(() => {
    store.get("crm-contacts").then(d => { if (d) setContacts(d); });
    store.get("reservations").then(d => { if (d) setReservations(d); });
  }, []);

  const filteredContacts = contacts.filter(c =>
    !search || `${c.name} ${c.email} ${c.phone}`.toLowerCase().includes(search.toLowerCase())
  );
  const filteredRes = reservations.filter(r =>
    !search || `${r.name} ${r.email} ${r.phone}`.toLowerCase().includes(search.toLowerCase())
  );

  const TABS = [
    { id:"contacts",     label:"Contacts",   count:contacts.length },
    { id:"bookings",     label:"Bookings",   count:reservations.length },
    { id:"inbox",        label:"Inbox",      count:null },
  ];

  return (
    <div style={{ padding:"28px 28px 0", maxWidth:760, margin:"0 auto" }}>
      <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:26, color:C.text, marginBottom:20 }}>Customers</div>
      {/* Search */}
      <input style={{ ...S.input, marginBottom:20, fontSize:13 }} value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search customers…" />
      {/* Tabs */}
      <div style={{ display:"flex", gap:0, borderBottom:`1px solid ${C.border}`, marginBottom:20 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)} style={{ padding:"10px 20px", border:"none", background:"transparent", color:tab===t.id?C.text:C.t2, fontSize:13, fontWeight:tab===t.id?700:400, cursor:"pointer", fontFamily:"inherit", borderBottom:`2px solid ${tab===t.id?C.accent:"transparent"}`, marginBottom:-1, transition:"all 0.15s", display:"flex", alignItems:"center", gap:6 }}>
            {t.label}{t.count!=null&&t.count>0&&<span style={{ fontSize:10, background:C.s3, color:C.t3, borderRadius:8, padding:"1px 6px" }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === "contacts" && (
        <div>
          {filteredContacts.length === 0 ? (
            <div style={{ textAlign:"center", padding:48, color:C.t2, fontSize:13 }}>No contacts yet. They'll appear here from bookings and campaigns.</div>
          ) : filteredContacts.map(c => (
            <div key={c.id} style={{ ...S.card, padding:"14px 18px", marginBottom:10, display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ width:38, height:38, borderRadius:"50%", background:`${C.accent}20`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:700, color:C.accent, flexShrink:0 }}>{(c.name||"?")[0].toUpperCase()}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:600, color:C.text }}>{c.name}</div>
                <div style={{ fontSize:12, color:C.t2 }}>{[c.email,c.phone].filter(Boolean).join(" · ")}</div>
              </div>
              {c.tags?.length>0 && <div style={{ display:"flex", gap:4 }}>{c.tags.slice(0,2).map(tag=><span key={tag} style={{ fontSize:10, padding:"2px 7px", borderRadius:8, background:`${C.accent}15`, color:C.accent }}>{tag}</span>)}</div>}
            </div>
          ))}
        </div>
      )}

      {tab === "bookings" && (
        <div>
          {filteredRes.length === 0 ? (
            <div style={{ textAlign:"center", padding:48, color:C.t2, fontSize:13 }}>No bookings yet.</div>
          ) : filteredRes.map(r => (
            <div key={r.id} style={{ ...S.card, padding:"14px 18px", marginBottom:10, display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ width:38, height:38, borderRadius:"50%", background:`${C.success}20`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:700, color:C.success, flexShrink:0 }}>📅</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:600, color:C.text }}>{r.name}</div>
                <div style={{ fontSize:12, color:C.t2 }}>{[r.date,r.time,r.guests&&`${r.guests} guests`].filter(Boolean).join(" · ")}</div>
              </div>
              <span style={{ fontSize:11, padding:"3px 10px", borderRadius:8, background:r.status==="confirmed"?`${C.success}18`:C.s3, color:r.status==="confirmed"?C.success:C.t3, fontWeight:600, textTransform:"capitalize" }}>{r.status||"pending"}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "inbox" && <MessagingInbox />}
    </div>
  );
}

// ── Default Mode: Settings ────────────────────────────────────────────────────
function DefaultSettings({ profile, onSwitchAdvanced, onEditProfile, authUser, onSignOut }) {
  return (
    <div style={{ padding:"32px 28px", maxWidth:560, margin:"0 auto" }}>
      <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:26, color:C.text, marginBottom:28 }}>Settings</div>

      {/* Business profile */}
      <div style={{ ...S.card, padding:20, marginBottom:16 }}>
        <div style={{ fontSize:11, fontWeight:700, color:C.t2, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:14 }}>Your Business</div>
        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:14 }}>
          <div style={{ width:44, height:44, borderRadius:10, background:`${C.accent}20`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>
            {NICHES.find(n=>n.id===profile?.niche)?.icon||"🏢"}
          </div>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:C.text }}>{profile?.name||"Your Business"}</div>
            <div style={{ fontSize:12, color:C.t2 }}>{profile?.category||""}{profile?.city?` · ${profile.city}`:""}</div>
          </div>
        </div>
        <button onClick={onEditProfile} style={{ ...S.btnGhost, fontSize:12, width:"100%", justifyContent:"center" }}>Edit Business Profile</button>
      </div>

      {/* Account */}
      {authUser && (
        <div style={{ ...S.card, padding:20, marginBottom:16 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.t2, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:14 }}>Account</div>
          <div style={{ fontSize:13, color:C.t2, marginBottom:14 }}>{authUser.email}</div>
          <button onClick={onSignOut} style={{ ...S.btnGhost, fontSize:12 }}>Sign Out</button>
        </div>
      )}

      {/* Advanced Mode */}
      <div style={{ ...S.card, padding:20, background:`${CM_COL}08`, borderColor:`${CM_COL}30`, marginBottom:16 }}>
        <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:6 }}>Advanced Controls</div>
        <div style={{ fontSize:12, color:C.t2, marginBottom:14, lineHeight:1.6 }}>Access the full marketing suite — ads manager, SEO tools, content library, social media, and more.</div>
        <button onClick={onSwitchAdvanced} style={{ ...S.btn, background:CM_COL, width:"100%", justifyContent:"center", fontSize:13 }}>
          Switch to Advanced Mode →
        </button>
      </div>
    </div>
  );
}

// ── Default Mode: Shell ───────────────────────────────────────────────────────
// DEFAULT_NAV → src/shared/constants.js

function DefaultShell({ profile, authUser, workspaces, activeWorkspace, onSwitchWorkspace, onAddWorkspace, onSwitchAdvanced, onEditProfile, onSignOut, wlConfig, notifications, showNotifPanel, onToggleNotifPanel, onMarkRead, onMarkAllRead, onNavigateFromNotif }) {
  const isMobile = useIsMobile();
  const [mod, setMod]               = useState("dashboard");
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [showWsPicker, setShowWsPicker] = useState(false);
  const [campaigns, setCampaigns]   = useState([]);
  const [contacts,  setContacts]    = useState([]);
  const [reservations, setReservations] = useState([]);
  const [messages,  setMessages]    = useState([]);

  useEffect(() => {
    store.get("campaigns").then(d => { if (d) setCampaigns(d); });
    store.get("crm-contacts").then(d => { if (d) setContacts(d); });
    store.get("reservations").then(d => { if (d) setReservations(d); });
    store.get("inbox-threads").then(d => { if (d) setMessages(d); });
  }, [mod]);

  const pages = {
    dashboard: <DefaultDashboard profile={profile} activeWorkspace={activeWorkspace} onNav={setMod} onEditProfile={onEditProfile} campaigns={campaigns} contacts={contacts} reservations={reservations} messages={messages} />,
    campaigns: <DefaultCampaigns profile={profile} onDone={()=>{ store.get("campaigns").then(d=>{if(d)setCampaigns(d);}); setMod("campaigns"); }} />,
    customers: <DefaultCustomers profile={profile} />,
    settings:  <DefaultSettings  profile={profile} onSwitchAdvanced={onSwitchAdvanced} onEditProfile={onEditProfile} authUser={authUser} onSignOut={onSignOut} />,
  };

  return (
    <div style={{ display:"flex", height:"100vh", background:C.bg, fontFamily:"'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", color:C.text, overflow:"hidden" }}>
      {/* Mobile nav overlay */}
      {isMobile && showMobileNav && (
        <div onClick={() => setShowMobileNav(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:299 }} />
      )}
      {/* Sidebar */}
      <aside style={{ width:228, background:SB.bg, borderRight:`1px solid ${SB.border}`, display:"flex", flexDirection:"column", flexShrink:0,
        ...(isMobile ? { position:"fixed", top:0, left:0, bottom:0, zIndex:300, transform: showMobileNav ? "translateX(0)" : "translateX(-100%)", transition:"transform 0.25s ease", boxShadow: showMobileNav ? "4px 0 32px rgba(0,0,0,0.6)" : "none" } : {}) }}>
        {/* Logo + close on mobile */}
        <div style={{ padding:"20px 16px 16px", borderBottom:`1px solid ${SB.border}`, display:"flex", alignItems:"center", gap:10 }}>
          {wlConfig?.logo_url
            ? <img src={wlConfig.logo_url} alt={wlConfig.app_name||"Logo"} style={{ height:34, maxWidth:120, objectFit:"contain" }} />
            : <div style={{ width:34, height:34, borderRadius:10, background:wlConfig?.primary_color||C.accent, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, color:"#fff", fontWeight:700, flexShrink:0 }}>
                {(wlConfig?.app_name||"K").charAt(0).toUpperCase()}
              </div>
          }
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:16, fontWeight:700, color:SB.text, letterSpacing:"0.01em", lineHeight:1.2 }}>{wlConfig?.app_name||"Kurb AI"}</div>
            <div style={{ fontSize:10, color:SB.t2, marginTop:2 }}>{wlConfig?.tagline||"Marketing Autopilot"}</div>
          </div>
          {isMobile && (
            <button onClick={() => setShowMobileNav(false)}
              style={{ background:"none", border:"none", color:SB.t2, fontSize:20, cursor:"pointer", padding:"4px 8px", lineHeight:1 }}>✕</button>
          )}
        </div>
          {/* Workspace switcher */}
          <div style={{ margin:"12px 14px 4px", position:"relative" }}>
            <button onClick={()=>setShowWsPicker(v=>!v)} style={{ width:"100%", padding:"8px 10px", background:SB.s2, borderRadius:8, border:`1px solid ${showWsPicker?C.accent:SB.border}`, cursor:"pointer", fontFamily:"inherit", textAlign:"left", display:"flex", alignItems:"center", gap:8, transition:"border-color 0.15s" }}>
              <div style={{ width:26, height:26, borderRadius:6, background:`${C.accent}25`, border:`1px solid ${C.accent}40`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, flexShrink:0 }}>
                {NICHES.find(n=>n.id===profile?.niche)?.icon||"🏢"}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:11, color:SB.t2, marginBottom:1 }}>Active business</div>
                <div style={{ fontSize:12, fontWeight:600, color:SB.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{activeWorkspace?.name||profile?.name||"My Business"}</div>
              </div>
              <span style={{ fontSize:9, color:SB.t3, transform:showWsPicker?"rotate(180deg)":"rotate(0deg)", transition:"transform 0.2s", flexShrink:0 }}>▲</span>
            </button>
            {showWsPicker && (
              <>
                <div style={{ position:"fixed", inset:0, zIndex:198 }} onClick={()=>setShowWsPicker(false)} />
                <WorkspacePicker
                  workspaces={workspaces||[]}
                  activeId={activeWorkspace?.id}
                  onSelect={async (ws) => { setShowWsPicker(false); await onSwitchWorkspace(ws); }}
                  onAdd={async () => { setShowWsPicker(false); await onAddWorkspace(); }}
                />
              </>
            )}
          </div>
          <nav style={{ flex:1, padding:"6px 0", overflowY:"auto" }}>
            {DEFAULT_NAV.map(m => {
              const active = mod === m.id;
              return (
                <button key={m.id} onClick={()=>{ setMod(m.id); setShowMobileNav(false); }} style={{ display:"flex", alignItems:"center", gap:10, width:"calc(100% - 16px)", margin:"1px 8px", padding:"9px 10px", border:"none", borderRadius:6, background:active?"rgba(255,255,255,0.1)":"transparent", color:SB.text, fontSize:14, fontFamily:"inherit", cursor:"pointer", textAlign:"left", transition:"all 0.12s" }}
                  onMouseOver={e=>{ if(!active){e.currentTarget.style.background="rgba(255,255,255,0.06)";e.currentTarget.style.color=SB.text;} }}
                  onMouseOut={e=>{ if(!active){e.currentTarget.style.background="transparent";e.currentTarget.style.color=SB.text;} }}>
                  <span style={{ fontSize:14, opacity:active?1:0.7, width:18, display:"inline-flex", justifyContent:"center", alignItems:"center", flexShrink:0 }}>{m.icon}</span>
                  <span style={{ fontWeight:active?600:400, flex:1 }}>{m.label}</span>
                </button>
              );
            })}
          </nav>
          {!wlConfig?.hide_powered_by && (
            <div style={{ padding:"12px 18px", borderTop:`1px solid ${SB.border}` }}>
              <span style={{ fontSize:10, color:SB.t3 }}>Powered by <span style={{ color:C.accent }}>Kurb AI</span></span>
            </div>
          )}
      </aside>

      {/* Right column: TopBar + content */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
        <TopBar
          profile={profile}
          authUser={authUser}
          userRole="owner"
          userMode="default"
          showAIPanel={showAIPanel}
          onToggleAI={()=>setShowAIPanel(v=>!v)}
          onNavigate={setMod}
          onNotifNavigate={onNavigateFromNotif}
          onSignOut={onSignOut}
          onEditProfile={onEditProfile}
          onSwitchMode={onSwitchAdvanced}
          notifications={notifications}
          showNotifPanel={showNotifPanel}
          onToggleNotifPanel={onToggleNotifPanel}
          onMarkRead={onMarkRead}
          onMarkAllRead={onMarkAllRead}
          onToggleMobileNav={() => setShowMobileNav(v => !v)}
        />
        <div style={{ flex:1, display:"flex", overflow:"hidden" }}>
          <main style={{ flex:1, overflowY:mod==="website"?"hidden":"auto", overflowX:"hidden", display:"flex", flexDirection:"column" }}>
            <div style={(mod==="website" || mod==="call-tracking" || mod==="content-engine" || mod==="conversion") ? { flex:1, display:"flex", flexDirection:"column", overflow:"hidden" } : (mod==="crm" || mod==="lead-agent" || mod==="seo") ? { padding: isMobile ? "16px 12px" : "32px 40px", width:"100%" } : { maxWidth:1100, padding: isMobile ? "20px 16px" : "40px 48px" }}>
              <React.Suspense fallback={<div style={{ padding:40, color:C.t3 }}>Loading…</div>}>
                {pages[mod] || pages.dashboard}
              </React.Suspense>
            </div>
          </main>
          <div style={{ display:showAIPanel?"flex":"none", flexShrink:0 }}>
            <AIAssistant key={activeWorkspace?.id || "default"} workspaceId={activeWorkspace?.id} profile={profile} mod={mod} userMode="default" onNavigate={setMod} onClose={()=>setShowAIPanel(false)} wlConfig={wlConfig} />
          </div>
        </div>
      </div>
    </div>
  );
}


/* ─── AUTHORITY AGENT ────────────────────────────────────────────────── */
/* ─── AUTHORITY AGENT → src/modules/authority.jsx (lazy-loaded) ─── */

export default function App() {
  const [mod, setMod]                     = useState("dashboard");
  const [ceTab, setCeTab]                 = useState("dashboard");
  const [cePlanCount, setCePlanCount]     = useState(0);
  const [backContext, setBackContext]     = useState(null); // { mod, label, seoTab? }
  const [refreshKey, setRefreshKey]       = useState(0);

  // ── Universal scroll preservation ────────────────────────────────────────
  // Reads scrollTop from the DOM BEFORE this render's mutations (pre-commit),
  // then restores it in useLayoutEffect (post-commit, pre-paint), so any
  // state update within a module never causes a scroll-to-top.
  const mainRef    = useRef(null);
  const _scrollMod = useRef("dashboard");
  const _scrollWs  = useRef(null);
  // Capture current scrollTop right now — before React mutates the DOM
  const _preRenderScroll = mainRef.current?.scrollTop ?? 0;
  useLayoutEffect(() => {
    if (!mainRef.current) return;
    const wsId = activeWorkspace?.id ?? null;
    if (_scrollMod.current === mod && _scrollWs.current === wsId) {
      // Same module + same workspace → preserve position
      mainRef.current.scrollTop = _preRenderScroll;
    } else {
      // Module or workspace changed → let browser default (0), just update trackers
      _scrollMod.current = mod;
      _scrollWs.current  = wsId;
    }
  });
  const [userMode, setUserMode]           = useState("default"); // "default" | "advanced"
  const [profile, setProfile]             = useState(null);
  const [userRole, setUserRole]           = useState("owner");
  const [rolePerms, setRolePerms]         = useState(null); // null = use defaults
  const [workspaces, setWorkspaces]       = useState([]);
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  // Mirror activeWorkspace.id to a window global so non-React code paths (event
  // handlers, fetch URLs assembled inside modules) can read the current workspace
  // synchronously. Updated automatically whenever activeWorkspace changes — fixes
  // stale-global bug when super admin enters a client workspace via Plan Dashboard.
  // Also drops the store's in-memory cache so reads after a workspace switch
  // don't return stale data from the previous workspace.
  const lastWsIdRef = useRef(null);
  useEffect(() => {
    window.__activeWorkspaceId = activeWorkspace?.id || null;
    if (lastWsIdRef.current && lastWsIdRef.current !== activeWorkspace?.id) {
      store.clearCache?.();
    }
    lastWsIdRef.current = activeWorkspace?.id || null;
  }, [activeWorkspace?.id]);
  const isContentAgentPlan = activeWorkspace?.product_kind === "content-agent";
  const [isSuperAdmin, setIsSuperAdmin]   = useState(false);
  const [isAgencyAdmin, setIsAgencyAdmin] = useState(false);
  const [adminMode, setAdminMode]         = useState(false); // true = in admin shell
  const [workspacePlan, setWorkspacePlan] = useState("pro");
  const [wlConfig, setWlConfig]           = useState(null); // white-label branding config
  const [wlConfigChecked, setWlConfigChecked] = useState(false);
  // Version check — polls /api/version every 60s, shows reload toast on SHA mismatch
  const [newVersionSha, setNewVersionSha]   = useState(null);
  const [dismissedSha, setDismissedSha]     = useState(null);
  // Detect /signup/X path — agent subdomain encoded by server to survive Supabase's SITE_URL redirect.
  // In production, server/index.js serves a pure-HTML redirect for /signup/:agent that sends the user
  // to ca.getkurb.ai BEFORE the React app (and Supabase client) loads, preserving the auth hash tokens.
  // This path is only reached in local dev where Vite intercepts before Express.
  const [signupAgent] = useState(() => {
    const m = window.location.pathname.match(/^\/signup\/(\w+)/);
    if (m) { window.history.replaceState({}, "", "/" + window.location.hash); return m[1]; }
    return null;
  });
  const [imgQualityDefault, setImgQualityDefault] = useState("standard"); // from system_settings
  const [showWorkspacePicker, setShowWorkspacePicker] = useState(false);
  const [showAddWebsite, setShowAddWebsite]           = useState(false);
  const [showAIPanel, setShowAIPanel]                 = useState(false);
  const [notifications, setNotifications]             = useState([]);
  const [showNotifPanel, setShowNotifPanel]           = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [inviteWelcome, setInviteWelcome]   = useState(null); // invite object when new invited user signs in
  // Reusable invite token — present in URL when user clicks invite email link
  const [inviteToken] = useState(() => new URLSearchParams(window.location.search).get("invite_token") || null);
  const [inviteVerifying, setInviteVerifying] = useState(false);
  const [inviteTokenError, setInviteTokenError] = useState(null);
  // Detect expired/invalid magic link in URL hash (Supabase sets #error=access_denied&error_code=otp_expired)
  const [otpError, setOtpError] = useState(() => {
    const h = window.location.hash;
    if (h.includes("error_code=otp_expired") || h.includes("error_code=otp_disabled") || (h.includes("access_denied") && h.includes("error"))) {
      return decodeURIComponent(h.match(/error_description=([^&]*)/)?.[1]?.replace(/\+/g, " ") || "Your invite link has expired or is invalid.");
    }
    return null;
  });
  const [ready, setReady]                 = useState(false);
  const [authUser, setAuthUser]           = useState(null);
  const [authChecked, setAuthChecked]     = useState(!supabase);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [isMobile, setIsMobile]           = useState(() => window.innerWidth < 768);
  const [showMobileNav, setShowMobileNav] = useState(false);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Custom domain / subdomain branding — detect hostname and apply white-label config before auth
  useEffect(() => {
    if (signupAgent) {
      // signupAgent is synchronous — we already know the agent, load config in background
      getWhiteLabelConfigBySubdomain(signupAgent).then(cfg => { if (cfg) setWlConfig(cfg); setWlConfigChecked(true); });
      return;
    }
    const hostname = window.location.hostname;
    const ownDomains = ["localhost", "127.0.0.1", "app.getkurb.ai", "kurbai.vercel.app"];
    if (ownDomains.includes(hostname) || hostname.endsWith(".vercel.app")) {
      setWlConfigChecked(true); // own domain — no wlConfig lookup needed
      return;
    }
    if (hostname.endsWith(".getkurb.ai")) {
      const subdomain = hostname.replace(".getkurb.ai", "");
      getWhiteLabelConfigBySubdomain(subdomain).then(cfg => { if (cfg) setWlConfig(cfg); setWlConfigChecked(true); });
      return;
    }
    getWhiteLabelConfigByDomain(hostname).then(cfg => { if (cfg) setWlConfig(cfg); setWlConfigChecked(true); });
  }, []);

  // Load system-level settings (image quality default, etc.)
  useEffect(() => {
    fetch("/api/admin/system-settings").then(r=>r.json())
      .then(d=>{ if (d.image_quality_default) setImgQualityDefault(d.image_quality_default); })
      .catch(()=>{});
  }, []);

  // Cross-module navigation via custom event. Must sit ABOVE all conditional
  // early returns so React's Rules of Hooks are obeyed across renders. The
  // handler inlines navTo's logic (setMod + setBackContext + setShowMobileNav
  // equivalents) because navTo itself is only defined later in this function
  // body, after the early-return gates.
  useEffect(() => {
    const handler = e => {
      const { mod: destMod, from, label } = e.detail || {};
      if (!destMod) return;
      if (from && label) setBackContext({ mod: from, label });
      else setBackContext(null);
      setMod(destMod);
    };
    window.addEventListener("navigate-to", handler);
    return () => window.removeEventListener("navigate-to", handler);
  }, []);

  // Auth state listener + initial session check
  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      setAuthUser(session?.user || null);
      setAuthChecked(true);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setAuthUser(session?.user || null);
      if (event === "PASSWORD_RECOVERY") setShowPasswordReset(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const handler = () => setShowOnboarding(true);
    window.addEventListener("edit-profile", handler);
    return () => window.removeEventListener("edit-profile", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const t = e.detail?.tab;
      if (t) setCeTab(t);
      setMod("content-engine");
    };
    window.addEventListener("content-agent-goto-tab", handler);
    return () => window.removeEventListener("content-agent-goto-tab", handler);
  }, []);

  // If invite_token in URL and user not signed in: get fresh magic link and redirect
  useEffect(() => {
    if (!inviteToken || !authChecked) return;
    if (authUser) {
      // Already signed in — clean URL and let normal accept flow handle it
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    setInviteVerifying(true);
    fetch(`/api/team/verify-invite?token=${encodeURIComponent(inviteToken)}&appBase=${encodeURIComponent(window.location.origin)}`)
      .then(r => r.json())
      .then(d => {
        if (d.expired) { setInviteTokenError("This invite link has already been used or has expired."); return; }
        if (d.error)   { setInviteTokenError(d.error); return; }
        window.history.replaceState({}, "", window.location.pathname);
        window.location.href = d.magicLinkUrl;
      })
      .catch(() => setInviteTokenError("Could not verify invite. Please try again or ask for a new invite."))
      .finally(() => setInviteVerifying(false));
  }, [inviteToken, authChecked, authUser]);

  useEffect(() => {
    const handler = () => getWhiteLabelConfig().then(cfg => setWlConfig(cfg || null));
    window.addEventListener("whitelabel-updated", handler);
    return () => window.removeEventListener("whitelabel-updated", handler);
  }, []);

  // Cross-module navigation via custom event (for modules that don't receive navTo as prop)
  useEffect(() => {
    const handler = e => {
      const { mod: destMod, from, label } = e.detail || {};
      if (!destMod) return;
      navTo(destMod, from && label ? { mod: from, label } : null);
    };
    window.addEventListener("navigate-to", handler);
    return () => window.removeEventListener("navigate-to", handler);
  }, []);

  // Apply white-label branding: document title
  useEffect(() => {
    document.title = wlConfig?.app_name || "Kurb AI";
  }, [wlConfig?.app_name]);

  // Version check — compare the loaded frontend against the SERVED frontend
  // (/version.json, emitted at frontend build), NOT the backend. This way a
  // reload actually fetches the newer frontend and the toast clears in one
  // click; backend deploys (which happen separately) never trigger it.
  useEffect(() => {
    const localSha = typeof __BUILD_SHA__ !== "undefined" ? __BUILD_SHA__ : "dev";
    if (localSha === "dev") return; // skip in local development
    const check = async () => {
      try {
        const r = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) return;
        const d = await r.json();
        const remoteShort = (d.sha || "").slice(0, 8);
        if (remoteShort && remoteShort !== "dev" && remoteShort !== localSha && remoteShort !== dismissedSha) {
          setNewVersionSha(remoteShort);
        }
      } catch {}
    };
    check();
    const id = setInterval(check, 60000);
    return () => clearInterval(id);
  }, [dismissedSha]);

  // Load profile once auth is confirmed and user is present
  useEffect(() => {
    if (!authChecked) return;
    if (supabase && !authUser) return;
    (async () => {
      try {
      // Accept any pending team invite — returns invite if found (workspace_id set)
      const acceptedInvite = authUser ? await acceptPendingInvite(authUser.email) : null;

      // Fetch Supabase profile to get role + active workspace
      if (authUser) {
        const sbProfile = await getProfile(authUser.id);
        if (sbProfile?.role) setUserRole(sbProfile.role);
      }
      // Load workspaces — declare active/superAdmin/agencyAdmin outside the block so later code can access them
      let active = null;
      let superAdmin = false, agencyAdmin = false;
      if (authUser) {
        const [wsList, sa, aa] = await Promise.all([getWorkspaces(), getIsSuperAdmin(), getIsAgencyAdmin()]);
        superAdmin = sa; agencyAdmin = aa;
        // Super admin loads all workspaces so they can switch into any of them
        const allWsList = superAdmin ? await adminGetAllWorkspaces().catch(() => wsList) : wsList;
        // Merge: own workspaces first, then any extra ones from adminGetAllWorkspaces
        const merged = allWsList.length > wsList.length ? allWsList : wsList;
        setWorkspaces(merged);
        setIsSuperAdmin(superAdmin);
        setIsAgencyAdmin(agencyAdmin);
        // Skip auto-entering admin mode if the URL hash indicates we just
        // returned from a workspace-context OAuth flow (Google Search Console,
        // GBP, Google Ads). The user was in a workspace before the redirect;
        // they should land back in that workspace, not bounce to the admin shell.
        const isOAuthCallback = window.location.hash.includes("connected=1");
        if ((superAdmin || agencyAdmin) && !isOAuthCallback) setAdminMode(true);
        // Hash override: an OAuth callback URL may pin a specific workspace
        // (e.g., #seo?ws=BLUEID&tab=index&connected=1). That id wins over
        // active_workspace_id so we always land in the workspace the OAuth
        // flow was initiated from — even if active_workspace_id changed in
        // another tab during the round-trip.
        let wid = null;
        try {
          const h = window.location.hash;
          if (h.includes("connected=1")) {
            const q = h.slice(h.indexOf("?") + 1);
            const wsFromHash = new URLSearchParams(q).get("ws");
            if (wsFromHash && merged.some(w => w.id === wsFromHash)) {
              wid = wsFromHash;
              // Persist so future reloads stay here.
              try { await switchWorkspace(wsFromHash); } catch {}
            }
          }
        } catch {}
        if (!wid) wid = await getWorkspaceId();
        active = merged.find(w => w.id === wid) || merged[0] || null;
        setActiveWorkspace(active);
        window.__activeWorkspaceId = active?.id || null;
        if (active?.plan) setWorkspacePlan(active.plan);
        // Per-workspace role overrides the global profiles.role set above
        // — a user can be owner of one workspace and staff of another.
        if (active?.id) {
          const wsRole = await getMyWorkspaceRole(active.id);
          if (wsRole) setUserRole(wsRole);
        }
        if (active?.white_label_enabled) {
          getWhiteLabelConfig().then(cfg => { if (cfg) setWlConfig(cfg); });
        }
        // OAuth callback routing: hash like #seo?tab=index&connected=1.
        // Parse the mod from the path segment before the `?`, set it, and
        // dispatch tab events (seo-goto-tab etc.) for sub-tab restoration.
        if (isOAuthCallback) {
          // Flag that an OAuth callback just happened. Components like
          // Index Agent read this once on mount and re-fetch oauthStatus
          // so their UI reflects the new connection state.
          window.__oauthCallbackJustHappened = true;
          const h = window.location.hash.replace(/^#/, "");
          const qIdx = h.indexOf("?");
          const targetMod = qIdx === -1 ? h : h.slice(0, qIdx);
          const params = new URLSearchParams(qIdx === -1 ? "" : h.slice(qIdx + 1));
          if (targetMod) setMod(targetMod);
          const subTab = params.get("tab");
          if (subTab && targetMod === "seo") {
            setTimeout(() => window.dispatchEvent(new CustomEvent("seo-goto-tab", { detail: { tab: subTab } })), 100);
          }
          // Clear the hash so a subsequent navigation doesn't re-trigger this.
          window.history.replaceState({}, "", window.location.pathname);
        }
        // WordPress authorize-application callback: browser returned from the
        // site's authorize flow. Credentials were saved server-side — route to the
        // Content Agent Configure tab and let it show the result.
        if (window.location.hash.includes("wpconnect=")) {
          const h = window.location.hash;
          const flag = new URLSearchParams(h.slice(h.indexOf("?") + 1)).get("wpconnect");
          sessionStorage.setItem("ce-open-tab", "settings");
          sessionStorage.setItem("ce-wp-connect", flag || "error");
          setMod("content-engine");
          window.history.replaceState({}, "", window.location.pathname);
        }
      }
      // On agent subdomains: if the workspace doesn't have the custom plan yet,
      // apply the domain template now (handles users who hit errors before onboarding completed).
      const onAgentHost = authUser && active?.id &&
        window.location.hostname.endsWith(".getkurb.ai") &&
        !["app.getkurb.ai", "kurbai.vercel.app"].includes(window.location.hostname);
      if (onAgentHost && active.plan !== "custom") {
        try {
          const tr = await fetch("/api/workspaces/apply-domain-template", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ workspaceId: active.id, hostname: window.location.hostname }),
          });
          const td = await tr.json();
          if (td.applied && td.modules) {
            active = { ...active, plan: "custom", enabled_modules: td.modules };
            setActiveWorkspace(active);
            setWorkspaces(prev => prev.map(w => w.id === active.id ? active : w));
            setWorkspacePlan("custom");
          }
        } catch(e) { console.warn("[agent-template]", e.message); }
      }

      const [done, saved, perms, mode] = await Promise.all([store.get("onboarding-done"), store.get("business-profile"), store.get("role-permissions"), store.get("user-mode")]);
      if (saved)  setProfile(saved);
      if (perms)  setRolePerms(perms);
      if (mode)   setUserMode(mode);
      // Invited users: skip onboarding, show welcome/password screen instead.
      // Check acceptedInvite (not ?.workspace_id) — old invites may have null workspace_id.
      if (acceptedInvite) {
        await store.set("onboarding-done", true);
        // Only show welcome screen for brand-new users (no existing profile)
        if (!saved) setInviteWelcome(acceptedInvite);
      } else if (!done && !saved) {
        // Super/agency admins are operators, not business owners — they should
        // never see the business onboarding wizard, even if they happen to have
        // no business-profile in store for the active workspace.
        if (superAdmin || agencyAdmin) {
          // no-op
        } else if (active?.plan === "custom" && active?.enabled_modules?.length) {
          await store.set("onboarding-done", true);
        } else {
          setShowOnboarding(true);
        }
      }
      // Always enforce advanced mode + correct starting module for custom plan workspaces.
      // This runs on every load so a stored "default" mode value never overrides agent plan.
      if (active?.plan === "custom" && active?.enabled_modules?.length) {
        setUserMode("advanced");
        if (mode !== "advanced") await store.set("user-mode", "advanced");
        if (active.product_kind === "content-agent") {
          setMod("content-engine");
        }
      }
      setReady(true);
      } catch (e) {
        console.error("[app-load]", e);
        setReady(true); // always unblock the render even on error
      }
    })();
  }, [authChecked, authUser?.id]);

  // Notification scan — fires once when workspace is known
  useEffect(() => {
    if (!authChecked || !authUser || !activeWorkspace?.id) return;
    setNotifications([]);
    fetch("/api/notifications/scan", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ workspaceId: activeWorkspace.id }),
    })
      .then(r => r.json())
      .then(d => { if (Array.isArray(d.notifications)) setNotifications(d.notifications); })
      .catch(() => {});
  }, [authChecked, authUser?.id, activeWorkspace?.id]);

  const switchMode = async (m) => { setUserMode(m); await store.set("user-mode", m); };

  const markNotifRead = (id) => {
    if (!activeWorkspace?.id) return;
    setNotifications(ns => ns.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
    fetch("/api/notifications/read", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ workspaceId: activeWorkspace.id, notificationId: id }),
    }).catch(() => {});
  };

  const navToFromNotif = (modId) => {
    if (userMode !== "advanced") switchMode("advanced");
    setMod(modId);
    setShowMobileNav(false);
  };

  const markAllNotifsRead = () => {
    if (!activeWorkspace?.id) return;
    setNotifications(ns => ns.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    fetch("/api/notifications/read-all", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ workspaceId: activeWorkspace.id }),
    }).catch(() => {});
  };

  // Effective image quality: workspace override → global default → 'standard'
  const effectiveImageQuality = activeWorkspace?.image_quality || imgQualityDefault || "standard";

  const handleOnboardingComplete = async (p) => {
    // Detect agent signup BEFORE dismissing onboarding so we can pre-set the module
    // and mode in the same synchronous batch — prevents a flash of the generic dashboard.
    const onAgentHost = window.location.hostname.endsWith(".getkurb.ai") && window.location.hostname !== "app.getkurb.ai";
    const isAgentSignup = !!(signupAgent || wlConfig?.plan_template_id || onAgentHost);
    if (isAgentSignup) {
      setMod("content-engine");
      setUserMode("advanced");
    }
    setProfile(p);
    setShowOnboarding(false);
    if (!profile) {
      const mode = isAgentSignup ? "advanced" : "default";
      setUserMode(mode); await store.set("user-mode", mode);
      // Save profile + mark done so refresh doesn't re-show onboarding
      await store.set("business-profile", p);
      await store.set("onboarding-done", true);
      // Seed business-brain with name + website so Brand Guide and setup wizard have it
      if (p.name || p.website) {
        const existingBrain = await store.get("business-brain") || {};
        await store.set("business-brain", {
          ...existingBrain,
          businessName: p.name || existingBrain.businessName,
          website: { ...(existingBrain.website || {}), url: p.website || existingBrain.website?.url || "" },
        });
      }
    }

    // Use a local ref to track the effective workspace throughout this function
    // (React state updates are async, so activeWorkspace may still be null after setActiveWorkspace)
    let ws = activeWorkspace;

    // Create workspace if none exists yet (new user with no auto-created workspace)
    if (!ws?.id && supabase && !profile) {
      try {
        const created = await createWorkspace(p.name || "My Business", p.category || p.niche || "other");
        clearWorkspaceCache();
        await switchWorkspace(created.id);
        ws = { ...created, name: p.name, category: p.category || p.niche };
        setActiveWorkspace(ws);
        setWorkspaces(prev => [...prev, ws]);
      } catch (e) { console.error("[onboarding] workspace creation failed:", e.message); }
    }

    // Apply domain plan template for agent subdomains (first-time only)
    let appliedModules = null;
    if (!profile && ws?.id && (wlConfig || signupAgent || onAgentHost)) {
      try {
        const agentHostname = signupAgent
          ? `${signupAgent}.getkurb.ai`
          : wlConfig?.subdomain
            ? `${wlConfig.subdomain}.getkurb.ai`
            : wlConfig?.custom_domain || window.location.hostname;
        const r = await fetch("/api/workspaces/apply-domain-template", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ workspaceId: ws.id, hostname: agentHostname }),
        });
        const d = await r.json();
        if (d.applied && d.modules) {
          appliedModules = d.modules;
          // Reload workspaces from DB — template was just applied, DB is authoritative
          const freshList = await getWorkspaces();
          const freshWs = freshList.find(w => w.id === ws.id) || ws;
          ws = freshWs.enabled_modules ? freshWs : { ...ws, enabled_modules: d.modules, plan: "custom", custom_label: d.templateName };
          setWorkspaces(freshList.length ? freshList : prev => prev.map(w => w.id === ws.id ? ws : w));
          setActiveWorkspace(ws);
          setWorkspacePlan("custom"); // sidebar gates on workspacePlan === "custom" to use enabled_modules
        }
      } catch (e) { console.warn("[onboarding] apply-domain-template failed:", e.message); }
    }

    // Keep workspace name in sync with business name
    if (supabase && ws?.id && p.name && p.name !== ws.name) {
      const updates = { name: p.name };
      if (p.category) updates.category = p.category;
      await supabase.from("workspaces").update(updates).eq("id", ws.id);
      const updated = { ...ws, name: p.name, ...(p.category ? { category: p.category } : {}) };
      setActiveWorkspace(updated);
      setWorkspaces(prev => prev.map(w => w.id === ws.id ? updated : w));
    }

    if (!profile) {
      if (ws?.product_kind === "content-agent") {
        setMod("content-engine");
      } else {
        setMod("campaigns");
      }
    }
  };

  const handleNewWorkspaceComplete = async (p) => {
    try {
      const ws = await createWorkspace(p.name, p.category || p.niche || "other");
      clearWorkspaceCache();
      await switchWorkspace(ws.id);
      if (supabase && p.name) {
        const updates = { name: p.name };
        if (p.category) updates.category = p.category;
        await supabase.from("workspaces").update(updates).eq("id", ws.id);
      }

      // Apply domain plan template if on an agent subdomain
      let appliedModules = null;
      let appliedProductKind = null;
      if (wlConfig) {
        try {
          const r = await fetch("/api/workspaces/apply-domain-template", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ workspaceId: ws.id, hostname: window.location.hostname }),
          });
          const d = await r.json();
          if (d.applied && d.modules) { appliedModules = d.modules; appliedProductKind = d.productKind || "full"; }
        } catch (e) { console.warn("[new-workspace] apply-domain-template failed:", e.message); }
      }

      const fullWs = { ...ws, name: p.name, category: p.category || p.niche,
        ...(appliedModules ? { enabled_modules: appliedModules } : {}),
        ...(appliedProductKind ? { product_kind: appliedProductKind } : {}),
      };
      setActiveWorkspace(fullWs);
      setWorkspaces(prev => [...prev, fullWs]);
      await store.set("business-profile", p);
      await store.set("onboarding-done", true);
      setProfile(p);
      setUserMode("default"); await store.set("user-mode", "default");

      if (fullWs.product_kind === "content-agent") {
        setMod("content-engine");
      } else {
        setMod("dashboard");
      }
    } catch(e) { console.error("[new-workspace]", e.message); }
  };

  const handleAddWebsitePurchase = async (billingPlan) => {
    try {
      await fetch("/api/billing/add-website-intent", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspaceId: activeWorkspace?.id, billingPlan }),
      });
    } catch (e) { console.warn("[add-website] intent failed:", e.message); }
    // Non-blocking — proceed as paid (Stripe replaces this body later)
  };

  const handleAddWebsiteComplete = async (name, url, billingPlan = "yearly") => {
    try {
      setShowAddWebsite(false);
      const ws = await createWorkspace(name, "other");
      clearWorkspaceCache();
      await switchWorkspace(ws.id);

      if (supabase) {
        await supabase.from("workspaces").update({ name }).eq("id", ws.id);
      }

      // Always copy current workspace's CA plan to the new workspace so it persists after refresh
      const modulesToApply = activeWorkspace?.enabled_modules || ["content-engine", "brain", "media", "settings"];
      let appliedModules = modulesToApply;
      try {
        await fetch("/api/admin/update-workspace", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            workspaceId: ws.id,
            updates: { plan: "custom", enabled_modules: modulesToApply, image_quality: null },
          }),
        });
      } catch (e) { console.warn("[add-website] plan apply failed:", e.message); }

      // Record purchase intent against the new workspace (correct workspace_id)
      try {
        await fetch("/api/billing/add-website-intent", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ workspaceId: ws.id, billingPlan }),
        });
      } catch (e) { console.warn("[add-website] intent failed:", e.message); }

      // Seed profile.website so CE setup wizard pre-fills the URL field
      const newProfile = { website: url, name };
      await store.set("business-profile", newProfile);
      setProfile(newProfile);

      const fullWs = {
        ...ws, name,
        enabled_modules: appliedModules,
        plan: "custom",
      };
      setActiveWorkspace(fullWs);
      setWorkspaces(prev => [...prev, fullWs]);
      setCeTab("dashboard");
      setMod("content-engine");
    } catch (e) { console.error("[add-website]", e.message); }
  };

  const switchWorkspaceEnter = async (ws) => {
    clearWorkspaceCache();
    await switchWorkspace(ws.id);
    setActiveWorkspace(ws);
    if (ws?.plan) setWorkspacePlan(ws.plan);
    // Re-resolve role for the new workspace — profiles.role is global, so
    // an owner of one workspace might be a manager/staff of another.
    const role = await getMyWorkspaceRole(ws.id);
    setUserRole(role);
    const saved = await store.get("business-profile");
    if (saved) setProfile(saved); else setProfile(null);
    setAdminMode(false);
    const isCA = ws.product_kind === "content-agent";
    if (isCA) { setMod("content-engine"); setCeTab("dashboard"); }
    else setMod("dashboard");
  };

  const exitToAdmin = () => {
    setAdminMode(true);
    setMod(isSuperAdmin ? "admin" : "agency-workspaces");
  };

  const handleSignOut = async () => {
    await signOut();
    setProfile(null);
    setReady(false);
    setShowOnboarding(false);
    setMod("dashboard");
  };

  if (inviteToken && inviteVerifying) return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ textAlign:"center", color:C.t2 }}>
        <div style={{ fontSize:32, marginBottom:16 }}>✉</div>
        <div style={{ fontSize:15, fontWeight:500 }}>Setting up your invitation…</div>
      </div>
    </div>
  );

  if (inviteTokenError) return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ width:"100%", maxWidth:440, background:C.s1, borderRadius:16, padding:48, boxShadow:"0 4px 32px rgba(0,0,0,0.08)", textAlign:"center" }}>
        <div style={{ fontSize:40, marginBottom:16 }}>⏱</div>
        <h2 style={{ fontSize:22, fontWeight:700, color:C.text, margin:"0 0 12px" }}>Invite Link Expired</h2>
        <p style={{ fontSize:14, color:C.t2, lineHeight:1.7, margin:"0 0 28px" }}>
          {inviteTokenError}<br/>
          Please ask the workspace owner to send you a new invite.
        </p>
        <button style={{ ...S.btn, width:"100%" }} onClick={() => { setInviteTokenError(null); window.history.replaceState({}, "", window.location.pathname); }}>
          Back to Sign In
        </button>
      </div>
    </div>
  );

  if (otpError) return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ width:"100%", maxWidth:440, background:C.s1, borderRadius:16, padding:48, boxShadow:"0 4px 32px rgba(0,0,0,0.08)", textAlign:"center" }}>
        <div style={{ fontSize:40, marginBottom:16 }}>⏱</div>
        <h2 style={{ fontSize:22, fontWeight:700, color:C.text, margin:"0 0 12px" }}>Invite Link Expired</h2>
        <p style={{ fontSize:14, color:C.t2, lineHeight:1.7, margin:"0 0 28px" }}>
          This invite link has already been used or has expired.<br/>
          Please ask the workspace owner to send you a new invite.
        </p>
        <button style={{ ...S.btn, width:"100%" }} onClick={() => { window.location.hash = ""; setOtpError(null); }}>
          Back to Sign In
        </button>
      </div>
    </div>
  );
  // Don't flash AuthScreen during a Supabase auth callback — hash has access_token while getSession() resolves
  const isAuthCallback = window.location.hash.includes("access_token");
  if (!authChecked || (authChecked && supabase && !authUser)) {
    if (isAuthCallback) return (
      <div style={{ position:"fixed", inset:0, background:C.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div style={{ width:32, height:32, border:`3px solid ${C.border}`, borderTopColor:C.accent, borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
      </div>
    );
    return <AuthScreen wlConfig={wlConfig} />;
  }
  if (!ready) return (
    <div style={{ position:"fixed", inset:0, background:C.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ width:32, height:32, border:`3px solid ${C.border}`, borderTopColor:C.accent, borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
    </div>
  );
  if (inviteWelcome) return <AcceptInviteWelcome invite={inviteWelcome} authUser={authUser} onComplete={() => setInviteWelcome(null)} />;
  if (showOnboarding) {
    // On a *.getkurb.ai subdomain (not app.) we know it's an agent even before wlConfig loads,
    // so we can show AgentOnboarding immediately without waiting for wlConfigChecked.
    const onAgentSubdomain = !signupAgent &&
      window.location.hostname.endsWith(".getkurb.ai") &&
      window.location.hostname !== "app.getkurb.ai";
    // Only block if we genuinely don't know whether this is an agent domain yet
    if (!signupAgent && !wlConfigChecked && !onAgentSubdomain) return null;
    if ((signupAgent || wlConfig?.plan_template_id || onAgentSubdomain) && !profile)
      return <AgentOnboarding
        agentSubdomain={signupAgent || (onAgentSubdomain ? window.location.hostname.replace(".getkurb.ai", "") : null)}
        wlConfig={wlConfig}
        onComplete={handleOnboardingComplete} />;
    return <Onboarding onComplete={handleOnboardingComplete} onCancel={() => setShowOnboarding(false)} existingProfile={profile} editMode={!!profile} wlConfig={wlConfig} />;
  }

  // Admin mode wins over userMode — a super/agency admin must always reach
  // the admin shell. Without this guard, a stored userMode=default would
  // trap the admin in the simplified 4-tab business-owner view.
  if (userMode === "default" && !adminMode) return (
    <DefaultShell
      profile={profile}
      authUser={authUser}
      workspaces={workspaces}
      activeWorkspace={activeWorkspace}
      onSwitchWorkspace={async (ws) => {
        clearWorkspaceCache();
        await switchWorkspace(ws.id);
        setActiveWorkspace(ws);
        const role = await getMyWorkspaceRole(ws.id);
        setUserRole(role);
        const saved = await store.get("business-profile");
        if (saved) setProfile(saved); else setProfile(null);
      }}
      onAddWorkspace={async () => { await switchMode("advanced"); setMod("add-workspace"); }}
      onSwitchAdvanced={() => switchMode("advanced")}
      onEditProfile={() => setShowOnboarding(true)}
      onSignOut={handleSignOut}
      wlConfig={wlConfig}
      notifications={notifications}
      showNotifPanel={showNotifPanel}
      onToggleNotifPanel={() => setShowNotifPanel(v => !v)}
      onMarkRead={markNotifRead}
      onMarkAllRead={markAllNotifsRead}
      onNavigateFromNotif={navToFromNotif}
    />
  );

  const navTo = (id, fromCtx) => {
    if (fromCtx) setBackContext(fromCtx);
    else setBackContext(null);
    setMod(id);
    setShowMobileNav(false);
  };
  const goBack = () => {
    const bc = backContext;
    setBackContext(null);
    setMod(bc.mod);
    setShowMobileNav(false);
    if (bc.seoTab) setTimeout(() => window.dispatchEvent(new CustomEvent("seo-goto-tab", { detail: { tab: bc.seoTab } })), 50);
  };

  const pages = {
    dashboard:    <Dashboard setMod={setMod} profile={profile} />,
    inbox:        <InboxModule profile={profile} navTo={navTo} />,
    brain:        <BusinessBrain profile={profile} navTo={navTo} />,
    "brain-ca":   isContentAgentPlan ? <ContentAgentMyBusiness profile={profile} /> : <div style={{ padding:40, color:C.t3, fontSize:14 }}>Not available.</div>,
    website:      <WebsiteBuilder profile={profile} imageQuality={effectiveImageQuality} />,
    media:        <MediaLibrary />,
    content:      <ContentWriter profile={profile} imageQuality={effectiveImageQuality} />,
    "content-engine": <ContentEngineShell profile={profile} workspace={activeWorkspace} activeTab={isContentAgentPlan ? ceTab : undefined} onTabChange={isContentAgentPlan ? setCeTab : undefined} onPlanCountChange={isContentAgentPlan ? setCePlanCount : undefined} imageQuality={effectiveImageQuality} />,
    "image-studio": <ImageStudio profile={profile} navTo={navTo} backContext={backContext} activeWorkspace={activeWorkspace} />,
    brand:        <BrandGuidelines profile={profile} />,
    reservations: <Reservations />,
    "lead-agent": <LeadAgent profile={profile} />,
    "conversion": <ConversionModule profile={profile} navTo={navTo} activeWorkspaceId={activeWorkspace?.id || null} />,
    crm:          <CRM navTo={navTo} profile={profile} />,
    social:       <SocialMedia profile={profile} imageQuality={effectiveImageQuality} navTo={navTo} />,
    ads:          <AdsManager profile={profile} imageQuality={effectiveImageQuality} />,
    "call-tracking": <CallTrackingModule profile={profile} navTo={navTo} />,
    bloggers:     <BloggerFinder profile={profile} />,
    gbp:          <GoogleBusinessProfile profile={profile} imageQuality={effectiveImageQuality} />,
    marketing:          <EmailCampaigns navTo={navTo} />,
    "email-campaigns":  <EmailCampaigns navTo={navTo} />,
    reviews:      <ReviewsModule />,
    campaigns:    <CampaignManager profile={profile} />,
    inquiries:    <Inquiries />,
    settings:     <Settings authUser={authUser} profile={profile} userRole={userRole} onEditProfile={() => setShowOnboarding(true)} onSignOut={handleSignOut} onSwitchDefault={isContentAgentPlan ? undefined : () => switchMode("default")} caMode={isContentAgentPlan} />,
    pricing:      <PricingPage wlConfig={wlConfig} />,
    roles:        <RolesPermissions authUser={authUser} rolePerms={rolePerms} activeWorkspace={activeWorkspace} onPermsChange={p => { setRolePerms(p); store.set("role-permissions", p); }} />,
    research:     <ResearchStrategy profile={profile} onGoToContent={() => navTo("content", { mod:"research", label:"Research & Strategy" })} />,
    seo:          <SEOModule profile={profile} activeWorkspace={activeWorkspace} onGoToContent={(keyword, type, title, seoTab) => { navTo("content", { mod:"seo", label: seoTab==="plan" ? "Content Plan" : seoTab==="opportunities" ? "SEO Opportunities" : "SEO", seoTab: seoTab||"opportunities" }); setTimeout(() => window.dispatchEvent(new CustomEvent("content-topic-selected", { detail: { title: title||keyword||"", type: type||"blog", _listId:undefined, _topicIndex:undefined } })), 100); }} />,
    aeo:          <AEOVisibility profile={profile} workspacePlan={activeWorkspace?.plan||"starter"} onGoToContent={() => navTo("content", { mod:"aeo", label:"AI Visibility" })} />,
    authority:    <AuthorityAgent workspace={activeWorkspace} profile={profile} navTo={navTo} />,
    integrations: <Integrations />,
    autopilot:    <Autopilot profile={profile} onNavigate={(id) => navTo(id)} />,
    scheduler:    <Scheduler profile={profile} />,
    reports:      <Reports />,
    changelog:     <ChangeLog />,
    admin:         <AdminDashboard onEnterWorkspace={switchWorkspaceEnter} onWorkspacesChanged={setWorkspaces} onOpenFullActivity={() => setMod("super-activity")} />,
    "admin-workspaces": <AdminDashboard onEnterWorkspace={switchWorkspaceEnter} onWorkspacesChanged={setWorkspaces} onOpenFullActivity={() => setMod("super-activity")} initialTab="workspaces" />,
    "super-users":      <SuperAdminUsers onEnterWorkspace={switchWorkspaceEnter} />,
    "super-activity":      <SuperAdminActivity onEnterWorkspace={switchWorkspaceEnter} />,
    "super-system-health": <SystemHealth onEnterWorkspace={switchWorkspaceEnter} onNavToActivity={() => setMod("super-activity")} />,
    "super-reports":       <PlatformReports />,
    "super-cost-usage":    <CostUsage />,
    "super-at-risk":       <AtRiskWorkspaces
                              onEnterWorkspace={(id) => { const ws = workspaces.find(w => w.id === id); if (ws) switchWorkspaceEnter(ws); }}
                              onNavTo={(target) => setMod(target)} />,
    "super-white-label":   <WhiteLabelDomains
                              onEnterWorkspace={(id) => { const ws = workspaces.find(w => w.id === id); if (ws) switchWorkspaceEnter(ws); }} />,
    "authority-library":   <CitationLibraryAdmin profile={profile} />,
    "agency-workspaces": <AgencyWorkspaces onEnter={switchWorkspaceEnter} />,
    "agency-users":     <AgencyUsers onEnterWorkspace={switchWorkspaceEnter} />,
    "add-workspace": <Onboarding inline={true} onComplete={handleNewWorkspaceComplete} onCancel={() => setMod("dashboard")} wlConfig={wlConfig} />,
  };

  const appName   = wlConfig?.app_name    || "Kurb AI";
  const appTagline = wlConfig?.tagline    || "Marketing Autopilot";
  const appAccent  = wlConfig?.primary_color || C.accent;

  const SidebarLogo = () => wlConfig?.logo_url ? (
    <img src={wlConfig.logo_url} alt={appName} style={{ height:34, maxWidth:120, objectFit:"contain" }} />
  ) : (
    <>
      <div style={{ width:34, height:34, borderRadius:10, background:appAccent, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, color:"#fff", fontWeight:700, flexShrink:0 }}>
        {appName.charAt(0).toUpperCase()}
      </div>
      <div>
        <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:16, fontWeight:700, color:SB.text, letterSpacing:"0.01em", lineHeight:1.2 }}>{appName}</div>
        <div style={{ fontSize:10, color:SB.t2, marginTop:2 }}>{appTagline}</div>
      </div>
    </>
  );

  const Sidebar = () => (
    <aside style={{ width: 228, background: SB.bg, borderRight: `1px solid ${SB.border}`, display: "flex", flexDirection: "column", flexShrink: 0, ...(isMobile ? { position:"fixed", top:0, left:0, bottom:0, zIndex:300, transform: showMobileNav ? "translateX(0)" : "translateX(-100%)", transition:"transform 0.25s ease", boxShadow: showMobileNav ? "4px 0 32px rgba(0,0,0,0.6)" : "none" } : {}) }}>
      <div style={{ padding:"20px 16px 16px", borderBottom:`1px solid ${SB.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <SidebarLogo />
          {wlConfig?.logo_url && <div>
            <div style={{ fontFamily:"'Plus Jakarta Sans', -apple-system, sans-serif", fontSize:16, fontWeight:700, color:SB.text, letterSpacing:"0.01em", lineHeight:1.2 }}>{appName}</div>
            <div style={{ fontSize:10, color:SB.t2, marginTop:2 }}>{appTagline}</div>
          </div>}
        </div>
        {isMobile && (
          <button onClick={() => setShowMobileNav(false)} style={{ background:"none", border:"none", color:SB.t2, fontSize:20, cursor:"pointer", padding:"4px 8px", lineHeight:1 }}>✕</button>
        )}
      </div>
      {(isSuperAdmin || isAgencyAdmin) && adminMode && (
        <div style={{ padding:"6px 16px 8px" }}>
          <span style={{ fontSize:10, fontWeight:700, background:"#6366f1", color:"#fff", borderRadius:4, padding:"2px 8px", letterSpacing:"0.05em" }}>
            {isSuperAdmin ? "Super Admin" : "Agency Admin"}
          </span>
        </div>
      )}
      {/* Workspace switcher — hidden in admin shell */}
      {!((isSuperAdmin || isAgencyAdmin) && adminMode) && <div style={{ margin:"12px 14px 0", position:"relative" }}>
        <button
          onClick={() => setShowWorkspacePicker(v => !v)}
          style={{ width:"100%", padding:"8px 10px", background:SB.s2, borderRadius:8, border:`1px solid ${showWorkspacePicker ? C.accent : SB.border}`, cursor:"pointer", fontFamily:"inherit", textAlign:"left", display:"flex", alignItems:"center", gap:8, transition:"border-color 0.15s" }}>
          <div style={{ width:26, height:26, borderRadius:6, background:`${C.accent}25`, border:`1px solid ${C.accent}40`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, flexShrink:0 }}>
            {activeWorkspace?.category === "restaurant" ? "🍽" : activeWorkspace?.category === "health" ? "🏥" : "🏢"}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:11, color:SB.t2, marginBottom:1 }}>Active business</div>
            <div style={{ fontSize:12, fontWeight:600, color:SB.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {activeWorkspace?.name || profile?.name || "My Business"}
            </div>
          </div>
          <span style={{ fontSize:9, color:SB.t3, transform: showWorkspacePicker ? "rotate(180deg)" : "rotate(0deg)", transition:"transform 0.2s", flexShrink:0 }}>▲</span>
        </button>
        {showWorkspacePicker && (
          <>
            <div style={{ position:"fixed", inset:0, zIndex:198 }} onClick={() => setShowWorkspacePicker(false)} />
            <WorkspacePicker
              workspaces={workspaces}
              activeId={activeWorkspace?.id}
              onSelect={async (ws) => {
                setShowWorkspacePicker(false);
                clearWorkspaceCache();
                await switchWorkspace(ws.id);
                setActiveWorkspace(ws);
                const role = await getMyWorkspaceRole(ws.id);
                setUserRole(role);
                const saved = await store.get("business-profile");
                if (saved) setProfile(saved); else setProfile(null);
              }}
              onAdd={isContentAgentPlan
                ? () => { setShowWorkspacePicker(false); setShowAddWebsite(true); }
                : () => { setShowWorkspacePicker(false); setMod("add-workspace"); }}
              {...(isContentAgentPlan ? { addLabel: "Add Website" } : {})}
            />
          </>
        )}
      </div>}
      <nav style={{ flex: 1, padding: "10px 0", overflowY: "auto" }}>
        {(isSuperAdmin || isAgencyAdmin) && adminMode ? (() => {
          const ADMIN_ITEMS = isSuperAdmin
            ? [
                { id: "admin",            label: "Plan Dashboard", icon: "⚿" },
                { id: "admin-workspaces", label: "Workspaces",     icon: "🏢" },
                { id: "super-users",      label: "Users",          icon: "👥" },
                { id: "super-activity",      label: "Activity",      icon: "📜" },
                { id: "super-system-health", label: "System Health", icon: "💓" },
                { id: "super-reports",       label: "Reports",       icon: "📊" },
                { id: "super-cost-usage",    label: "Cost & Usage", icon: "💰" },
                { id: "super-at-risk",       label: "At-Risk",      icon: "🚩" },
                { id: "super-white-label",   label: "White Label",  icon: "🌐" },
                { id: "authority-library",   label: "Citation Library", icon: "📚" },
                { id: "changelog",           label: "Change Log",   icon: "◷" },
              ]
            : [
                { id: "agency-workspaces", label: "Workspaces", icon: "🏢" },
                { id: "agency-users",      label: "Users",      icon: "👥" },
              ];
          return ADMIN_ITEMS.map(item => {
            const active = mod === item.id;
            return (
              <button key={item.id} onClick={() => navTo(item.id)}
                style={{ display:"flex", alignItems:"center", gap:10, width:"calc(100% - 16px)", margin:"1px 8px", padding:"9px 10px", border:"none", borderRadius:6, background: active ? "rgba(255,255,255,0.1)" : "transparent", color: SB.text, fontSize:14, fontFamily:"inherit", cursor:"pointer", textAlign:"left", transition:"all 0.12s" }}
                onMouseOver={e => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                onMouseOut={e => { if (!active) e.currentTarget.style.background = "transparent"; }}>
                <span style={{ fontSize:14, opacity: active ? 1 : 0.7 }}>{item.icon}</span>
                <span style={{ fontWeight: active ? 600 : 400 }}>{item.label}</span>
              </button>
            );
          });
        })()

        : isContentAgentPlan ? (() => {
          const CA_NAV = [
            { id: "home",     icon: "🏠", label: "Home",        action: () => { navTo("content-engine"); setCeTab("dashboard"); } },
            { id: "brain-ca", icon: "🗂", label: "Brand Guide", action: () => navTo("brain-ca") },
            { id: "cal",      icon: "📅", label: "Calendar",    action: () => { navTo("content-engine"); setCeTab("calendar"); }, count: cePlanCount },
            { id: "art",      icon: "📄", label: "Articles",    action: () => { navTo("content-engine"); setCeTab("articles"); } },
            { id: "kw",       icon: "🔍", label: "Keywords",    action: () => { navTo("content-engine"); setCeTab("keywords"); } },
            ...(activeWorkspace?.enabled_modules?.includes("social") ? [{ id: "social", icon: "📱", label: "Social", action: () => navTo("social"), badge: "New" }] : []),
            ...(activeWorkspace?.enabled_modules?.includes("media") ? [{ id: "media", icon: "⊞", label: "Media Library", action: () => navTo("media") }] : []),
            { id: "settings", icon: "⚙", label: "Settings",    action: () => navTo("settings"), bottom: true },
          ];
          const activeCAId = mod === "brain-ca" ? "brain-ca"
            : mod === "social" ? "social"
            : mod === "media" ? "media"
            : mod === "settings" ? "settings"
            : mod === "content-engine" ? (ceTab === "calendar" ? "cal" : ceTab === "articles" ? "art" : ceTab === "keywords" ? "kw" : "home")
            : "home";
          return CA_NAV.map(item => {
            const active = activeCAId === item.id;
            return (
              <button key={item.id} onClick={item.action}
                style={{ display:"flex", alignItems:"center", gap:10, width:"calc(100% - 16px)", margin: item.bottom ? "auto 8px 0" : "1px 8px", padding:"9px 10px", border:"none", borderRadius:6, background: active ? "rgba(255,255,255,0.1)" : "transparent", color: SB.text, fontSize:14, fontFamily:"inherit", cursor:"pointer", textAlign:"left", transition:"all 0.12s" }}
                onMouseOver={e => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                onMouseOut={e => { if (!active) e.currentTarget.style.background = "transparent"; }}>
                <span style={{ fontSize:14, opacity: active ? 1 : 0.7, width:18, display:"inline-flex", justifyContent:"center", alignItems:"center", flexShrink:0 }}>{item.icon}</span>
                <span style={{ fontWeight: active ? 600 : 400, flex:1 }}>{item.label}</span>
                {item.count > 0 && <span style={{ fontSize:10, background:"#6366f1", color:"#fff", borderRadius:8, padding:"1px 7px", fontWeight:700 }}>{item.count}</span>}
                {item.badge && <span style={{ fontSize:9, background:"#f59e0b", color:"#fff", borderRadius:4, padding:"2px 5px", fontWeight:700 }}>{item.badge}</span>}
              </button>
            );
          });
        })()

        : NAV.filter((m, i, arr) => {
          // Super/agency admin items belong in the admin shell, not the
          // workspace shell. Without this guard, super admin gets every
          // admin nav item duplicated inside every workspace's sidebar.
          if (m.superAdminOnly) return isSuperAdmin && adminMode;
          if (m.agencyAdminOnly) return isAgencyAdmin && adminMode;
          if (m.id) {
            if (!canAccess(userRole, m.id, rolePerms)) return false;
            // null / undefined enabled_modules means "all modules" (the convention
            // used by plan templates). Only enforce membership when an explicit
            // array is set.
            const allowed = workspacePlan === "custom"
              ? activeWorkspace?.enabled_modules
              : PLAN_MODULES[workspacePlan];
            if (Array.isArray(allowed) && !allowed.includes(m.id)) return false;
            return true;
          }
          if (m.type === "divider") return true;
          const children = arr.slice(i + 1).filter(x => !x.type);
          const nextSection = children.findIndex(x => x.type === "section");
          const sectionChildren = nextSection === -1 ? children : children.slice(0, nextSection);
          return sectionChildren.some(x => {
            if (x.superAdminOnly) return isSuperAdmin;
            if (x.agencyAdminOnly) return isAgencyAdmin;
            return canAccess(userRole, x.id, rolePerms);
          });
        }).map((m, i) => {
          if (m.type === "section") return (
            <div key={i} style={{ padding:"16px 18px 6px", fontSize:11, fontWeight:600, color:SB.t3, textTransform:"uppercase", letterSpacing:"0.07em" }}>{m.label}</div>
          );
          if (m.type === "divider") return (
            <div key={i} style={{ height:1, background:SB.border, margin:"8px 14px" }} />
          );
          const active = mod === m.id;
          const indented = ["lead-agent","conversion","crm","inquiries","reservations","website","content","content-engine","seo","integrations","ads","call-tracking","social","gbp","marketing","reviews","bloggers","aeo","roles","brand","settings","pricing","autopilot","scheduler","media","reports"].includes(m.id);
          return (
            <button key={m.id} onClick={() => navTo(m.id)} style={{ display:"flex", alignItems:"center", gap:10, width:`calc(100% - 16px)`, margin:"1px 8px", padding:`9px ${indented?10:10}px 9px ${indented?22:10}px`, border:"none", borderRadius:6, background: active ? "rgba(255,255,255,0.1)" : "transparent", color:SB.text, fontSize:14, fontFamily:"inherit", cursor:"pointer", textAlign:"left", transition:"all 0.12s" }}
              onMouseOver={e => { if (!active) { e.currentTarget.style.background="rgba(255,255,255,0.06)"; e.currentTarget.style.color=SB.text; } }}
              onMouseOut={e => { if (!active) { e.currentTarget.style.background="transparent"; e.currentTarget.style.color=SB.text; } }}>
              <span style={{ fontSize:14, opacity: active ? 1 : 0.7, fontWeight: m.id==="gbp"?700:"normal", color: m.id==="gbp"&&active?"#4285f4":"inherit", width:18, display:"inline-flex", justifyContent:"center", alignItems:"center", flexShrink:0 }}>{m.icon}</span>
              <span style={{ fontWeight: active ? 600 : 400, flex:1 }}>{m.label}</span>
              {m.badge && <span style={{ fontSize:9, background:"#4285f4", color:"#fff", borderRadius:4, padding:"2px 5px", fontWeight:700, letterSpacing:"0.06em" }}>{m.badge}</span>}
            </button>
          );
        })}
      </nav>
      {!wlConfig?.hide_powered_by && (
        <div style={{ padding:"12px 18px", borderTop:`1px solid ${SB.border}` }}>
          <span style={{ fontSize:10, color:SB.t3 }}>Powered by <span style={{ color:C.accent }}>Kurb AI</span></span>
        </div>
      )}
    </aside>
  );

  return (
    <div style={{ display:"flex", height:"100vh", background:C.bg, fontFamily:"'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", color:C.text, overflow:"hidden" }}>
      <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}} @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}} @keyframes crm-pulse{0%,100%{transform:scale(1);opacity:0.6}50%{transform:scale(1.35);opacity:0}}`}</style>

      {/* New version toast — bottom-right, user-initiated reload only */}
      {newVersionSha && (
        <div style={{
          position:"fixed", bottom:20, right:20, zIndex:9999,
          background:C.text, color:C.s1, padding:"10px 14px", borderRadius:8,
          boxShadow:"0 4px 16px rgba(0,0,0,0.18)",
          display:"flex", alignItems:"center", gap:10, fontSize:13,
        }}>
          <span>✦ New version available</span>
          <button onClick={() => window.location.reload()}
            style={{ background:"#6366f1", color:"#fff", border:"none",
              padding:"5px 12px", borderRadius:5, fontSize:12, cursor:"pointer", fontWeight:600 }}>
            Reload
          </button>
          <button onMouseDown={e => e.preventDefault()}
            onClick={() => { setDismissedSha(newVersionSha); setNewVersionSha(null); }}
            style={{ background:"transparent", color:C.s1, border:"none", cursor:"pointer", fontSize:14, padding:"0 4px" }}
            aria-label="Dismiss">✕</button>
        </div>
      )}

      {/* Sidebar — full height, left edge */}
      {isMobile && showMobileNav && (
        <div onClick={() => setShowMobileNav(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:299 }} />
      )}
      {showAddWebsite && (
        <AddWebsiteModal
          onPurchase={handleAddWebsitePurchase}
          onComplete={handleAddWebsiteComplete}
          onClose={() => setShowAddWebsite(false)}
        />
      )}
      <Sidebar />

      {/* Right column: TopBar + content */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
        <TopBar
          profile={profile}
          authUser={authUser}
          userRole={userRole}
          userMode={userMode}
          showAIPanel={showAIPanel}
          onToggleAI={() => setShowAIPanel(v => !v)}
          onNavigate={navTo}
          onSignOut={handleSignOut}
          onEditProfile={() => setShowOnboarding(true)}
          onSwitchMode={() => switchMode(userMode === "default" ? "advanced" : "default")}
          notifications={notifications}
          showNotifPanel={showNotifPanel}
          onToggleNotifPanel={() => setShowNotifPanel(v => !v)}
          onMarkRead={markNotifRead}
          onMarkAllRead={markAllNotifsRead}
          onToggleMobileNav={() => setShowMobileNav(v => !v)}
        />

        <div style={{ flex:1, display:"flex", overflow:"hidden" }}>
          <main ref={mainRef} key={activeWorkspace?.id || "ws"} style={{ flex:1, overflowY:mod==="website"?"hidden":"auto", overflowX:"hidden", display:"flex", flexDirection:"column" }}>
            {/* Exit to Admin banner — shows when super/agency admin is inside a client workspace */}
            {(isSuperAdmin || isAgencyAdmin) && !adminMode && (
              <div style={{ background:"#1e293b", borderBottom:"1px solid #334155", padding:"8px 20px", display:"flex", alignItems:"center", gap:16, flexShrink:0 }}>
                <button onMouseDown={e=>e.preventDefault()} onClick={exitToAdmin}
                  style={{ background:"none", border:"1px solid #6366f1", borderRadius:6, padding:"4px 12px", color:"#a5b4fc", fontSize:12, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s" }}
                  onMouseOver={e=>{e.currentTarget.style.background="#6366f120";}}
                  onMouseOut={e=>{e.currentTarget.style.background="none";}}>
                  ← Exit to Admin
                </button>
                <div style={{ fontSize:12, color:"#94a3b8", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  <span>Viewing: <strong style={{ color:"#e2e8f0" }}>{activeWorkspace?.name}</strong></span>
                  {(activeWorkspace?.custom_label || activeWorkspace?.plan) && (
                    <span style={{ background:"rgba(99,102,241,0.18)", color:"#a5b4fc", border:"1px solid rgba(99,102,241,0.4)", padding:"2px 8px", borderRadius:4, fontSize:11, fontWeight:600 }}>
                      {activeWorkspace.custom_label || activeWorkspace.plan}
                    </span>
                  )}
                  <span style={{ color:"#64748b" }}>(owner access)</span>
                </div>
              </div>
            )}
            <div style={(mod==="website" || mod==="call-tracking" || mod==="content-engine" || mod==="conversion") ? { flex:1, display:"flex", flexDirection:"column", overflow:"hidden" } : (mod==="crm" || mod==="lead-agent" || mod==="seo") ? { padding: isMobile ? "16px 12px" : "32px 24px", width:"100%" } : { maxWidth:1100, padding: isMobile ? "20px 16px" : "40px 48px" }}>
              {!canAccess(userRole, mod, rolePerms) ? (
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"60vh", gap:12 }}>
                  <div style={{ fontSize:36 }}>◑</div>
                  <div style={{ fontSize:18, fontWeight:600, color:C.text }}>Access Restricted</div>
                  <div style={{ fontSize:13, color:C.t3 }}>You don't have permission to view this module.</div>
                  <div style={{ fontSize:11, color:C.t3 }}>Contact your account owner to request access.</div>
                </div>
              ) : (
                <>
                  {mod !== "website" && (
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, minHeight:32 }}>
                      <div>
                        {backContext && (
                          <button onMouseDown={e=>e.preventDefault()} onClick={goBack}
                            style={{ display:"inline-flex", alignItems:"center", gap:6, background:"none", border:`1px solid ${C.border}`, borderRadius:8, padding:"6px 14px", cursor:"pointer", fontSize:13, color:C.t2, fontFamily:"inherit", transition:"all 0.15s" }}
                            onMouseOver={e=>{e.currentTarget.style.background=C.s2;e.currentTarget.style.color=C.text;}}
                            onMouseOut={e=>{e.currentTarget.style.background="none";e.currentTarget.style.color=C.t2;}}>
                            ← {backContext.label}
                          </button>
                        )}
                      </div>
                      <button onClick={() => setRefreshKey(k => k + 1)}
                        style={{ display:"inline-flex", alignItems:"center", gap:5, background:"none", border:`1px solid ${C.border}`, borderRadius:8, padding:"6px 14px", cursor:"pointer", fontSize:13, color:C.t2, fontFamily:"inherit", transition:"all 0.15s" }}
                        onMouseOver={e=>{e.currentTarget.style.background=C.s2;e.currentTarget.style.color=C.text;}}
                        onMouseOut={e=>{e.currentTarget.style.background="none";e.currentTarget.style.color=C.t2;}}>
                        ↺ Refresh
                      </button>
                    </div>
                  )}
                  <React.Suspense fallback={<div style={{ padding:40, color:C.t3 }}>Loading…</div>}>
                    <React.Fragment key={`${mod}-${refreshKey}`}>
                      {pages[mod]}
                    </React.Fragment>
                  </React.Suspense>
                </>
              )}
            </div>
          </main>

          {/* AI Panel */}
          {!isMobile && (
            <div style={{ display: showAIPanel ? "flex" : "none", flexShrink:0 }}>
              <AIAssistant key={activeWorkspace?.id || "default"} workspaceId={activeWorkspace?.id} profile={profile} mod={mod} userMode={userMode} onNavigate={navTo} onClose={() => setShowAIPanel(false)} wlConfig={wlConfig} />
            </div>
          )}
        </div>
      </div>

      {showPasswordReset && <PasswordResetModal onClose={() => setShowPasswordReset(false)} />}

    </div>
  );
}
